import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useLayoutEffect,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from 'react';
import { Link, useSearchParams, useParams } from 'react-router-dom';
import Header from '../../components/Header';
import { supabase } from '../../lib/supabase';
import {
  ArrowLeft,
  AlertCircle,
  Ban,
  RotateCcw,
  X,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Mail,
  MailX,
  Info,
  TrendingUp,
  Banknote,
  Trash2,
  Copy,
  FileText,
  FileSpreadsheet,
  Pencil,
  Play,
  Plus,
  Search,
  CloudOff,
  ScrollText,
} from 'lucide-react';
import {
  formatCurrency,
  formatCurrencyDecimals,
  formatDateOnly,
  formatDateTime,
  formatGroupNightsLabel,
  formatManagerInitials,
  formatRealisedAmountInputValue,
  normaliseRealisedAmountInput,
  parseRealisedAmountInputToNumber,
  revenueResponseRowKey,
} from '../../utils/formatters';
import { drainCampaignInitialSends } from '../../utils/revenueCollectionLaunch';
import { AddHotelsToLaunchedCampaignModal } from './AddHotelsToLaunchedCampaignModal';
import { downloadRevenueCampaignExcel } from '../../utils/revenueCampaignExcelExport';
import {
  canAdminCancelCampaignHotel,
  canAdminUndoCancelCampaignHotel,
  isCampaignHotelIncludedInKpis,
  isCampaignHotelNoProduction,
  isRevenueCampaignHotelFormComplete,
  revenueCampaignMaterializationPct,
  shouldClearRemindersCancelledAfterUndoCancel,
  statusAfterUndoCancel,
} from '../../utils/revenueCampaignHotelStatus';
import {
  addCalendarDays,
  defaultManualLineStayDates,
  manualLineStayWindowBounds,
  nightsBetweenStayDates,
  normalizeCheckinDate,
} from '../../utils/revenueStayDates';
import { EmailTestModeBadge } from '../../components/revenue/EmailTestModeBadge';
import {
  EmailBounceDetailModal,
  type EmailBounceDetailView,
} from '../../components/revenue/EmailBounceDetailModal';
import SingleSelectDropdown from '../../components/SingleSelectDropdown';
import { useAuth } from '../../context/AuthContext';
import { useRevenueAccess } from '../../hooks/useRevenueAccess';
import {
  bounceDetailForEmailSend,
  enrichEmailSendsWithBounceTooltips,
  isBounceLikeDeliveryState,
  latestHotelBounceDetail,
  type EmailBounceDetailContent,
  type PostmarkBounceEventRow,
} from '../../utils/postmarkBounceDetail';

async function fetchRevenueCollectionBounceEvents(params: {
  campaignId: string;
  hotelId?: number;
}): Promise<PostmarkBounceEventRow[]> {
  let query = supabase
    .from('revenue_collection_postmark_events')
    .select('postmark_message_id, payload, occurred_at')
    .eq('campaign_id', params.campaignId)
    .eq('record_type', 'Bounce');
  if (params.hotelId != null) {
    query = query.eq('hotel_id', params.hotelId);
  }
  const { data, error } = await query;
  if (error) {
    console.error('fetchRevenueCollectionBounceEvents:', error);
    return [];
  }
  return (data ?? []) as PostmarkBounceEventRow[];
}

async function persistCampaignHotelInternalNote(params: {
  campaignId: string;
  hotelId: number;
  noteId: string | null;
  body: string;
  authorUserId: string | null;
  authorEmail: string | null;
}): Promise<{ id: string | null; body: string } | { error: string }> {
  const trimmed = params.body.trim();
  if (!trimmed) {
    if (params.noteId) {
      const { error } = await supabase
        .from('revenue_collection_hotel_internal_comments')
        .delete()
        .eq('id', params.noteId);
      if (error) return { error: error.message };
    }
    return { id: null, body: '' };
  }
  if (params.noteId) {
    const { error } = await supabase
      .from('revenue_collection_hotel_internal_comments')
      .update({ body: trimmed })
      .eq('id', params.noteId);
    if (error) return { error: error.message };
    return { id: params.noteId, body: trimmed };
  }
  const { data, error } = await supabase
    .from('revenue_collection_hotel_internal_comments')
    .insert({
      campaign_id: params.campaignId,
      hotel_id: params.hotelId,
      body: trimmed,
      author_user_id: params.authorUserId,
      author_email: params.authorEmail,
    })
    .select('id')
    .single();
  if (error) return { error: error.message };
  return { id: String(data.id), body: trimmed };
}

function CampaignHotelInternalNoteField({
  campaignId,
  hotelId,
  noteId,
  initialBody,
  onSaved,
  rows = 1,
  className = '',
}: Readonly<{
  campaignId: string;
  hotelId: number;
  noteId: string | null;
  initialBody: string;
  onSaved?: (note: { id: string | null; body: string }) => void;
  rows?: number;
  className?: string;
}>) {
  const { user } = useAuth();
  const { canMutateRevenue } = useRevenueAccess();
  const [text, setText] = useState(initialBody);
  const [saving, setSaving] = useState(false);
  const baselineRef = useRef(initialBody);

  useEffect(() => {
    setText(initialBody);
    baselineRef.current = initialBody;
  }, [noteId, initialBody]);

  const commit = useCallback(async () => {
    if (!canMutateRevenue) return;
    const trimmed = text.trim();
    if (trimmed === baselineRef.current.trim()) return;
    setSaving(true);
    try {
      const result = await persistCampaignHotelInternalNote({
        campaignId,
        hotelId,
        noteId,
        body: text,
        authorUserId: user?.id ?? null,
        authorEmail: user?.email ?? null,
      });
      if ('error' in result) {
        console.error('persistCampaignHotelInternalNote:', result.error);
        return;
      }
      baselineRef.current = result.body;
      setText(result.body);
      onSaved?.(result);
    } finally {
      setSaving(false);
    }
  }, [campaignId, hotelId, noteId, text, user?.id, user?.email, onSaved, canMutateRevenue]);

  return (
    <textarea
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => void commit()}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      disabled={saving || !canMutateRevenue}
      readOnly={!canMutateRevenue}
      rows={rows}
      title={
        canMutateRevenue
          ? 'Internal admin note (not visible to the hotel). Saves when you leave the field.'
          : 'Internal note (not visible to the hotel).'
      }
      aria-label="Internal admin note"
      className={`box-border w-full min-w-0 min-h-[1.5rem] resize-y rounded border border-gray-300 bg-white px-1.5 py-0.5 text-[11px] leading-tight text-gray-800 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#148D82]/35 disabled:opacity-60 ${className}`}
    />
  );
}

interface CampaignHotel {
  hotel_id: number;
  status: string;
  /** Set when an admin validates via dashboard; null if only the hotel submitted via link. */
  validated_by: string | null;
  email_sent_at: string | null;
  reminder_1_at: string | null;
  reminder_2_at: string | null;
  reminder_3_at: string | null;
  reminders_cancelled: boolean;
  /** Set when the initial invitation was rejected for this invoicing address. */
  initial_send_error?: string | null;
  hotel?: { id: number; title: string; main_manager_email: string | null };
  /** From revenue_collection_hotel_commissions */
  invoicing_entity?: 'NL' | 'SP' | null;
  /** Booked | reserved rows with estimated_revenue > 0 */
  booked_groups_count?: number;
  estimated_revenue?: number;
  realised_revenue?: number;
  /** Realised / estimated from form rows (responses + manual lines); Mat. column when validated */
  materialization_form_realised?: number;
  materialization_form_estimated?: number;
  /** Sum of realised × effective commission % per form row */
  invoice_amount?: number;
  /**
   * Realised-weighted average commission % on form rows with a resolved rate
   * (sum of realised×pct over sum of realised for rows with positive realised).
   */
  commission_avg_percent?: number | null;
  commission_weighted_num?: number;
  commission_weighted_den?: number;
  /** True when the hotel (or admin) added at least one extra group line on the submission form */
  has_extra_groups?: boolean;
  /** Latest internal admin note for this hotel (editable by any admin). */
  internal_comment_id?: string | null;
  internal_comment_body?: string;
}

interface Campaign {
  id: string;
  period_month: number;
  period_year: number;
  name: string | null;
  status: string;
  /** When set and status is `scheduled`, automation launches after this instant (UTC). */
  scheduled_at: string | null;
}

/** `revenue_collection_hotel_commissions.invoicing_entity`; null when unset or invalid. */
function normaliseHotelInvoicingEntity(raw: unknown): 'NL' | 'SP' | null {
  if (raw === 'NL' || raw === 'SP') return raw;
  return null;
}

function formatHotelInvoicingEntityCell(entity: 'NL' | 'SP' | null | undefined): string {
  return entity === 'NL' || entity === 'SP' ? entity : '—';
}

/** Clipboard API fails in some iframe / permission contexts; fallback keeps admin copy buttons working. */
async function copyPlainTextToClipboard(text: string): Promise<void> {
  if (typeof globalThis.navigator?.clipboard?.writeText === 'function') {
    try {
      await globalThis.navigator.clipboard.writeText(text);
      return;
    } catch {
      /* try legacy below */
    }
  }
  if (typeof document === 'undefined') {
    throw new Error('Clipboard unavailable');
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.left = '-9999px';
  ta.style.top = '0';
  document.body.appendChild(ta);
  ta.focus();
  ta.select();
  ta.setSelectionRange(0, text.length);
  const ok = document.execCommand('copy');
  document.body.removeChild(ta);
  if (!ok) {
    throw new Error('Clipboard copy failed');
  }
}

function campaignHotelDisplayTitle(h: CampaignHotel): string {
  return (h.hotel?.title ?? '').trim() || `Hotel ${h.hotel_id}`;
}

/** Form-row materialization for one hotel; only defined once the hotel has submitted (validated). */
function hotelMaterializationPct(h: CampaignHotel): number | null {
  if (!isRevenueCampaignHotelFormComplete(h.status)) return null;
  return revenueCampaignMaterializationPct(
    h.materialization_form_realised ?? 0,
    h.materialization_form_estimated ?? 0,
  );
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

/** UI buckets for the hotel table filter (reminders grouped; validated split by admin validation). */
const CAMPAIGN_HOTEL_FILTER_BUCKETS = [
  'pending',
  'email_sent',
  'bounced',
  'reminders',
  'submitted',
  'validated',
  'no_production',
  'invoice_sent',
  'cancelled',
] as const;

type CampaignHotelFilterBucket = (typeof CAMPAIGN_HOTEL_FILTER_BUCKETS)[number];

function campaignHotelFilterBucketLabel(bucket: CampaignHotelFilterBucket): string {
  const labels: Record<CampaignHotelFilterBucket, string> = {
    pending: 'Pending',
    email_sent: 'Email sent',
    bounced: 'Bounced',
    reminders: 'Reminders (1–3)',
    submitted: 'Submitted',
    validated: 'Validated',
    no_production: 'No production',
    invoice_sent: 'Invoice sent',
    cancelled: 'Cancelled',
  };
  return labels[bucket];
}

/** Map row to filter bucket; unknown statuses return null (always kept visible in the table). */
function campaignHotelFilterBucket(h: CampaignHotel): CampaignHotelFilterBucket | null {
  switch (h.status) {
    case 'pending':
      return 'pending';
    case 'email_sent':
      return 'email_sent';
    case 'bounced':
      return 'bounced';
    case 'reminder_1':
    case 'reminder_2':
    case 'reminder_3':
      return 'reminders';
    case 'validated':
      return h.validated_by ? 'validated' : 'submitted';
    case 'no_production':
      return 'no_production';
    case 'invoice_sent':
      return 'invoice_sent';
    case 'cancelled':
      return 'cancelled';
    default:
      return null;
  }
}

const CAMPAIGN_HOTEL_INVOICING_ENTITY_BUCKETS = ['NL', 'SP', 'not_set'] as const;
type CampaignHotelInvoicingEntityBucket = (typeof CAMPAIGN_HOTEL_INVOICING_ENTITY_BUCKETS)[number];

function campaignHotelInvoicingEntityFilterBucket(h: CampaignHotel): CampaignHotelInvoicingEntityBucket {
  const e = h.invoicing_entity;
  if (e === 'NL' || e === 'SP') return e;
  return 'not_set';
}

function campaignHotelInvoicingEntityFilterLabel(bucket: CampaignHotelInvoicingEntityBucket): string {
  const labels: Record<CampaignHotelInvoicingEntityBucket, string> = {
    NL: 'NL',
    SP: 'SP',
    not_set: 'Not set',
  };
  return labels[bucket];
}

function CampaignHotelStatusFilterDropdown({
  includedFilterBuckets,
  setIncludedFilterBuckets,
}: Readonly<{
  includedFilterBuckets: Set<CampaignHotelFilterBucket>;
  setIncludedFilterBuckets: Dispatch<SetStateAction<Set<CampaignHotelFilterBucket>>>;
}>) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const total = CAMPAIGN_HOTEL_FILTER_BUCKETS.length;
  const n = includedFilterBuckets.size;
  let summary: string;
  if (n === total) summary = 'All statuses';
  else if (n === 0) summary = 'None';
  else summary = `${n}/${total}`;

  const toggleBucket = (bucket: CampaignHotelFilterBucket) => {
    setIncludedFilterBuckets((prev) => {
      const next = new Set(prev);
      if (next.has(bucket)) next.delete(bucket);
      else next.add(bucket);
      return next;
    });
  };

  const selectAll = () => setIncludedFilterBuckets(new Set(CAMPAIGN_HOTEL_FILTER_BUCKETS));
  const clearAll = () => setIncludedFilterBuckets(new Set());

  return (
    <div className="relative w-44 shrink-0" ref={wrapRef}>
      <button
        type="button"
        id="campaign-hotel-status-filter"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-1.5 rounded-lg border border-slate-300/90 bg-white px-2 py-1.5 text-left text-xs font-medium text-slate-900 shadow-sm transition-[background-color,border-color] hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-action/35 focus-visible:ring-offset-1"
      >
        <span className="min-w-0 truncate">{summary}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>

      {open ? (
        <div className="absolute left-0 z-30 mt-1 w-56 rounded-lg border border-slate-200/90 bg-white py-1 shadow-lg ring-1 ring-slate-900/5">
          <div className="flex items-center justify-end gap-2 border-b border-slate-100 px-2 py-1.5">
            <button type="button" onClick={selectAll} className="btn-text-action !text-xs">
              Select all
            </button>
            <button
              type="button"
              onClick={clearAll}
              className="rounded border-none bg-transparent p-0 text-xs font-medium text-slate-600 transition-colors hover:text-slate-900 hover:underline hover:underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/40 focus-visible:ring-offset-2"
            >
              Clear
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto py-0.5">
            {CAMPAIGN_HOTEL_FILTER_BUCKETS.map((bucket) => (
              <label
                key={bucket}
                className="flex cursor-pointer items-center gap-2.5 px-3 py-2 text-xs font-medium text-slate-900 hover:bg-slate-50"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-action accent-action focus:ring-action/30"
                  checked={includedFilterBuckets.has(bucket)}
                  onChange={() => toggleBucket(bucket)}
                />
                <span>{campaignHotelFilterBucketLabel(bucket)}</span>
              </label>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function CampaignHotelInvoicingEntityFilterDropdown({
  includedBuckets,
  setIncludedBuckets,
}: Readonly<{
  includedBuckets: Set<CampaignHotelInvoicingEntityBucket>;
  setIncludedBuckets: Dispatch<SetStateAction<Set<CampaignHotelInvoicingEntityBucket>>>;
}>) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const total = CAMPAIGN_HOTEL_INVOICING_ENTITY_BUCKETS.length;
  const n = includedBuckets.size;
  let summary: string;
  if (n === total) summary = 'All entities';
  else if (n === 0) summary = 'None';
  else summary = `${n}/${total}`;

  const toggleBucket = (bucket: CampaignHotelInvoicingEntityBucket) => {
    setIncludedBuckets((prev) => {
      const next = new Set(prev);
      if (next.has(bucket)) next.delete(bucket);
      else next.add(bucket);
      return next;
    });
  };

  const selectAll = () => setIncludedBuckets(new Set(CAMPAIGN_HOTEL_INVOICING_ENTITY_BUCKETS));
  const clearAll = () => setIncludedBuckets(new Set());

  return (
    <div className="relative w-44 shrink-0" ref={wrapRef}>
      <button
        type="button"
        id="campaign-hotel-invoicing-entity-filter"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-1.5 rounded-lg border border-slate-300/90 bg-white px-2 py-1.5 text-left text-xs font-medium text-slate-900 shadow-sm transition-[background-color,border-color] hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-action/35 focus-visible:ring-offset-1"
      >
        <span className="min-w-0 truncate">{summary}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>

      {open ? (
        <div className="absolute left-0 z-30 mt-1 w-56 rounded-lg border border-slate-200/90 bg-white py-1 shadow-lg ring-1 ring-slate-900/5">
          <div className="flex items-center justify-end gap-2 border-b border-slate-100 px-2 py-1.5">
            <button type="button" onClick={selectAll} className="btn-text-action !text-xs">
              Select all
            </button>
            <button
              type="button"
              onClick={clearAll}
              className="rounded border-none bg-transparent p-0 text-xs font-medium text-slate-600 transition-colors hover:text-slate-900 hover:underline hover:underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/40 focus-visible:ring-offset-2"
            >
              Clear
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto py-0.5">
            {CAMPAIGN_HOTEL_INVOICING_ENTITY_BUCKETS.map((bucket) => (
              <label
                key={bucket}
                className="flex cursor-pointer items-center gap-2.5 px-3 py-2 text-xs font-medium text-slate-900 hover:bg-slate-50"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-action accent-action focus:ring-action/30"
                  checked={includedBuckets.has(bucket)}
                  onChange={() => toggleBucket(bucket)}
                />
                <span>{campaignHotelInvoicingEntityFilterLabel(bucket)}</span>
              </label>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

const LAUNCHED_REMINDER_ROW_DEFAULTS: { templateId: string; days: number }[] = [
  { templateId: '', days: 7 },
  { templateId: '', days: 14 },
  { templateId: '', days: 21 },
];

const LAUNCHED_REMINDER_SLOT_TYPES = ['reminder_1', 'reminder_2', 'reminder_3'] as const;

function templateOptionsForReminderSlot(
  templates: LaunchedReminderTemplateOption[],
  slotIndex: number,
  selectedId: string,
): string[] {
  const slotType = LAUNCHED_REMINDER_SLOT_TYPES[slotIndex];
  const forSlot = templates.filter((t) => t.type === slotType);
  const ids = forSlot.map((t) => t.id);
  if (selectedId && !ids.includes(selectedId)) {
    const selected = templates.find((t) => t.id === selectedId);
    if (selected) return [selectedId, ...ids];
  }
  return ids;
}

interface LaunchedReminderTemplateOption {
  id: string;
  name: string;
  type: string;
}

const REMINDER_FIELD_CLASS =
  'rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500';

function validationErrorForLaunchedReminderRows(
  rows: { templateId: string; days: number }[],
): string | null {
  for (let i = 0; i < rows.length; i++) {
    const { templateId, days } = rows[i];
    if (templateId && days < 1) {
      return `Reminder ${i + 1}: use at least 1 day after the reference send, or clear the template to disable this reminder.`;
    }
  }
  return null;
}

async function persistLaunchedReminderConfigSlot(
  campaignId: string,
  reminderNumber: number,
  templateId: string,
  days: number,
): Promise<void> {
  const enabled = templateId !== '' && days > 0;
  if (!enabled) {
    const { error: dErr } = await supabase
      .from('revenue_collection_reminder_config')
      .delete()
      .eq('campaign_id', campaignId)
      .eq('reminder_number', reminderNumber);
    if (dErr) throw dErr;
    return;
  }
  const { data: existing } = await supabase
    .from('revenue_collection_reminder_config')
    .select('id')
    .eq('campaign_id', campaignId)
    .eq('reminder_number', reminderNumber)
    .maybeSingle();
  if (existing?.id) {
    const { error: uErr } = await supabase
      .from('revenue_collection_reminder_config')
      .update({
        reminder_template_id: templateId,
        days_after_initial: days,
      })
      .eq('id', existing.id);
    if (uErr) throw uErr;
    return;
  }
  const { error: iErr } = await supabase
    .from('revenue_collection_reminder_config')
    .insert({
      campaign_id: campaignId,
      reminder_number: reminderNumber,
      reminder_template_id: templateId,
      days_after_initial: days,
    });
  if (iErr) throw iErr;
}

type CampaignEmailSendRow = {
  hotel_id: number;
  email_kind: string;
  delivery_state: string;
  last_event_at: string | null;
  last_event_detail: string | null;
  postmark_message_id: string;
  bounce_tooltip: string | null;
};

interface CampaignDetailLoadedSnapshot {
  campaign: Campaign;
  hotels: CampaignHotel[];
  metrics: {
    totalEstimated: number;
    materializationRealised: number;
    materializationEstimated: number;
    bounces: number;
    validated: number;
  };
  emailSends: CampaignEmailSendRow[];
  isLaunched: boolean;
}

async function loadCampaignDetailSnapshot(
  campaignId: string,
): Promise<CampaignDetailLoadedSnapshot> {
  const { data: campData, error: campErr } = await supabase
    .from('revenue_collection_campaigns')
    .select('id, period_month, period_year, name, status, scheduled_at')
    .eq('id', campaignId)
    .single();

  if (campErr || !campData) throw campErr || new Error('Campaign not found');

  const { data: chData, error: chErr } = await supabase
    .from('revenue_collection_campaign_hotels')
    .select(
      'hotel_id, status, validated_by, email_sent_at, reminder_1_at, reminder_2_at, reminder_3_at, reminders_cancelled, initial_send_error',
    )
    .eq('campaign_id', campaignId);

  if (chErr) throw chErr;

  const [{ data: sendsRows }, bounceEvents] = await Promise.all([
    supabase
      .from('revenue_collection_email_sends')
      .select(
        'hotel_id, email_kind, delivery_state, last_event_at, last_event_detail, postmark_message_id',
      )
      .eq('campaign_id', campaignId),
    fetchRevenueCollectionBounceEvents({ campaignId }),
  ]);

  const hotelIds = (chData || []).map((ch) => ch.hotel_id);
  const { data: hotelData } = await supabase
    .from('hotels')
    .select('id, title, main_manager_email')
    .in('id', hotelIds);

  const hotelMap = new Map((hotelData || []).map((h) => [h.id, h]));

  const hotelDefaultCommissionById = new Map<number, number>();
  const hotelInvoicingEntityById = new Map<number, 'NL' | 'SP' | null>();
  if (hotelIds.length > 0) {
    const { data: hotelCommData } = await supabase
      .from('revenue_collection_hotel_commissions')
      .select('hotel_id, commission_percent, invoicing_entity')
      .in('hotel_id', hotelIds);
    (hotelCommData || []).forEach(
      (h: { hotel_id: number; commission_percent: number; invoicing_entity?: string | null }) => {
        hotelDefaultCommissionById.set(h.hotel_id, Number(h.commission_percent));
        hotelInvoicingEntityById.set(h.hotel_id, normaliseHotelInvoicingEntity(h.invoicing_entity));
      },
    );
  }

  const invoiceByHotel = new Map<number, number>();
  const addInvoiceLine = (
    hotelId: number,
    realisedRaw: unknown,
    commissionOverride: number | null | undefined,
  ) => {
    if (realisedRaw == null || realisedRaw === '') return;
    const realised = Number(realisedRaw);
    if (!Number.isFinite(realised)) return;
    const defaultComm = hotelDefaultCommissionById.get(hotelId);
    const resolvedComm = commissionOverride ?? defaultComm;
    if (resolvedComm == null) return;
    const pct = Number(resolvedComm);
    if (Number.isNaN(pct)) return;
    invoiceByHotel.set(
      hotelId,
      (invoiceByHotel.get(hotelId) || 0) + realised * (pct / 100),
    );
  };

  const commWeightedByHotel = new Map<number, { num: number; den: number }>();
  const bumpCommissionWeighted = (
    hotelId: number,
    realisedRaw: unknown,
    commissionOverride: number | null | undefined,
  ) => {
    if (realisedRaw == null || realisedRaw === '') return;
    const realised = Number(realisedRaw);
    if (!Number.isFinite(realised) || realised <= 0) return;
    const defaultComm = hotelDefaultCommissionById.get(hotelId);
    const resolvedComm = commissionOverride ?? defaultComm;
    if (resolvedComm == null) return;
    const pct = Number(resolvedComm);
    if (Number.isNaN(pct)) return;
    const cur = commWeightedByHotel.get(hotelId) ?? { num: 0, den: 0 };
    cur.num += realised * pct;
    cur.den += realised;
    commWeightedByHotel.set(hotelId, cur);
  };

  let totalEstimated = 0;
  const kpiHotelIds = (chData || [])
    .filter((ch) => isCampaignHotelIncludedInKpis(ch.status))
    .map((ch) => ch.hotel_id);
  if (kpiHotelIds.length > 0) {
    const { data: groups } = await supabase
      .from('mv_group_enriched_summary')
      .select('hotel_id, estimated_revenue')
      .eq('arrival_month', campData.period_month)
      .eq('arrival_year', campData.period_year)
      .in('hotel_id', kpiHotelIds)
      .in('status', ['Booked', 'reserved'])
      .gt('estimated_revenue', 0);
    const byHotel = new Map<number, number>();
    (groups || []).forEach((g: { hotel_id: number; estimated_revenue: number }) => {
      const rev = Number(g.estimated_revenue) || 0;
      byHotel.set(g.hotel_id, (byHotel.get(g.hotel_id) || 0) + rev);
    });
    totalEstimated = Array.from(byHotel.values()).reduce((a, b) => a + b, 0);
  }

  const validatedHotelIds = new Set(
    (chData || [])
      .filter(
        (ch) =>
          isCampaignHotelIncludedInKpis(ch.status) &&
          isRevenueCampaignHotelFormComplete(ch.status),
      )
      .map((ch) => ch.hotel_id),
  );

  const { data: respData } = await supabase
    .from('revenue_collection_responses')
    .select('hotel_id, realised_revenue, estimated_revenue, commission_percent')
    .eq('campaign_id', campaignId);
  const realisedByHotel = new Map<number, number>();
  const matFormByHotel = new Map<number, { realised: number; estimated: number }>();
  const bumpMatForm = (hotelId: number, realised: number, estimated: number) => {
    const o = matFormByHotel.get(hotelId) ?? { realised: 0, estimated: 0 };
    o.realised += realised;
    o.estimated += estimated;
    matFormByHotel.set(hotelId, o);
  };
  let materializationRealised = 0;
  let materializationEstimated = 0;
  (respData || []).forEach(
    (r: {
      hotel_id: number;
      realised_revenue: number | null;
      estimated_revenue: number;
      commission_percent?: number | null;
    }) => {
      const rev = Number(r.realised_revenue) || 0;
      const estRow = Number(r.estimated_revenue) || 0;
      realisedByHotel.set(r.hotel_id, (realisedByHotel.get(r.hotel_id) || 0) + rev);
      bumpMatForm(r.hotel_id, rev, estRow);
      addInvoiceLine(r.hotel_id, r.realised_revenue, r.commission_percent);
      bumpCommissionWeighted(r.hotel_id, r.realised_revenue, r.commission_percent);
      if (validatedHotelIds.has(r.hotel_id)) {
        materializationRealised += rev;
        materializationEstimated += estRow;
      }
    },
  );

  const { data: manualRealisedRows } = await supabase
    .from('revenue_collection_manual_lines')
    .select('hotel_id, realised_revenue, estimated_revenue, commission_percent')
    .eq('campaign_id', campaignId);
  const hotelsWithExtraGroups = new Set<number>();
  (manualRealisedRows || []).forEach(
    (r: {
      hotel_id: number;
      realised_revenue: number | null;
      estimated_revenue: number | null;
      commission_percent?: number | null;
    }) => {
      hotelsWithExtraGroups.add(r.hotel_id);
      const rev = Number(r.realised_revenue ?? 0);
      const estRow = Number(r.estimated_revenue) || 0;
      realisedByHotel.set(r.hotel_id, (realisedByHotel.get(r.hotel_id) || 0) + rev);
      bumpMatForm(r.hotel_id, rev, estRow);
      addInvoiceLine(r.hotel_id, r.realised_revenue, r.commission_percent);
      bumpCommissionWeighted(r.hotel_id, r.realised_revenue, r.commission_percent);
      if (validatedHotelIds.has(r.hotel_id)) {
        materializationRealised += rev;
        materializationEstimated += estRow;
      }
    },
  );

  const byHotelEst = new Map<number, number>();
  const byHotelBookedGroups = new Map<number, number>();
  if (hotelIds.length > 0) {
    const { data: g } = await supabase
      .from('mv_group_enriched_summary')
      .select('hotel_id, estimated_revenue')
      .eq('arrival_month', campData.period_month)
      .eq('arrival_year', campData.period_year)
      .in('hotel_id', hotelIds)
      .in('status', ['Booked', 'reserved'])
      .gt('estimated_revenue', 0);
    (g || []).forEach((x: { hotel_id: number; estimated_revenue: number }) => {
      const rev = Number(x.estimated_revenue) || 0;
      byHotelEst.set(x.hotel_id, (byHotelEst.get(x.hotel_id) || 0) + rev);
      byHotelBookedGroups.set(
        x.hotel_id,
        (byHotelBookedGroups.get(x.hotel_id) || 0) + 1,
      );
    });
  }

  const internalNoteByHotel = new Map<number, { id: string; body: string }>();
  if (hotelIds.length > 0) {
    const { data: commentRows } = await supabase
      .from('revenue_collection_hotel_internal_comments')
      .select('hotel_id, id, body, created_at')
      .eq('campaign_id', campaignId)
      .in('hotel_id', hotelIds)
      .order('created_at', { ascending: false });
    for (const row of commentRows ?? []) {
      const hid = row.hotel_id as number;
      if (!internalNoteByHotel.has(hid)) {
        internalNoteByHotel.set(hid, {
          id: String(row.id),
          body: String(row.body),
        });
      }
    }
  }

  const list: CampaignHotel[] = (chData || []).map((ch) => {
    const mf = matFormByHotel.get(ch.hotel_id);
    const cw = commWeightedByHotel.get(ch.hotel_id);
    const cwd = cw?.den ?? 0;
    const cwn = cw?.num ?? 0;
    const internalNote = internalNoteByHotel.get(ch.hotel_id);
    return {
      ...ch,
      hotel: hotelMap.get(ch.hotel_id),
      invoicing_entity: hotelInvoicingEntityById.get(ch.hotel_id) ?? null,
      booked_groups_count: byHotelBookedGroups.get(ch.hotel_id) || 0,
      estimated_revenue: byHotelEst.get(ch.hotel_id) || 0,
      realised_revenue: realisedByHotel.get(ch.hotel_id) || 0,
      materialization_form_realised: mf?.realised ?? 0,
      materialization_form_estimated: mf?.estimated ?? 0,
      invoice_amount: invoiceByHotel.get(ch.hotel_id) || 0,
      commission_avg_percent: cwd > 0 ? cwn / cwd : null,
      commission_weighted_num: cwn,
      commission_weighted_den: cwd,
      internal_comment_id: internalNote?.id ?? null,
      internal_comment_body: internalNote?.body ?? '',
      has_extra_groups: hotelsWithExtraGroups.has(ch.hotel_id),
    };
  });

  return {
    campaign: campData,
    hotels: list,
    metrics: {
      totalEstimated,
      materializationRealised,
      materializationEstimated,
      bounces: list.filter((h) => h.status === 'bounced').length,
      validated: list.filter(
        (h) =>
          isCampaignHotelIncludedInKpis(h.status) &&
          isRevenueCampaignHotelFormComplete(h.status),
      ).length,
    },
    emailSends: enrichEmailSendsWithBounceTooltips(
      (sendsRows ?? []) as Omit<
        CampaignEmailSendRow,
        'bounce_tooltip'
      >[],
      bounceEvents,
      deliveryStateLabel,
    ) as CampaignEmailSendRow[],
    isLaunched: campData.status === 'launched',
  };
}

async function loadLaunchedReminderEditorState(campaignId: string): Promise<{
  templates: LaunchedReminderTemplateOption[];
  rows: { templateId: string; days: number }[];
}> {
  const [{ data: remTmpl }, { data: remCfg }] = await Promise.all([
    supabase
      .from('revenue_collection_email_templates')
      .select('id, name, type')
      .in('type', ['reminder_1', 'reminder_2', 'reminder_3']),
    supabase
      .from('revenue_collection_reminder_config')
      .select('reminder_number, reminder_template_id, days_after_initial')
      .eq('campaign_id', campaignId)
      .order('reminder_number'),
  ]);
  const templates = (remTmpl ?? []) as LaunchedReminderTemplateOption[];
  const rows = LAUNCHED_REMINDER_ROW_DEFAULTS.map((d) => ({ ...d }));
  (remCfg ?? []).forEach(
    (row: {
      reminder_number: number;
      reminder_template_id: string;
      days_after_initial: number;
    }) => {
      const i = row.reminder_number - 1;
      if (i >= 0 && i < 3) {
        rows[i] = {
          templateId: row.reminder_template_id ?? '',
          days: Number(row.days_after_initial) || 7,
        };
      }
    },
  );
  return { templates, rows };
}

function CampaignHotelStatusBadge({
  status,
  validatedBy,
  sendError,
}: Readonly<{
  status: string;
  validatedBy: string | null | undefined;
  sendError?: string | null;
}>) {
  const colors: Record<string, string> = {
    pending: 'bg-gray-100 text-gray-800',
    email_sent: 'bg-blue-100 text-blue-800',
    reminder_1: 'bg-amber-100 text-amber-800',
    reminder_2: 'bg-amber-100 text-amber-800',
    reminder_3: 'bg-amber-100 text-amber-800',
    bounced: 'bg-red-100 text-red-800',
    validated: 'bg-orange-100 text-orange-800',
    invoice_sent: 'bg-teal-100 text-teal-900',
    cancelled: 'bg-slate-200 text-slate-700',
  };
  const labels: Record<string, string> = {
    pending: 'Pending',
    email_sent: 'Email sent',
    reminder_1: 'Reminder 1',
    reminder_2: 'Reminder 2',
    reminder_3: 'Reminder 3',
    bounced: 'Bounced',
    validated: 'Submitted',
    no_production: 'No production',
    invoice_sent: 'Invoice sent',
    cancelled: 'Cancelled',
  };
  const badgeBase =
    'inline-flex items-center whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium leading-none';
  if (status === 'cancelled') {
    return <span className={`${badgeBase} bg-slate-200 text-slate-700`}>Cancelled</span>;
  }
  if (status === 'invoice_sent') {
    return <span className={`${badgeBase} bg-teal-600 text-white`}>Invoice sent</span>;
  }
  if (status === 'no_production') {
    return <span className={`${badgeBase} bg-violet-100 text-violet-900`}>No production</span>;
  }
  if (status === 'validated' && validatedBy) {
    return <span className={`${badgeBase} bg-emerald-600 text-white`}>Validated</span>;
  }
  const label = labels[status] ?? status.replace(/_/g, ' ');
  return (
    <span
      className={`${badgeBase} ${colors[status] || 'bg-gray-100 text-gray-800'}`}
      title={sendError?.trim() || undefined}
    >
      {label}
    </span>
  );
}

type CampaignEmailSendAggRow = {
  email_kind: string;
  delivery_state: string;
  last_event_at: string | null;
  bounce_tooltip: string | null;
};

function groupCampaignEmailSendsByHotel(
  rows: CampaignEmailSendRow[],
): Map<number, CampaignEmailSendAggRow[]> {
  const m = new Map<number, CampaignEmailSendAggRow[]>();
  for (const row of rows) {
    const arr = m.get(row.hotel_id) ?? [];
    arr.push({
      email_kind: row.email_kind,
      delivery_state: row.delivery_state,
      last_event_at: row.last_event_at,
      bounce_tooltip: row.bounce_tooltip,
    });
    m.set(row.hotel_id, arr);
  }
  return m;
}

function aggregateHotelTableFooterTotals(hotels: CampaignHotel[]) {
  let estimatedMv = 0;
  let realised = 0;
  let matFormRealised = 0;
  let matFormEstimated = 0;
  let invoice = 0;
  let commissionNum = 0;
  let commissionDen = 0;
  for (const h of hotels) {
    estimatedMv += h.estimated_revenue ?? 0;
    realised += h.realised_revenue ?? 0;
    if (isRevenueCampaignHotelFormComplete(h.status)) {
      matFormRealised += h.materialization_form_realised ?? 0;
      matFormEstimated += h.materialization_form_estimated ?? 0;
    }
    invoice += h.invoice_amount ?? 0;
    commissionNum += h.commission_weighted_num ?? 0;
    commissionDen += h.commission_weighted_den ?? 0;
  }
  const matPct = revenueCampaignMaterializationPct(matFormRealised, matFormEstimated);
  const commissionAvgPct =
    commissionDen > 0 ? commissionNum / commissionDen : null;
  return {
    estimatedMv,
    realised,
    matPct,
    matFormRealised,
    matFormEstimated,
    commissionAvgPct,
    invoice,
  };
}

function useCampaignDetailPage() {
  const { id: campaignId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const launchParam = searchParams.get('launch') === 'true';
  const { canMutateRevenue } = useRevenueAccess();

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [hotels, setHotels] = useState<CampaignHotel[]>([]);
  const [metrics, setMetrics] = useState({
    totalEstimated: 0,
    /** Realised total from responses + manual lines for hotels with validated submission only. */
    materializationRealised: 0,
    /** Estimated total from those same rows (for materialization ratio). */
    materializationEstimated: 0,
    bounces: 0,
    validated: 0,
  });
  const [loading, setLoading] = useState(true);
  const [launching, setLaunching] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedHotelId, setSelectedHotelId] = useState<number | null>(null);
  const [cancellingHotelId, setCancellingHotelId] = useState<number | null>(null);
  const [cancellingCampaignHotelId, setCancellingCampaignHotelId] = useState<number | null>(null);
  const [campaignEmailSends, setCampaignEmailSends] = useState<CampaignEmailSendRow[]>([]);

  const [launchedReminderTemplates, setLaunchedReminderTemplates] = useState<
    LaunchedReminderTemplateOption[]
  >([]);
  const [launchedReminderRows, setLaunchedReminderRows] = useState<
    { templateId: string; days: number }[]
  >(() => [...LAUNCHED_REMINDER_ROW_DEFAULTS]);
  const [launchedReminderModalOpen, setLaunchedReminderModalOpen] = useState(false);
  const [addHotelsModalOpen, setAddHotelsModalOpen] = useState(false);
  const [launchedReminderSaving, setLaunchedReminderSaving] = useState(false);
  const [launchedReminderError, setLaunchedReminderError] = useState<string | null>(null);
  const launchedReminderDialogRef = useRef<HTMLDialogElement>(null);

  useLayoutEffect(() => {
    const el = launchedReminderDialogRef.current;
    if (!el || !launchedReminderModalOpen || campaign?.status !== 'launched') return;
    if (!el.open) el.showModal();
    return () => {
      if (el.isConnected && el.open) el.close();
    };
  }, [launchedReminderModalOpen, campaign?.status]);

  const refreshLaunchedReminderEditor = useCallback(async () => {
    if (!campaignId) return;
    const { templates, rows } = await loadLaunchedReminderEditorState(campaignId);
    setLaunchedReminderTemplates(templates);
    setLaunchedReminderRows(rows);
    setLaunchedReminderError(null);
  }, [campaignId]);

  const fetchCampaign = useCallback(async (opts?: { silent?: boolean }) => {
    if (!campaignId) return;
    const silent = opts?.silent === true;
    if (!silent) {
      setLoading(true);
      setCampaignEmailSends([]);
    }
    try {
      const snap = await loadCampaignDetailSnapshot(campaignId);
      setCampaign(snap.campaign);
      setCampaignEmailSends(snap.emailSends);
      setHotels(snap.hotels);
      setMetrics(snap.metrics);
      if (snap.isLaunched) {
        await refreshLaunchedReminderEditor();
      } else {
        setLaunchedReminderTemplates([]);
        setLaunchedReminderRows([...LAUNCHED_REMINDER_ROW_DEFAULTS]);
        setLaunchedReminderError(null);
        setLaunchedReminderModalOpen(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [campaignId, refreshLaunchedReminderEditor]);

  const [includedFilterBuckets, setIncludedFilterBuckets] = useState<Set<CampaignHotelFilterBucket>>(
    () => new Set(CAMPAIGN_HOTEL_FILTER_BUCKETS),
  );
  const [includedInvoicingEntityBuckets, setIncludedInvoicingEntityBuckets] = useState<
    Set<CampaignHotelInvoicingEntityBucket>
  >(() => new Set(CAMPAIGN_HOTEL_INVOICING_ENTITY_BUCKETS));
  const [hotelNameSearchQuery, setHotelNameSearchQuery] = useState('');
  const [filterExtraGroupsOnly, setFilterExtraGroupsOnly] = useState(false);

  const filteredHotels = useMemo(() => {
    const nameQuery = hotelNameSearchQuery.trim().toLowerCase();
    const list = hotels.filter((h) => {
      const bucket = campaignHotelFilterBucket(h);
      if (bucket !== null && !includedFilterBuckets.has(bucket)) return false;
      const entityBucket = campaignHotelInvoicingEntityFilterBucket(h);
      if (!includedInvoicingEntityBuckets.has(entityBucket)) return false;
      if (filterExtraGroupsOnly && !h.has_extra_groups) return false;
      if (nameQuery && !campaignHotelDisplayTitle(h).toLowerCase().includes(nameQuery)) {
        return false;
      }
      return true;
    });
    list.sort((a, b) => {
      const byName = campaignHotelDisplayTitle(a).localeCompare(
        campaignHotelDisplayTitle(b),
        undefined,
        { sensitivity: 'base' },
      );
      if (byName !== 0) return byName;
      return a.hotel_id - b.hotel_id;
    });
    return list;
  }, [
    hotels,
    includedFilterBuckets,
    includedInvoicingEntityBuckets,
    hotelNameSearchQuery,
    filterExtraGroupsOnly,
  ]);

  const kpiHotels = useMemo(
    () => filteredHotels.filter((h) => isCampaignHotelIncludedInKpis(h.status)),
    [filteredHotels],
  );

  const filteredKpiMetrics = useMemo(() => {
    const validatedRows = kpiHotels.filter((h) => isRevenueCampaignHotelFormComplete(h.status));
    let materializationRealised = 0;
    let materializationEstimated = 0;
    for (const h of validatedRows) {
      materializationRealised += h.materialization_form_realised ?? 0;
      materializationEstimated += h.materialization_form_estimated ?? 0;
    }
    return {
      totalEstimated: kpiHotels.reduce((s, h) => s + (h.estimated_revenue ?? 0), 0),
      validated: validatedRows.length,
      materializationRealised,
      materializationEstimated,
    };
  }, [kpiHotels]);

  const submissionsSharePct = useMemo(() => {
    if (kpiHotels.length === 0) return null;
    return Math.round((filteredKpiMetrics.validated / kpiHotels.length) * 100);
  }, [kpiHotels.length, filteredKpiMetrics.validated]);

  /** Materialization: realised ÷ estimated for validated hotels only; null when denominator is 0. Can exceed 100%. */
  const materializationPct = useMemo(
    () =>
      revenueCampaignMaterializationPct(
        filteredKpiMetrics.materializationRealised,
        filteredKpiMetrics.materializationEstimated,
      ),
    [filteredKpiMetrics.materializationEstimated, filteredKpiMetrics.materializationRealised],
  );

  const emailDeliveryKpi = useMemo(() => {
    const idSet = new Set(filteredHotels.map((h) => h.hotel_id));
    const rows = campaignEmailSends.filter((r) => idSet.has(r.hotel_id));
    const total = rows.length;
    if (total === 0) {
      return { total: 0, delivered: 0, pct: null as number | null };
    }
    const delivered = rows.filter((r) => isDeliveredLikeEmailState(r.delivery_state)).length;
    return { total, delivered, pct: Math.round((delivered / total) * 100) };
  }, [campaignEmailSends, filteredHotels]);

  const emailSendsByHotel = useMemo(
    () => groupCampaignEmailSendsByHotel(campaignEmailSends),
    [campaignEmailSends],
  );

  /** Sums for the hotels table footer (Mat. = Σ form realised / Σ form estimated, visible rows). */
  const hotelTableFooterTotals = useMemo(
    () => aggregateHotelTableFooterTotals(filteredHotels),
    [filteredHotels],
  );

  useEffect(() => {
    fetchCampaign();
  }, [fetchCampaign]);

  const handleSaveLaunchedReminderConfig = useCallback(async () => {
    if (!campaignId || !canMutateRevenue) return;
    setLaunchedReminderError(null);
    const rowError = validationErrorForLaunchedReminderRows(launchedReminderRows);
    if (rowError) {
      setLaunchedReminderError(rowError);
      return;
    }
    setLaunchedReminderSaving(true);
    try {
      for (let i = 0; i < 3; i++) {
        const { templateId, days } = launchedReminderRows[i];
        await persistLaunchedReminderConfigSlot(campaignId, i + 1, templateId, days);
      }
      await refreshLaunchedReminderEditor();
      setLaunchedReminderModalOpen(false);
    } catch (e) {
      setLaunchedReminderError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setLaunchedReminderSaving(false);
    }
  }, [campaignId, launchedReminderRows, refreshLaunchedReminderEditor, canMutateRevenue]);

  const handleLaunch = useCallback(async () => {
    if (!campaignId || !canMutateRevenue) return;
    setLaunching(true);
    setError(null);
    try {
      const result = await drainCampaignInitialSends(campaignId);
      await fetchCampaign();
      globalThis.history.replaceState({}, '', `/revenue-collection/campaigns/${campaignId}`);
      if (result.permanent > 0) {
        setError(
          `${result.permanent} hotel(s) were not invited because the invoicing address was rejected. Hover the Pending status to see why.`,
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Launch failed');
    } finally {
      setLaunching(false);
    }
  }, [campaignId, fetchCampaign, canMutateRevenue]);

  useEffect(() => {
    if (canMutateRevenue && launchParam && campaign?.status === 'draft' && !launching) {
      handleLaunch();
    }
  }, [launchParam, campaign?.status, launching, handleLaunch, canMutateRevenue]);

  /** Pick up automated scheduled→launched transition without a full page reload. */
  useEffect(() => {
    if (!campaignId || campaign?.status !== 'scheduled') return;
    const id = window.setInterval(() => {
      void fetchCampaign({ silent: true });
    }, 45_000);
    return () => window.clearInterval(id);
  }, [campaignId, campaign?.status, fetchCampaign]);

  const handleExportDetailsExcel = useCallback(async () => {
    if (!campaignId || !campaign) return;
    setError(null);
    setExportingExcel(true);
    try {
      await downloadRevenueCampaignExcel({
        campaignId,
        periodMonth: campaign.period_month,
        periodYear: campaign.period_year,
        campaignName: campaign.name,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExportingExcel(false);
    }
  }, [campaignId, campaign]);

  const handleCancelReminders = async (hotelId: number) => {
    if (!canMutateRevenue) return;
    setCancellingHotelId(hotelId);
    try {
      await supabase
        .from('revenue_collection_campaign_hotels')
        .update({ reminders_cancelled: true })
        .eq('campaign_id', campaignId)
        .eq('hotel_id', hotelId);
      await fetchCampaign();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel reminders');
    } finally {
      setCancellingHotelId(null);
    }
  };

  const handleCancelCampaignHotel = async (hotelId: number) => {
    if (!canMutateRevenue) return;
    setCancellingCampaignHotelId(hotelId);
    try {
      const { error: updErr } = await supabase
        .from('revenue_collection_campaign_hotels')
        .update({ status: 'cancelled', reminders_cancelled: true })
        .eq('campaign_id', campaignId)
        .eq('hotel_id', hotelId);
      if (updErr) throw updErr;
      await fetchCampaign();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel hotel');
    } finally {
      setCancellingCampaignHotelId(null);
    }
  };

  const handleInternalNoteSaved = useCallback((hotelId: number, note: { id: string | null; body: string }) => {
    setHotels((prev) =>
      prev.map((h) =>
        h.hotel_id === hotelId
          ? {
              ...h,
              internal_comment_id: note.id,
              internal_comment_body: note.body,
            }
          : h,
      ),
    );
  }, []);

  const handleUndoCancelCampaignHotel = async (
    hotelId: number,
    outreachSnapshot: CampaignHotelUndoCancelSnapshot,
  ) => {
    if (!canMutateRevenue) return;
    setCancellingCampaignHotelId(hotelId);
    try {
      const hotelRow = hotels.find((h) => h.hotel_id === hotelId);
      const nextStatus = statusAfterUndoCancel({
        ...outreachSnapshot,
        total_realised: hotelRow?.materialization_form_realised ?? 0,
      });
      const payload: { status: string; reminders_cancelled?: boolean } = { status: nextStatus };
      if (shouldClearRemindersCancelledAfterUndoCancel(nextStatus)) {
        payload.reminders_cancelled = false;
      }
      const { error: updErr } = await supabase
        .from('revenue_collection_campaign_hotels')
        .update(payload)
        .eq('campaign_id', campaignId)
        .eq('hotel_id', hotelId);
      if (updErr) throw updErr;
      await fetchCampaign();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to restore hotel');
    } finally {
      setCancellingCampaignHotelId(null);
    }
  };

  return {
    campaignId,
    campaign,
    hotels,
    metrics,
    loading,
    error,
    selectedHotelId,
    setSelectedHotelId,
    handleInternalNoteSaved,
    cancellingHotelId,
    cancellingCampaignHotelId,
    launchedReminderTemplates,
    launchedReminderRows,
    setLaunchedReminderRows,
    launchedReminderModalOpen,
    setLaunchedReminderModalOpen,
    addHotelsModalOpen,
    setAddHotelsModalOpen,
    launchedReminderSaving,
    launchedReminderError,
    setLaunchedReminderError,
    launchedReminderDialogRef,
    refreshLaunchedReminderEditor,
    fetchCampaign,
    submissionsSharePct,
    materializationPct,
    emailDeliveryKpi,
    emailSendsByHotel,
    hotelTableFooterTotals,
    filteredHotels,
    includedFilterBuckets,
    setIncludedFilterBuckets,
    includedInvoicingEntityBuckets,
    setIncludedInvoicingEntityBuckets,
    hotelNameSearchQuery,
    setHotelNameSearchQuery,
    filterExtraGroupsOnly,
    setFilterExtraGroupsOnly,
    filteredKpiMetrics,
    kpiHotelCount: kpiHotels.length,
    handleSaveLaunchedReminderConfig,
    handleCancelReminders,
    handleCancelCampaignHotel,
    handleUndoCancelCampaignHotel,
    exportingExcel,
    handleExportDetailsExcel,
    launching,
    handleLaunch,
  };
}

function CampaignDetailKpiGrid({
  hotelCount,
  metrics,
  submissionsSharePct,
  materializationPct,
  emailDeliveryKpi,
}: Readonly<{
  hotelCount: number;
  metrics: Pick<
    CampaignDetailLoadedSnapshot['metrics'],
    | 'totalEstimated'
    | 'validated'
    | 'materializationRealised'
    | 'materializationEstimated'
  >;
  submissionsSharePct: number | null;
  materializationPct: number | null;
  emailDeliveryKpi: { total: number; delivered: number; pct: number | null };
}>) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5 md:gap-3 mb-6 md:items-stretch">
      <div className="flex flex-col rounded-xl border border-gray-200/90 bg-white p-3 shadow-sm ring-1 ring-gray-200/50">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Hotels</div>
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gray-50 text-gray-500 ring-1 ring-inset ring-gray-200/80">
            <Building2 className="h-3.5 w-3.5" aria-hidden />
          </span>
        </div>
        <p className="mt-2 text-xl font-semibold leading-none tracking-tight tabular-nums text-gray-900">
          {hotelCount}
        </p>
        <p className="mt-1.5 text-[11px] leading-snug text-gray-500">In this campaign</p>
      </div>

      <div className="flex flex-col rounded-xl border border-gray-200/90 bg-white p-3 shadow-sm ring-1 ring-gray-200/50">
        <div className="flex min-h-7 items-center justify-between gap-2">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Estimated revenue</div>
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gray-50 text-gray-500 ring-1 ring-inset ring-gray-200/80">
            <Banknote className="h-3.5 w-3.5" aria-hidden />
          </span>
        </div>
        <p className="mt-2 text-xl font-semibold leading-none tracking-tight text-gray-900 tabular-nums">
          {formatCurrency(metrics.totalEstimated)}
        </p>
        <p className="mt-1.5 text-[11px] leading-snug text-gray-500">Booked groups total</p>
      </div>

      <div className="flex flex-col rounded-xl border border-gray-200/90 bg-white p-3 shadow-sm ring-1 ring-gray-200/50">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Submissions</div>
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gray-50 text-gray-500 ring-1 ring-inset ring-gray-200/80">
            <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
          </span>
        </div>
        <p className="mt-2 text-xl font-semibold leading-none tracking-tight tabular-nums text-gray-900">
          {metrics.validated}
        </p>
        <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-gray-100 ring-1 ring-inset ring-gray-200/60">
          <div
            className="h-full rounded-full bg-action transition-[width] duration-500 ease-out"
            style={{ width: `${submissionsSharePct ?? 0}%` }}
          />
        </div>
        <div className="mt-1 flex items-center justify-between gap-2 text-[11px] leading-tight">
          <span className="min-w-0 truncate text-gray-600 tabular-nums">
            {hotelCount > 0 ? (
              <>
                <span className="font-medium text-gray-800">{metrics.validated}</span>
                <span className="text-gray-400">/</span>
                <span className="text-gray-600">{hotelCount}</span>
                <span className="text-gray-500"> hotels</span>
              </>
            ) : (
              <span className="text-gray-500">No hotels</span>
            )}
          </span>
          {submissionsSharePct == null ? (
            <span className="shrink-0 text-gray-400">—</span>
          ) : (
            <span className="shrink-0 font-semibold tabular-nums text-gray-700">{submissionsSharePct}%</span>
          )}
        </div>
      </div>

      <div className="flex flex-col rounded-xl border border-gray-200/90 bg-white p-3 shadow-sm ring-1 ring-gray-200/50">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Materialization</div>
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gray-50 text-gray-500 ring-1 ring-inset ring-gray-200/80">
            <TrendingUp className="h-3.5 w-3.5" aria-hidden />
          </span>
        </div>
        <p
          className="mt-2 text-xl font-semibold leading-none tracking-tight text-gray-900 tabular-nums"
          title="Realised revenue from hotels with a validated (submitted) form"
        >
          {formatCurrency(metrics.materializationRealised)}
        </p>
        <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-gray-100 ring-1 ring-inset ring-gray-200/60">
          <div
            className="h-full rounded-full bg-emerald-500 transition-[width] duration-500 ease-out"
            style={{
              width: `${Math.min(100, materializationPct ?? 0)}%`,
            }}
          />
        </div>
        <div className="mt-1 flex items-center justify-between gap-2 text-[11px] leading-tight">
          <span
            className="min-w-0 truncate text-gray-600 tabular-nums"
            title={`${formatCurrency(metrics.materializationRealised)} / ${formatCurrency(metrics.materializationEstimated)} (validated hotels, groups + extra lines)`}
          >
            {metrics.materializationEstimated > 0 ? (
              <>
                <span className="font-medium text-gray-800">
                  {formatCurrency(metrics.materializationRealised)}
                </span>
                <span className="text-gray-400">/</span>
                <span className="text-gray-600">{formatCurrency(metrics.materializationEstimated)}</span>
              </>
            ) : (
              <span className="text-gray-500">No estimated baseline (submitted)</span>
            )}
          </span>
          {materializationPct == null ? (
            <span className="shrink-0 text-gray-400">—</span>
          ) : (
            <span className="shrink-0 font-semibold tabular-nums text-gray-700">{materializationPct}%</span>
          )}
        </div>
      </div>

      <div className="flex flex-col rounded-xl border border-gray-200/90 bg-white p-3 shadow-sm ring-1 ring-gray-200/50">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Emails sent</div>
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gray-50 text-gray-500 ring-1 ring-inset ring-gray-200/80">
            <Mail className="h-3.5 w-3.5" aria-hidden />
          </span>
        </div>
        <p className="mt-2 text-xl font-semibold leading-none tracking-tight tabular-nums text-gray-900">
          {emailDeliveryKpi.delivered}
        </p>
        <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-gray-100 ring-1 ring-inset ring-gray-200/60">
          <div
            className="h-full rounded-full bg-sky-500 transition-[width] duration-500 ease-out"
            style={{ width: `${emailDeliveryKpi.pct ?? 0}%` }}
          />
        </div>
        <div className="mt-1 flex items-center justify-between gap-2 text-[11px] leading-tight">
          <span className="min-w-0 truncate text-gray-600 tabular-nums">
            {emailDeliveryKpi.total > 0 ? (
              <>
                <span className="font-medium text-gray-800">{emailDeliveryKpi.delivered}</span>
                <span className="text-gray-400">/</span>
                <span className="text-gray-600">{emailDeliveryKpi.total}</span>
                <span className="text-gray-500"> tracked sends</span>
              </>
            ) : (
              <span className="text-gray-500">No tracked sends yet</span>
            )}
          </span>
          {emailDeliveryKpi.pct == null ? (
            <span className="shrink-0 text-gray-400">—</span>
          ) : (
            <span className="shrink-0 font-semibold tabular-nums text-gray-700">{emailDeliveryKpi.pct}%</span>
          )}
        </div>
      </div>
    </div>
  );
}

function ScheduledCampaignBanner({ campaign }: Readonly<{ campaign: Campaign }>) {
  const { canMutateRevenue } = useRevenueAccess();
  if (campaign.status !== 'scheduled' || campaign.scheduled_at == null || campaign.scheduled_at === '') {
    return null;
  }
  const at = new Date(campaign.scheduled_at);
  const past = !Number.isNaN(at.getTime()) && at.getTime() <= Date.now();
  return (
    <div
      role="status"
      className="mb-4 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-950 shadow-sm flex gap-3 items-start"
    >
      <Info className="w-5 h-5 shrink-0 mt-0.5" aria-hidden />
      <div className="space-y-1 leading-relaxed min-w-0">
        <p>
          <span className="font-semibold">Scheduled launch.</span>{' '}
          Initial emails are sent automatically after{' '}
          <span className="font-medium tabular-nums">{formatDateTime(campaign.scheduled_at)}</span>{' '}
          (your local time), on the next automation run — at most about 30 minutes later.
        </p>
        {past ? (
          <p className="text-sky-900/90">
            That time has passed — messages should go out on the next automation run
            {canMutateRevenue ? ', or use Launch below to send immediately.' : '.'}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function CampaignDetailShellToolbar({
  campaignId,
  campaignStatus,
  hotelsLength,
  launching,
  handleLaunch,
  exportingExcel,
  handleExportDetailsExcel,
  onOpenRemindersEditor,
  onOpenAddHotels,
}: Readonly<{
  campaignId: string;
  campaignStatus: string;
  hotelsLength: number;
  launching: boolean;
  handleLaunch: () => Promise<void>;
  handleExportDetailsExcel: () => Promise<void>;
  exportingExcel: boolean;
  onOpenRemindersEditor: () => void;
  onOpenAddHotels: () => void;
}>) {
  const { canMutateRevenue } = useRevenueAccess();
  const showToolbar =
    campaignStatus === 'draft' ||
    campaignStatus === 'scheduled' ||
    campaignStatus === 'launched' ||
    hotelsLength > 0;

  if (!showToolbar) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center justify-end gap-2 ml-auto">
      {canMutateRevenue && (campaignStatus === 'draft' || campaignStatus === 'scheduled') && (
        <>
          <Link to={`/revenue-collection/campaigns/${campaignId}/edit`} className="btn-secondary">
            <Pencil className="h-4 w-4 shrink-0" aria-hidden />
            Edit
          </Link>
          <button
            type="button"
            disabled={launching}
            onClick={async () => {
              await handleLaunch();
            }}
            className="btn-primary h-10 py-2 disabled:pointer-events-none"
          >
            <Play className="h-4 w-4 shrink-0" aria-hidden />
            {launching ? 'Launching…' : 'Launch'}
          </button>
        </>
      )}
      {campaignStatus === 'launched' || hotelsLength > 0 ? (
        <button
          type="button"
          title="Download Excel export"
          disabled={hotelsLength === 0 || exportingExcel}
          onClick={async () => {
            await handleExportDetailsExcel();
          }}
          className="btn-secondary disabled:pointer-events-none"
        >
          <FileSpreadsheet className="h-4 w-4 shrink-0" aria-hidden />
          {exportingExcel ? 'Exporting…' : 'Export'}
        </button>
      ) : null}
      {canMutateRevenue && campaignStatus === 'launched' ? (
        <>
          <button
            type="button"
            disabled={launching}
            onClick={() => {
              void handleLaunch();
            }}
            className="btn-primary h-10 py-2 disabled:pointer-events-none"
          >
            <Play className="h-4 w-4 shrink-0" aria-hidden />
            {launching ? 'Sending…' : 'Send remaining'}
          </button>
          <button type="button" onClick={onOpenAddHotels} className="btn-secondary">
            <Plus className="h-4 w-4 shrink-0" aria-hidden />
            Add hotels
          </button>
          <button type="button" onClick={onOpenRemindersEditor} className="btn-secondary">
            <FileText className="h-4 w-4 shrink-0" aria-hidden />
            Reminders
          </button>
        </>
      ) : null}
      {!canMutateRevenue && campaignStatus === 'launched' ? (
        <button type="button" onClick={onOpenRemindersEditor} className="btn-secondary">
          <FileText className="h-4 w-4 shrink-0" aria-hidden />
          Reminders
        </button>
      ) : null}
    </div>
  );
}

type HotelTableFooterTotals = ReturnType<typeof aggregateHotelTableFooterTotals>;

type HotelAmountSortKey = 'estimated' | 'realised' | 'invoice';
type HotelAmountSortDir = 'asc' | 'desc';

function hotelAmountForSort(hotel: CampaignHotel, key: HotelAmountSortKey): number {
  if (key === 'estimated') return hotel.estimated_revenue ?? 0;
  if (key === 'realised') return hotel.realised_revenue ?? 0;
  return hotel.invoice_amount ?? 0;
}

function HotelAmountSortHeader({
  label,
  title,
  sortKey,
  sort,
  onSort,
}: Readonly<{
  label: string;
  title: string;
  sortKey: HotelAmountSortKey;
  sort: { key: HotelAmountSortKey; dir: HotelAmountSortDir } | null;
  onSort: (key: HotelAmountSortKey) => void;
}>) {
  const active = sort?.key === sortKey;
  const nextDir = active && sort.dir === 'desc' ? 'ascending' : 'descending';
  return (
    <th
      scope="col"
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className="px-2 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-gray-500 leading-tight whitespace-nowrap"
      title={title}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="inline-flex w-full items-center justify-end gap-0.5 hover:text-gray-800"
        aria-label={`Sort by ${title}, ${nextDir}`}
      >
        {label}
        {active ? (
          sort.dir === 'asc' ? (
            <ChevronUp className="h-3 w-3 shrink-0" aria-hidden />
          ) : (
            <ChevronDown className="h-3 w-3 shrink-0" aria-hidden />
          )
        ) : (
          <ChevronDown className="h-3 w-3 shrink-0 opacity-30" aria-hidden />
        )}
      </button>
    </th>
  );
}

function CampaignDetailHotelsTable({
  campaignId,
  filteredHotels,
  hotelsLength,
  setSelectedHotelId,
  onInternalNoteSaved,
  emailSendsByHotel,
  hotelTableFooterTotals,
  onOpenBounceDetail,
}: Readonly<{
  campaignId: string;
  filteredHotels: CampaignHotel[];
  hotelsLength: number;
  setSelectedHotelId: (id: number) => void;
  onInternalNoteSaved: (hotelId: number, note: { id: string | null; body: string }) => void;
  emailSendsByHotel: Map<number, CampaignEmailSendAggRow[]>;
  hotelTableFooterTotals: HotelTableFooterTotals;
  onOpenBounceDetail: (view: EmailBounceDetailView) => void;
}>) {
  const [amountSort, setAmountSort] = useState<{
    key: HotelAmountSortKey;
    dir: HotelAmountSortDir;
  } | null>(null);

  const sortedHotels = useMemo(() => {
    if (!amountSort) return filteredHotels;
    const factor = amountSort.dir === 'asc' ? 1 : -1;
    return [...filteredHotels].sort((a, b) => {
      const diff = hotelAmountForSort(a, amountSort.key) - hotelAmountForSort(b, amountSort.key);
      if (diff !== 0) return diff * factor;
      return campaignHotelDisplayTitle(a).localeCompare(campaignHotelDisplayTitle(b));
    });
  }, [amountSort, filteredHotels]);

  const toggleAmountSort = (key: HotelAmountSortKey) => {
    setAmountSort((current) => {
      if (current?.key !== key) return { key, dir: 'desc' };
      return { key, dir: current.dir === 'desc' ? 'asc' : 'desc' };
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm ring-1 ring-gray-200/80 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full table-fixed border-collapse text-xs">
          <colgroup>
            <col className="w-[24%]" />
            <col className="w-[7%]" />
            <col className="w-[12%]" />
            <col className="w-[10%]" />
            <col className="w-[10%]" />
            <col className="w-[5%]" />
            <col className="w-[5%]" />
            <col className="w-[8%]" />
            <col className="w-[19%]" />
          </colgroup>
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50/90">
              <th
                scope="col"
                className="px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500 leading-tight"
              >
                Hotel
              </th>
              <th
                scope="col"
                className="px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500 leading-tight"
                title="From Revenue collection → Hotel commissions"
              >
                <span className="block normal-case tracking-normal">Invoicing entity</span>
              </th>
              <th
                scope="col"
                className="px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500 leading-tight whitespace-nowrap"
              >
                Status
              </th>
              <HotelAmountSortHeader
                label="Est."
                title="Estimated revenue"
                sortKey="estimated"
                sort={amountSort}
                onSort={toggleAmountSort}
              />
              <HotelAmountSortHeader
                label="Real."
                title="Realised revenue"
                sortKey="realised"
                sort={amountSort}
                onSort={toggleAmountSort}
              />
              <th
                scope="col"
                className="px-2 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-gray-500 leading-tight whitespace-nowrap"
                title="Materialization: form realised ÷ form estimated (submitted hotels only)"
              >
                Mat.
              </th>
              <th
                scope="col"
                className="px-2 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-gray-500 leading-tight whitespace-nowrap"
                title="Average commission % (realised-weighted on form rows with a rate)"
              >
                Com.
              </th>
              <HotelAmountSortHeader
                label="Inv."
                title="Invoice amount"
                sortKey="invoice"
                sort={amountSort}
                onSort={toggleAmountSort}
              />
              <th
                scope="col"
                className="px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500 leading-tight"
                title="Internal admin note (not visible to the hotel)"
              >
                Note
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredHotels.length === 0 && hotelsLength > 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-sm text-gray-500">
                  No hotels match the current filters. Turn filters back on to see rows.
                </td>
              </tr>
            ) : null}
            {sortedHotels.map((h) => {
              const hotelTitle = campaignHotelDisplayTitle(h);
              const matPct = hotelMaterializationPct(h);
              const matR = h.materialization_form_realised ?? 0;
              const matE = h.materialization_form_estimated ?? 0;
              const hotelBounceDetail = latestHotelBounceDetail(
                emailSendsByHotel.get(h.hotel_id) ?? [],
                h.status,
                (kind) =>
                  EMAIL_KIND_LABELS[kind as (typeof EMAIL_KIND_ORDER)[number]] ??
                  kind.replace(/_/g, ' '),
                deliveryStateLabel,
              );
              return (
                <tr
                  key={h.hotel_id}
                  tabIndex={0}
                  aria-label={`Open hotel details for ${hotelTitle}`}
                  onClick={() => setSelectedHotelId(h.hotel_id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedHotelId(h.hotel_id);
                    }
                  }}
                  className={`cursor-pointer transition-colors hover:bg-[#148D82]/[0.06] odd:bg-white even:bg-gray-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#148D82]/35${
                    h.status === 'cancelled' ? ' opacity-60' : ''
                  }`}
                >
                  <td className="px-2 py-2 align-middle min-w-0">
                    <div className="flex min-w-0 items-center justify-between gap-1">
                      <span className="min-w-0 truncate font-medium text-gray-900" title={hotelTitle}>
                        {hotelTitle}
                      </span>
                      {h.has_extra_groups ? (
                        <span
                          className="inline-flex shrink-0 items-center justify-center rounded bg-orange-100 p-0.5 text-orange-600"
                          title="Submission includes extra groups added by the hotel or an admin"
                        >
                          <Plus className="h-3 w-3" strokeWidth={2.5} aria-hidden />
                          <span className="sr-only">Has extra groups on submission</span>
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-2 py-2 align-middle whitespace-nowrap tabular-nums text-gray-800">
                    {formatHotelInvoicingEntityCell(h.invoicing_entity)}
                  </td>
                  <td className="px-2 py-2 align-middle whitespace-nowrap">
                    <div className="inline-flex items-center gap-1">
                      <CampaignHotelStatusBadge
                        status={h.status}
                        validatedBy={h.validated_by}
                        sendError={h.status === 'pending' ? h.initial_send_error : null}
                      />
                      {hotelBounceDetail ? (
                        <button
                          type="button"
                          className="inline-flex shrink-0 items-center rounded p-0.5 text-red-600 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400/40"
                          aria-label={`View bounce details for ${hotelTitle}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenBounceDetail({
                              contextTitle: hotelTitle,
                              ...hotelBounceDetail,
                            });
                          }}
                        >
                          <MailX className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />
                        </button>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-2 py-2 align-middle text-right tabular-nums text-gray-800 whitespace-nowrap">
                    {formatCurrencyDecimals(h.estimated_revenue ?? 0)}
                  </td>
                  <td className="px-2 py-2 align-middle text-right tabular-nums text-gray-800 whitespace-nowrap">
                    {formatCurrencyDecimals(h.realised_revenue ?? 0)}
                  </td>
                  <td className="px-2 py-2 align-middle text-right tabular-nums text-gray-800 whitespace-nowrap">
                    {typeof matPct === 'number' ? (
                      <span
                        title={`${formatCurrencyDecimals(matR)} / ${formatCurrencyDecimals(matE)} (form rows)`}
                        className="font-medium"
                      >
                        {matPct}%
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-2 py-2 align-middle text-right tabular-nums text-gray-800 whitespace-nowrap">
                    {typeof h.commission_avg_percent === 'number' ? (
                      <span title="Realised-weighted average commission on form rows">
                        {h.commission_avg_percent.toFixed(1)}%
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-2 py-2 align-middle text-right tabular-nums text-gray-800 whitespace-nowrap">
                    {formatCurrencyDecimals(h.invoice_amount ?? 0)}
                  </td>
                  <td className="px-2 py-2 align-middle min-w-0" onClick={(e) => e.stopPropagation()}>
                    <CampaignHotelInternalNoteField
                      campaignId={campaignId}
                      hotelId={h.hotel_id}
                      noteId={h.internal_comment_id ?? null}
                      initialBody={h.internal_comment_body ?? ''}
                      onSaved={(note) => onInternalNoteSaved(h.hotel_id, note)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
          {filteredHotels.length > 0 ? (
            <tfoot>
              <tr className="border-t-2 border-gray-200 bg-gray-50/95 text-gray-900">
                <th
                  scope="row"
                  colSpan={3}
                  className="px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-600"
                >
                  Total
                </th>
                <td className="px-2 py-2 text-right text-xs font-semibold tabular-nums whitespace-nowrap">
                  {formatCurrencyDecimals(hotelTableFooterTotals.estimatedMv)}
                </td>
                <td className="px-2 py-2 text-right text-xs font-semibold tabular-nums whitespace-nowrap">
                  {formatCurrencyDecimals(hotelTableFooterTotals.realised)}
                </td>
                <td className="px-2 py-2 text-right text-xs font-semibold tabular-nums whitespace-nowrap">
                  {typeof hotelTableFooterTotals.matPct === 'number' ? (
                    <span
                      title={`${formatCurrencyDecimals(hotelTableFooterTotals.matFormRealised)} / ${formatCurrencyDecimals(hotelTableFooterTotals.matFormEstimated)} (form rows, visible hotels)`}
                    >
                      {hotelTableFooterTotals.matPct}%
                    </span>
                  ) : (
                    <span className="font-normal text-gray-400">—</span>
                  )}
                </td>
                <td className="px-2 py-2 text-right text-xs font-semibold tabular-nums whitespace-nowrap">
                  {typeof hotelTableFooterTotals.commissionAvgPct === 'number' ? (
                    <span title="Campaign-wide realised-weighted average commission">
                      {hotelTableFooterTotals.commissionAvgPct.toFixed(1)}%
                    </span>
                  ) : (
                    <span className="font-normal text-gray-400">—</span>
                  )}
                </td>
                <td className="px-2 py-2 text-right text-xs font-semibold tabular-nums whitespace-nowrap">
                  {formatCurrencyDecimals(hotelTableFooterTotals.invoice)}
                </td>
                <td className="px-2 py-2 text-center text-xs font-normal tabular-nums text-gray-400">—</td>
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  );
}

function CampaignLaunchedReminderDialog({
  open,
  launchedReminderDialogRef,
  launchedReminderSaving,
  onClose,
  launchedReminderError,
  launchedReminderTemplates,
  launchedReminderRows,
  setLaunchedReminderRows,
  refreshLaunchedReminderEditor,
  handleSaveLaunchedReminderConfig,
  loading,
}: Readonly<{
  open: boolean;
  launchedReminderDialogRef: MutableRefObject<HTMLDialogElement | null>;
  launchedReminderSaving: boolean;
  onClose: () => void;
  launchedReminderError: string | null;
  launchedReminderTemplates: LaunchedReminderTemplateOption[];
  launchedReminderRows: { templateId: string; days: number }[];
  setLaunchedReminderRows: Dispatch<SetStateAction<{ templateId: string; days: number }[]>>;
  refreshLaunchedReminderEditor: () => Promise<void>;
  handleSaveLaunchedReminderConfig: () => Promise<void>;
  loading: boolean;
}>) {
  const { canMutateRevenue } = useRevenueAccess();
  if (!open) {
    return null;
  }

  return (
    <dialog
      ref={launchedReminderDialogRef}
      aria-labelledby="launched-reminder-modal-title"
      className="fixed inset-0 z-50 m-0 flex w-full max-w-none translate-x-0 translate-y-0 items-center justify-center border-0 bg-transparent p-4 shadow-none open:flex [&::backdrop]:bg-black/50"
      onCancel={(e) => {
        e.preventDefault();
        if (!launchedReminderSaving) {
          launchedReminderDialogRef.current?.close();
          onClose();
        }
      }}
    >
      <button
        type="button"
        className={`fixed inset-0 z-0 bg-transparent ${launchedReminderSaving ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        aria-label="Close reminders dialog"
        disabled={launchedReminderSaving}
        onClick={() => {
          launchedReminderDialogRef.current?.close();
          onClose();
        }}
      />
      <div className="relative z-10 flex w-full max-w-6xl max-h-[90vh] flex-col overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-gray-200/80">
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-3 pr-12">
          <div className="min-w-0">
            <h2 id="launched-reminder-modal-title" className="text-base font-semibold text-gray-900">
              Reminders
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">Future automated reminders only</p>
          </div>
          <button
            type="button"
            onClick={() => {
              if (!launchedReminderSaving) {
                launchedReminderDialogRef.current?.close();
                onClose();
              }
            }}
            className="absolute right-2 top-2 rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-action/30"
            aria-label="Close"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
          <p className="text-xs leading-relaxed text-gray-600">
            Only <strong className="text-gray-800">future</strong> reminder sends use these templates and day offsets.
            Messages already delivered are unchanged. Every reminder offset is counted from the{' '}
            <strong className="text-gray-800">initial notification</strong> send date, not from the previous reminder.{' '}
            <Link to="/revenue-collection/templates" className="font-medium text-action hover:underline">
              Manage email templates
            </Link>
            .
          </p>
          {launchedReminderError ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
              {launchedReminderError}
            </div>
          ) : null}
          <div className="space-y-3">
            {launchedReminderRows.map((rc, i) => {
              const slotTemplateIds = templateOptionsForReminderSlot(
                launchedReminderTemplates,
                i,
                rc.templateId,
              );
              return (
                <div
                  key={`launched-reminder-slot-${i + 1}`}
                  className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4"
                >
                  <span className="w-28 shrink-0 text-xs font-medium text-gray-700">Reminder {i + 1}</span>
                  <SingleSelectDropdown
                    label={`Reminder ${i + 1} template`}
                    options={slotTemplateIds}
                    selected={rc.templateId}
                    disabled={!canMutateRevenue}
                    onChange={(id) => {
                      if (!canMutateRevenue) return;
                      setLaunchedReminderRows((prev) => {
                        const next = [...prev];
                        next[i] = { ...next[i], templateId: id };
                        return next;
                      });
                    }}
                    renderOption={(tid) => {
                      const t = launchedReminderTemplates.find((x) => x.id === tid);
                      return t?.name ?? String(tid);
                    }}
                    placeholder={
                      slotTemplateIds.length > 0
                        ? 'None (disable)'
                        : 'No template for this reminder type — create one'
                    }
                    className="min-w-0 flex-1"
                  />
                  <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                    <span className="text-xs text-gray-600">Days after ref. send</span>
                    <input
                      type="number"
                      min={1}
                      value={rc.days || ''}
                      readOnly={!canMutateRevenue}
                      onChange={(e) => {
                        if (!canMutateRevenue) return;
                        const v = Number(e.target.value) || 0;
                        setLaunchedReminderRows((prev) => {
                          const next = [...prev];
                          next[i] = { ...next[i], days: v };
                          return next;
                        });
                      }}
                      className={`w-20 ${REMINDER_FIELD_CLASS}`}
                      aria-label={`Reminder ${i + 1} days after reference send`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/80 px-4 py-3">
          {canMutateRevenue ? (
            <>
          <button
            type="button"
            onClick={async () => {
              await refreshLaunchedReminderEditor();
            }}
            disabled={launchedReminderSaving || loading}
            className="btn-secondary-sm disabled:opacity-50"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={async () => {
              await handleSaveLaunchedReminderConfig();
            }}
            disabled={launchedReminderSaving || loading}
            className="btn-primary-sm disabled:opacity-50"
          >
            {launchedReminderSaving ? 'Saving…' : 'Save reminder settings'}
          </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => {
                launchedReminderDialogRef.current?.close();
                onClose();
              }}
              className="btn-secondary-sm"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </dialog>
  );
}

type CampaignDetailShellProps = Omit<ReturnType<typeof useCampaignDetailPage>, 'campaign'> & {
  campaign: Campaign;
};

function CampaignDetailShell({
  campaignId,
  campaign,
  hotels,
  metrics: _metricsUnused,
  loading,
  error,
  selectedHotelId,
  setSelectedHotelId,
  handleInternalNoteSaved,
  cancellingHotelId,
  cancellingCampaignHotelId,
  launchedReminderTemplates,
  launchedReminderRows,
  setLaunchedReminderRows,
  launchedReminderModalOpen,
  setLaunchedReminderModalOpen,
  addHotelsModalOpen,
  setAddHotelsModalOpen,
  launchedReminderSaving,
  launchedReminderError,
  setLaunchedReminderError,
  launchedReminderDialogRef,
  refreshLaunchedReminderEditor,
  fetchCampaign,
  submissionsSharePct,
  materializationPct,
  emailDeliveryKpi,
  emailSendsByHotel,
  hotelTableFooterTotals,
  filteredHotels,
  includedFilterBuckets,
  setIncludedFilterBuckets,
  includedInvoicingEntityBuckets,
  setIncludedInvoicingEntityBuckets,
  hotelNameSearchQuery,
  setHotelNameSearchQuery,
  filterExtraGroupsOnly,
  setFilterExtraGroupsOnly,
  filteredKpiMetrics,
  kpiHotelCount,
  handleSaveLaunchedReminderConfig,
  handleCancelReminders,
  handleCancelCampaignHotel,
  handleUndoCancelCampaignHotel,
  exportingExcel,
  handleExportDetailsExcel,
  launching,
  handleLaunch,
}: CampaignDetailShellProps) {
  const { canMutateRevenue } = useRevenueAccess();
  const [tableBounceDetail, setTableBounceDetail] = useState<EmailBounceDetailView | null>(null);
  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="max-w-[90rem] mx-auto py-6 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <Link to="/revenue-collection" className="btn-icon-back shrink-0" aria-label="Back">
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <h1 className="page-title truncate">
              {campaign.name || `${MONTHS[campaign.period_month - 1]} ${campaign.period_year}`}
            </h1>
            <EmailTestModeBadge />
          </div>
          <CampaignDetailShellToolbar
            campaignId={campaignId!}
            campaignStatus={campaign.status}
            hotelsLength={hotels.length}
            launching={launching}
            handleLaunch={handleLaunch}
            exportingExcel={exportingExcel}
            handleExportDetailsExcel={handleExportDetailsExcel}
            onOpenRemindersEditor={() => {
              setLaunchedReminderError(null);
              void (async () => {
                await refreshLaunchedReminderEditor();
                setLaunchedReminderModalOpen(true);
              })();
            }}
            onOpenAddHotels={() => setAddHotelsModalOpen(true)}
          />
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" /> {error}
          </div>
        )}

        <ScheduledCampaignBanner campaign={campaign} />

        <CampaignDetailKpiGrid
          hotelCount={kpiHotelCount}
          metrics={{
            totalEstimated: filteredKpiMetrics.totalEstimated,
            validated: filteredKpiMetrics.validated,
            materializationRealised: filteredKpiMetrics.materializationRealised,
            materializationEstimated: filteredKpiMetrics.materializationEstimated,
          }}
          submissionsSharePct={submissionsSharePct}
          materializationPct={materializationPct}
          emailDeliveryKpi={emailDeliveryKpi}
        />

        <div className="mb-3">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <label
                htmlFor="campaign-hotel-status-filter"
                className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 whitespace-nowrap leading-tight"
              >
                Hotel status
              </label>
              <CampaignHotelStatusFilterDropdown
                includedFilterBuckets={includedFilterBuckets}
                setIncludedFilterBuckets={setIncludedFilterBuckets}
              />
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <label
                htmlFor="campaign-hotel-invoicing-entity-filter"
                className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 whitespace-nowrap leading-tight"
              >
                Invoicing entity
              </label>
              <CampaignHotelInvoicingEntityFilterDropdown
                includedBuckets={includedInvoicingEntityBuckets}
                setIncludedBuckets={setIncludedInvoicingEntityBuckets}
              />
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <label
                htmlFor="campaign-hotel-name-search"
                className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 whitespace-nowrap leading-tight"
              >
                Hotel name
              </label>
              <div className="relative w-44 shrink-0">
                <Search
                  className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                  aria-hidden
                />
                <input
                  id="campaign-hotel-name-search"
                  type="search"
                  value={hotelNameSearchQuery}
                  onChange={(e) => setHotelNameSearchQuery(e.target.value)}
                  placeholder="Search by name…"
                  className="w-full rounded-lg border border-slate-300/90 bg-white py-1.5 pl-7 pr-2 text-xs text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-action focus:outline-none focus:ring-2 focus:ring-action/35 focus:ring-offset-1"
                />
              </div>
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-slate-800">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-action accent-action focus:ring-action/30"
                checked={filterExtraGroupsOnly}
                onChange={(e) => setFilterExtraGroupsOnly(e.target.checked)}
              />
              <span>With added groups</span>
            </label>
          </div>
        </div>
        <CampaignDetailHotelsTable
          campaignId={campaignId!}
          filteredHotels={filteredHotels}
          hotelsLength={hotels.length}
          setSelectedHotelId={setSelectedHotelId}
          onInternalNoteSaved={handleInternalNoteSaved}
          emailSendsByHotel={emailSendsByHotel}
          hotelTableFooterTotals={hotelTableFooterTotals}
          onOpenBounceDetail={setTableBounceDetail}
        />

        <EmailBounceDetailModal
          view={tableBounceDetail}
          onClose={() => setTableBounceDetail(null)}
        />

        {selectedHotelId && (
          <HotelDetailModal
            campaignId={campaignId!}
            hotelId={selectedHotelId}
            periodMonth={campaign.period_month}
            periodYear={campaign.period_year}
            onClose={() => setSelectedHotelId(null)}
            onRefresh={fetchCampaign}
            onCancelReminders={() => handleCancelReminders(selectedHotelId)}
            cancellingReminders={cancellingHotelId === selectedHotelId}
            onCancelCampaignHotel={() => handleCancelCampaignHotel(selectedHotelId)}
            onUndoCancelCampaignHotel={(outreachSnapshot) =>
              handleUndoCancelCampaignHotel(selectedHotelId, outreachSnapshot)
            }
            cancellingCampaignHotel={cancellingCampaignHotelId === selectedHotelId}
          />
        )}

        <CampaignLaunchedReminderDialog
          open={launchedReminderModalOpen && campaign.status === 'launched'}
          launchedReminderDialogRef={launchedReminderDialogRef}
          launchedReminderSaving={launchedReminderSaving}
          onClose={() => setLaunchedReminderModalOpen(false)}
          launchedReminderError={launchedReminderError}
          launchedReminderTemplates={launchedReminderTemplates}
          launchedReminderRows={launchedReminderRows}
          setLaunchedReminderRows={setLaunchedReminderRows}
          refreshLaunchedReminderEditor={refreshLaunchedReminderEditor}
          handleSaveLaunchedReminderConfig={handleSaveLaunchedReminderConfig}
          loading={loading}
        />

        {canMutateRevenue ? (
        <AddHotelsToLaunchedCampaignModal
          open={addHotelsModalOpen && campaign.status === 'launched'}
          onClose={() => setAddHotelsModalOpen(false)}
          campaignId={campaignId!}
          periodMonth={campaign.period_month}
          periodYear={campaign.period_year}
          campaignHotels={hotels.map((h) => ({ hotel_id: h.hotel_id, status: h.status }))}
          onSuccess={fetchCampaign}
        />
        ) : null}
      </main>
    </div>
  );
}

export default function CampaignDetail() {
  const vm = useCampaignDetailPage();
  if (vm.loading && !vm.campaign) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <main className="max-w-[90rem] mx-auto py-6 px-6">
          <div className="text-gray-500">Loading...</div>
        </main>
      </div>
    );
  }
  if (!vm.campaign) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <main className="max-w-[90rem] mx-auto py-6 px-6">
          <div className="text-red-600">Campaign not found</div>
          <Link to="/revenue-collection" className="text-blue-600 hover:underline mt-2 inline-block">
            Back to dashboard
          </Link>
        </main>
      </div>
    );
  }
  return <CampaignDetailShell {...vm} campaign={vm.campaign} />;
}

function HotelModalRecapRow({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <p className="text-xs text-gray-700">
      {label}
      <span className="tabular-nums"> · {value}</span>
    </p>
  );
}

const EMAIL_KIND_ORDER = [
  'initial',
  'reminder_1',
  'reminder_2',
  'reminder_3',
  'confirmation',
  'invoice',
] as const;

const EMAIL_KIND_LABELS: Record<(typeof EMAIL_KIND_ORDER)[number], string> = {
  initial: 'Initial notification',
  reminder_1: 'Reminder 1',
  reminder_2: 'Reminder 2',
  reminder_3: 'Reminder 3',
  confirmation: 'Confirmation',
  invoice: 'Invoice sent',
};

function deliveryStateLabel(state: string): string {
  const m: Record<string, string> = {
    accepted: 'Queued (accepted by Postmark)',
    delivered: 'Delivered',
    delivery_failed: 'Delivery failed',
    bounce_hard: 'Hard bounce',
    bounce_soft: 'Soft bounce',
    bounce_transient: 'Temporary bounce (may retry)',
    bounce_unknown: 'Bounce',
    bounce_vapor: 'Bounce',
    bounce_bad_address: 'Invalid email address',
    bounce_unsubscribe: 'Unsubscribed',
    spam_complaint: 'Spam complaint',
    opened: 'Opened (tracking)',
    clicked: 'Link clicked',
    subscription_change: 'Subscription / suppression change',
    event: 'Event received',
    external: 'Sent outside this tool',
    unknown: 'Unknown',
  };
  return m[state] ?? state.replace(/_/g, ' ');
}

/** Short status for Initial notification row (single line with timestamp). */
function shortDeliveryStateLabel(state: string): string {
  const m: Record<string, string> = {
    accepted: 'Queued',
    delivered: 'Delivered',
    delivery_failed: 'Delivery failed',
    bounce_hard: 'Hard bounce',
    bounce_soft: 'Soft bounce',
    bounce_transient: 'Temporary bounce',
    bounce_unknown: 'Bounce',
    bounce_vapor: 'Bounce',
    bounce_bad_address: 'Invalid address',
    bounce_unsubscribe: 'Unsubscribed',
    spam_complaint: 'Spam complaint',
    opened: 'Opened',
    clicked: 'Clicked',
    subscription_change: 'Subscription change',
    event: 'Event',
    external: 'Outside tool',
    unknown: 'Unknown',
  };
  return m[state] ?? state.replace(/_/g, ' ');
}

function legacySentAtForKind(
  kind: (typeof EMAIL_KIND_ORDER)[number],
  outreach: {
    email_sent_at: string | null;
    reminder_1_at: string | null;
    reminder_2_at: string | null;
    reminder_3_at: string | null;
  },
): string | null {
  switch (kind) {
    case 'initial':
      return outreach.email_sent_at;
    case 'reminder_1':
      return outreach.reminder_1_at;
    case 'reminder_2':
      return outreach.reminder_2_at;
    case 'reminder_3':
      return outreach.reminder_3_at;
    default:
      return null;
  }
}

type ReminderKind = 'reminder_1' | 'reminder_2' | 'reminder_3';

interface CampaignReminderConfigRow {
  reminder_number: number;
  days_after_initial: number;
}

/** Calendar offset matching `send_revenue_reminders` (setDate on anchor). */
function plannedReminderSendAt(refIso: string, daysAfter: number): Date | null {
  const d = new Date(refIso);
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + daysAfter);
  return d;
}

/** Next scheduled reminder sends not yet reflected in email_sends / legacy timestamps. */
function buildPendingReminderPlans(
  outreach: {
    status: string;
    email_sent_at: string | null;
    reminder_1_at: string | null;
    reminder_2_at: string | null;
    reminder_3_at: string | null;
    validated_at: string | null;
    reminders_cancelled: boolean;
  },
  configs: CampaignReminderConfigRow[],
): Partial<Record<ReminderKind, { at: Date; overdue: boolean }>> {
  const out: Partial<Record<ReminderKind, { at: Date; overdue: boolean }>> = {};
  if (outreach.reminders_cancelled) return out;
  if (
    outreach.status === 'validated' ||
    outreach.status === 'no_production' ||
    outreach.status === 'invoice_sent' ||
    outreach.validated_at
  ) {
    return out;
  }

  const now = Date.now();
  const daysFor = (n: number) => configs.find((c) => c.reminder_number === n)?.days_after_initial;
  const initial = outreach.email_sent_at;

  const d1 = daysFor(1);
  if (d1 != null && initial && !outreach.reminder_1_at) {
    const at = plannedReminderSendAt(initial, d1);
    if (at) {
      out.reminder_1 = { at, overdue: at.getTime() <= now };
    }
  }

  // Reminders 2–3: offset from initial notification (same as send_revenue_reminders), not from the previous reminder.
  const d2 = daysFor(2);
  if (d2 != null && initial && outreach.reminder_1_at && !outreach.reminder_2_at) {
    const at = plannedReminderSendAt(initial, d2);
    if (at) {
      out.reminder_2 = { at, overdue: at.getTime() <= now };
    }
  }

  const d3 = daysFor(3);
  if (d3 != null && initial && outreach.reminder_2_at && !outreach.reminder_3_at) {
    const at = plannedReminderSendAt(initial, d3);
    if (at) {
      out.reminder_3 = { at, overdue: at.getTime() <= now };
    }
  }

  return out;
}

/** Inbox delivery or engagement — aligns with success styling in the hotels table. */
function isDeliveredLikeEmailState(deliveryState: string): boolean {
  return (
    deliveryState === 'delivered' ||
    deliveryState === 'opened' ||
    deliveryState === 'clicked'
  );
}

/** Badge colours from Postmark / webhook delivery_state */
function outreachDeliveryBadgeClass(deliveryState: string): string {
  if (isBounceLikeDeliveryState(deliveryState)) {
    return 'bg-red-100 text-red-800';
  }
  if (deliveryState === 'delivered' || deliveryState === 'opened' || deliveryState === 'clicked') {
    return 'bg-emerald-100 text-emerald-800';
  }
  if (deliveryState === 'accepted') {
    return 'bg-sky-100 text-sky-800';
  }
  if (deliveryState === 'external') {
    return 'bg-teal-100 text-teal-900';
  }
  return 'bg-slate-100 text-slate-700';
}

type OutreachEmailSendRow = {
  email_kind: string;
  delivery_state: string;
  last_event_at: string | null;
};

type OutreachHotelSnapshot = {
  status: string;
  validated_by?: string | null;
  email_sent_at: string | null;
  reminder_1_at: string | null;
  reminder_2_at: string | null;
  reminder_3_at: string | null;
};

function sortTrackedOutreachByRecency(
  tracked: OutreachEmailSendRow[],
): OutreachEmailSendRow[] {
  return [...tracked].sort((a, b) => {
    const ta = a.last_event_at ? new Date(a.last_event_at).getTime() : 0;
    const tb = b.last_event_at ? new Date(b.last_event_at).getTime() : 0;
    if (tb !== ta) return tb - ta;
    return (
      EMAIL_KIND_ORDER.indexOf(b.email_kind as (typeof EMAIL_KIND_ORDER)[number]) -
      EMAIL_KIND_ORDER.indexOf(a.email_kind as (typeof EMAIL_KIND_ORDER)[number])
    );
  });
}

function pickLatestLegacySentOutreach(
  outreach: OutreachHotelSnapshot,
): { kind: (typeof EMAIL_KIND_ORDER)[number]; at: string } | null {
  let best: { kind: (typeof EMAIL_KIND_ORDER)[number]; at: string } | null = null;
  for (const kind of EMAIL_KIND_ORDER) {
    if (kind === 'confirmation' || kind === 'invoice') continue;
    const at = legacySentAtForKind(kind, outreach);
    if (at != null && at !== '') {
      const t = new Date(at).getTime();
      if (!Number.isNaN(t) && (!best || t > new Date(best.at).getTime())) {
        best = { kind, at };
      }
    }
  }
  return best;
}

function outreachStatusFallbackDisplay(
  outreach: OutreachHotelSnapshot,
): { text: string; className: string; title: string } {
  if (outreach.status === 'pending') {
    return {
      text: 'Not sent',
      className: 'bg-slate-100 text-slate-700',
      title: 'No notification sent yet',
    };
  }
  if (outreach.status === 'cancelled') {
    return {
      text: 'Cancelled',
      className: 'bg-slate-200 text-slate-700',
      title: 'Hotel excluded from collection KPIs and reminders',
    };
  }
  if (outreach.status === 'invoice_sent') {
    return {
      text: 'Invoice sent',
      className: 'bg-teal-600 text-white',
      title: 'Invoice email was sent successfully',
    };
  }
  if (outreach.status === 'no_production') {
    return {
      text: 'No production',
      className: 'bg-violet-100 text-violet-900',
      title: 'Admin validated — 0€ total realised revenue',
    };
  }
  if (outreach.status === 'validated') {
    if (outreach.validated_by) {
      return {
        text: 'Validated',
        className: 'bg-emerald-600 text-white',
        title: 'Admin validated the submission',
      };
    }
    return {
      text: 'Submitted',
      className: 'bg-orange-100 text-orange-800',
      title: 'Hotel submitted the form',
    };
  }
  return {
    text: outreach.status.replace(/_/g, ' '),
    className: 'bg-slate-100 text-slate-700',
    title: outreach.status,
  };
}

/** Shared logic: latest tracked send, then legacy timestamps, then workflow fallbacks. */
function resolveLatestOutreachDisplay(
  emailSends: OutreachEmailSendRow[],
  outreach: OutreachHotelSnapshot,
): { text: string; className: string; title: string } {
  const tracked = emailSends.filter((s) =>
    EMAIL_KIND_ORDER.includes(s.email_kind as (typeof EMAIL_KIND_ORDER)[number]),
  );

  if (tracked.length > 0) {
    const latest = sortTrackedOutreachByRecency(tracked)[0];
    const kindLabel =
      EMAIL_KIND_LABELS[latest.email_kind as (typeof EMAIL_KIND_ORDER)[number]] ??
      latest.email_kind.replace(/_/g, ' ');
    const stateLabel = shortDeliveryStateLabel(latest.delivery_state);
    const text = `${kindLabel} · ${stateLabel}`;
    const title = `${kindLabel} — ${deliveryStateLabel(latest.delivery_state)}`;
    return {
      text,
      className: outreachDeliveryBadgeClass(latest.delivery_state),
      title,
    };
  }

  const best = pickLatestLegacySentOutreach(outreach);
  if (best) {
    const kindLabel = EMAIL_KIND_LABELS[best.kind];
    return {
      text: `${kindLabel} · Sent (before tracking)`,
      className: 'bg-slate-100 text-slate-700',
      title: `${kindLabel} — Sent before delivery tracking was enabled`,
    };
  }

  return outreachStatusFallbackDisplay(outreach);
}

/**
 * Email outreach summary in modal header: reflects the latest notification by `last_event_at`
 * (e.g. "Initial notification · Delivered", "Reminder 1 · Hard bounce").
 */
function HotelModalLatestOutreachBadge({
  emailSends,
  outreach,
}: Readonly<{
  emailSends: OutreachEmailSendRow[];
  outreach: OutreachHotelSnapshot;
}>) {
  const d = resolveLatestOutreachDisplay(emailSends, outreach);
  return (
    <span
      className={`inline-flex min-w-0 max-w-full items-center rounded px-2 py-0.5 text-xs font-medium sm:max-w-[min(100%,24rem)] ${d.className}`}
      title={d.title}
    >
      <span className="min-w-0 truncate sm:whitespace-nowrap">{d.text}</span>
    </span>
  );
}

type HotelDetailModalOutreach = {
  status: string;
  email_sent_at: string | null;
  reminder_1_at: string | null;
  reminder_2_at: string | null;
  reminder_3_at: string | null;
  validated_at: string | null;
  validated_by: string | null;
  admin_validated_at: string | null;
  /** Admin email when submission was recorded via dashboard on-behalf action; null for magic-link submit */
  submitted_on_behalf_by?: string | null;
  reminders_cancelled: boolean;
};

type CampaignHotelUndoCancelSnapshot = Pick<
  HotelDetailModalOutreach,
  | 'validated_at'
  | 'validated_by'
  | 'email_sent_at'
  | 'reminder_1_at'
  | 'reminder_2_at'
  | 'reminder_3_at'
> & {
  total_realised?: number;
};

/** Send Invoice UI: submission complete (`validated` / `invoice_sent`) and validated by admin (`validated_by`). */
function hotelOutreachEligibleForInvoiceSection(
  outreach: HotelDetailModalOutreach | null | undefined,
): boolean {
  if (!outreach?.status || isCampaignHotelNoProduction(outreach.status)) return false;
  if (!isRevenueCampaignHotelFormComplete(outreach.status)) return false;
  return outreach.validated_by != null && String(outreach.validated_by).trim() !== '';
}

function hotelModalSaveButtonTitle(
  validatedBy: string | null | undefined,
  isFormDirty: boolean,
): string | undefined {
  if (!validatedBy) {
    return undefined;
  }
  if (!isFormDirty) {
    return 'No changes to save. Edit realised amounts, commission, or extra groups, then save to update Backyou.';
  }
  return 'Save changes and push realised revenues to Backyou again.';
}

type HotelDetailModalEmailSend = {
  email_kind: string;
  delivery_state: string;
  last_event_at: string | null;
  last_event_detail: string | null;
  postmark_message_id?: string;
  bounce_tooltip: string | null;
  team_member_id?: number | null;
  /** Set for sales_manager_recap rows when team_members could be loaded */
  manager_label?: string;
};

type HotelDetailCommissionRow = {
  realised_revenue: number | null;
  commission_percent: number | null;
};

/** At least one group or extra line has a numeric realised amount (Backyou sync has rows to push). */
function hotelModalHasRealisedForBackyou(
  groups: { realised_revenue: number | null }[],
  manualLines: { realised_revenue: number | null }[],
): boolean {
  for (const g of groups) {
    if (g.realised_revenue != null && Number.isFinite(Number(g.realised_revenue))) return true;
  }
  for (const m of manualLines) {
    if (m.realised_revenue != null && Number.isFinite(Number(m.realised_revenue))) return true;
  }
  return false;
}

type HotelModalGroupBackyouRow = {
  realised_revenue: number | null;
  backyou_last_sync_at: string | null;
  backyou_last_sync_ok: boolean | null;
  backyou_synced_realised_revenue: number | null;
};

/** Same “green check” logic as `HotelDetailModalBackyouIndicator` (groups pushed to Backyou only). */
function hotelModalGroupRealisedIsGreenOnByColumn(g: HotelModalGroupBackyouRow): boolean {
  if (g.realised_revenue == null || !Number.isFinite(Number(g.realised_revenue))) return true;
  if (g.backyou_last_sync_at == null) return false;
  if (g.backyou_last_sync_ok === false) return false;
  return hotelDetailModalRealisedMatchesBackyou(g.realised_revenue, g.backyou_synced_realised_revenue);
}

/** Every group row with realised revenue matches a successful BY column sync (no drift). */
function hotelModalAllGroupRealisedRowsGreenOnByColumn(groups: HotelModalGroupBackyouRow[]): boolean {
  return groups.every((g) => hotelModalGroupRealisedIsGreenOnByColumn(g));
}

/** At least one group with realised revenue has BY in the API failure state (red icon), not merely unsynced or drift. */
function hotelModalHasAnyGroupWithFailedBySync(
  groups: Array<{ realised_revenue: number | null; backyou_last_sync_ok: boolean | null }>,
): boolean {
  for (const g of groups) {
    if (g.realised_revenue == null || !Number.isFinite(Number(g.realised_revenue))) continue;
    if (g.backyou_last_sync_ok === false) return true;
  }
  return false;
}

/** Latest `backyou_last_sync_at` among groups that carry a realised amount (for recap line). */
function hotelModalLatestBackyouSyncAtAmongRealisedGroups(
  groups: HotelModalGroupBackyouRow[],
): string | null {
  let latestMs = 0;
  let latestIso: string | null = null;
  for (const g of groups) {
    if (g.realised_revenue == null || !Number.isFinite(Number(g.realised_revenue))) continue;
    if (!g.backyou_last_sync_at) continue;
    const ms = Date.parse(g.backyou_last_sync_at);
    if (!Number.isFinite(ms) || ms < latestMs) continue;
    latestMs = ms;
    latestIso = g.backyou_last_sync_at;
  }
  return latestIso;
}

function computeHotelModalBackyouSyncRecap(
  groups: HotelModalGroupBackyouRow[],
): { atLabel: string; success: boolean } | null {
  if (!hotelModalHasRealisedForBackyou(groups, [])) return null;
  const latestIso = hotelModalLatestBackyouSyncAtAmongRealisedGroups(groups);
  if (!latestIso) return null;
  const atLabel = formatDateTime(latestIso);
  const success = hotelModalAllGroupRealisedRowsGreenOnByColumn(groups);
  return { atLabel, success };
}

/** Snapshot of editable modal fields for dirty detection (validated hotels: Save only when this differs from last load/save). */
function serializeHotelModalDirtySnapshot(
  groups: Array<{
    request_id: number;
    checkin_date: string;
    realised_revenue: number | null;
    comment: string | null;
    commission_percent: number | null;
  }>,
  manualLines: Array<{
    id: string;
    client_name: string;
    ref_client: string | null;
    checkin_date: string;
    checkout_date: string;
    estimated_revenue: number;
    commission_percent: number | null;
    realised_revenue: number | null;
    comment: string | null;
  }>,
  globalComment: string,
  deletedManualLineIds: string[],
): string {
  return JSON.stringify({
    groups: groups.map((g) => ({
      request_id: g.request_id,
      checkin_date: g.checkin_date,
      realised_revenue: g.realised_revenue,
      comment: g.comment,
      commission_percent: g.commission_percent,
    })),
    manualLines: manualLines.map((m) => ({
      id: m.id,
      client_name: m.client_name,
      ref_client: m.ref_client,
      checkin_date: m.checkin_date,
      checkout_date: m.checkout_date,
      estimated_revenue: m.estimated_revenue,
      commission_percent: m.commission_percent,
      realised_revenue: m.realised_revenue,
      comment: m.comment,
    })),
    globalComment,
    deletedManualLineIds: [...deletedManualLineIds].sort((a, b) => a.localeCompare(b)),
  });
}

function computeTotalRealisedAndInvoice(
  groups: HotelDetailCommissionRow[],
  manualLines: HotelDetailCommissionRow[],
  hotelDefaultCommission: number | null,
) {
  let totalRealisedRevenue = 0;
  let totalInvoiceAmount = 0;
  const accumulate = (row: HotelDetailCommissionRow) => {
    if (row.realised_revenue != null) totalRealisedRevenue += Number(row.realised_revenue);
    const pct = row.commission_percent ?? hotelDefaultCommission;
    if (pct != null && row.realised_revenue != null) {
      totalInvoiceAmount += Number(row.realised_revenue) * (pct / 100);
    }
  };
  for (const g of groups) accumulate(g);
  for (const m of manualLines) accumulate(m);
  return { totalRealisedRevenue, totalInvoiceAmount };
}

function resolveAdminValidationTimeDisplay(
  outreach: HotelDetailModalOutreach | null | undefined,
): string | null {
  if (!outreach?.validated_by) return null;
  if (outreach.admin_validated_at) return formatDateTime(outreach.admin_validated_at);
  if (outreach.validated_at) return formatDateTime(outreach.validated_at);
  return '—';
}

function revenueAdminInvokeErrorMessage(
  data: unknown,
  fnError: { message: string },
  fallback: string,
): string {
  const fromBody =
    data && typeof data === 'object' && data !== null && 'error' in data
      ? String((data as { error: string }).error)
      : '';
  return fromBody || fnError.message || fallback;
}

function hotelDetailModalRealisedMatchesBackyou(
  current: number | null,
  synced: number | null,
): boolean {
  if (current == null || synced == null) return false;
  return Math.abs(Number(current) - Number(synced)) < 0.015;
}

function parseAdminSaveBackyouSync(data: unknown): {
  ok: boolean;
  rows: Array<{ request_id: number; checkin_date: string; success: boolean; error?: string }>;
} | null {
  if (!data || typeof data !== 'object' || !('backyou_sync' in data)) return null;
  const raw = (data as { backyou_sync?: unknown }).backyou_sync;
  if (!raw || typeof raw !== 'object') return null;
  const bs = raw as { ok?: unknown; rows?: unknown };
  if (typeof bs.ok !== 'boolean' || !Array.isArray(bs.rows)) return null;
  return { ok: bs.ok, rows: bs.rows };
}

function backyouSyncFailureBannerText(bs: NonNullable<ReturnType<typeof parseAdminSaveBackyouSync>>): string {
  const failed = bs.rows.filter((r) => !r.success);
  const n = failed.length;
  if (n === 0) {
    return 'Backyou sync completed with warnings.';
  }
  const lines = failed.slice(0, 4).map((r) => {
    let ref: string;
    if (r.request_id == null) {
      ref = 'sync';
    } else if (r.checkin_date) {
      ref = `req ${r.request_id} · ${r.checkin_date}`;
    } else {
      ref = `req ${r.request_id}`;
    }
    const err = (r.error ?? 'unknown error').trim();
    const shortErr = err.length > 220 ? `${err.slice(0, 219)}…` : err;
    return `• ${ref}: ${shortErr}`;
  });
  const more = n > 4 ? `\n… and ${n - 4} more (see BY column tooltips / sync log).` : '';
  return `Backyou sync reported ${n} failing row(s).\n${lines.join('\n')}${more}`;
}

function HotelDetailModalBackyouIndicator({
  g,
}: Readonly<{
  g: {
    realised_revenue: number | null;
    backyou_last_sync_at: string | null;
    backyou_last_sync_ok: boolean | null;
    backyou_last_sync_error: string | null;
    backyou_synced_realised_revenue: number | null;
  };
}>) {
  if (g.realised_revenue == null) {
    return (
      <span className="text-gray-300" title="No realised amount">
        —
      </span>
    );
  }
  const lines: string[] = [];
  if (g.backyou_last_sync_at) {
    lines.push(`Last sync: ${formatDateTime(g.backyou_last_sync_at)}`);
  }
  if (g.backyou_last_sync_ok === false && g.backyou_last_sync_error) {
    lines.push(g.backyou_last_sync_error);
  }

  if (g.backyou_last_sync_at == null) {
    const title = lines.length ? lines.join('\n') : 'Not yet pushed to Backyou';
    return (
      <span title={title} className="inline-flex justify-center">
        <CloudOff className="h-3.5 w-3.5 text-gray-400" aria-hidden />
      </span>
    );
  }
  if (g.backyou_last_sync_ok === false) {
    return (
      <span title={lines.join('\n')} className="inline-flex justify-center text-red-600">
        <AlertCircle className="h-3.5 w-3.5" aria-hidden />
      </span>
    );
  }
  if (
    !hotelDetailModalRealisedMatchesBackyou(g.realised_revenue, g.backyou_synced_realised_revenue)
  ) {
    const driftNote = 'Amount changed since last successful Backyou push';
    const title = lines.length ? `${lines.join('\n')}\n${driftNote}` : driftNote;
    return (
      <span title={title} className="inline-flex justify-center text-amber-600">
        <AlertCircle className="h-3.5 w-3.5" aria-hidden />
      </span>
    );
  }
  const title = lines.length ? lines.join('\n') : 'Synchronised with Backyou';
  return (
    <span title={title} className="inline-flex justify-center text-emerald-600">
      <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
    </span>
  );
}

type HotelDetailInvoiceTemplateOption = {
  id: string;
  name: string;
  subject: string;
};

type HotelModalGroupSort = 'checkin' | 'client' | 'manager';

function sortedHotelModalGroupRows<
  T extends {
    request_id: number;
    checkin_date: string;
    company: string;
    request_manager: string | null;
  },
>(groups: T[], sortBy: HotelModalGroupSort): Array<{ g: T; index: number }> {
  const withIdx = groups.map((g, index) => ({ g, index }));
  withIdx.sort((a, b) => {
    let c = 0;
    if (sortBy === 'checkin') {
      c = a.g.checkin_date.localeCompare(b.g.checkin_date);
    } else if (sortBy === 'client') {
      c = (a.g.company || '').localeCompare(b.g.company || '', undefined, { sensitivity: 'base' });
    } else {
      c = (a.g.request_manager || '').localeCompare(b.g.request_manager || '', undefined, {
        sensitivity: 'base',
      });
    }
    if (c !== 0) return c;
    c = a.g.checkin_date.localeCompare(b.g.checkin_date);
    if (c !== 0) return c;
    return a.g.request_id - b.g.request_id;
  });
  return withIdx;
}

function uniqueInvoiceToEmails(emails: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of emails) {
    const e = raw.trim();
    if (!e) continue;
    const key = e.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(e);
  }
  return out;
}

/** Prefill invoice To: all invoice-type contacts, else Backyou hotels.invoicing_contact. */
function defaultInvoiceToField(
  contactEmails: readonly string[],
  backyouInvoicingContact: string | null,
): string {
  const fromContacts = uniqueInvoiceToEmails(contactEmails);
  if (fromContacts.length > 0) return fromContacts.join(', ');
  return backyouInvoicingContact?.trim() || '';
}

async function loadHotelDetailModalData(params: {
  campaignId: string;
  hotelId: number;
  periodMonth: number;
  periodYear: number;
}): Promise<{
  outreach: HotelDetailModalOutreach | null;
  modalHotelTitle: string | null;
  emailSends: HotelDetailModalEmailSend[];
  campaignReminderConfig: CampaignReminderConfigRow[];
  hotelDefaultCommission: number | null;
  groups: Array<{
    request_id: number;
    checkin_date: string;
    checkout_date: string | null;
    number_of_nights: number | null;
    company: string;
    client_request_reference?: string | null;
    request_manager: string | null;
    estimated_revenue: number;
    commission_percent: number | null;
    realised_revenue: number | null;
    comment: string | null;
    backyou_last_sync_at: string | null;
    backyou_last_sync_ok: boolean | null;
    backyou_last_sync_error: string | null;
    backyou_synced_realised_revenue: number | null;
  }>;
  manualLines: Array<{
    id: string;
    client_name: string;
    ref_client: string | null;
    checkin_date: string;
    checkout_date: string;
    estimated_revenue: number;
    commission_percent: number | null;
    realised_revenue: number | null;
    comment: string | null;
  }>;
  globalComment: string;
  hotelierFormUrl: string | null;
  /** Default invoice To: invoice contacts (comma-separated), else hotels.invoicing_contact. */
  hotelInvoicingContact: string | null;
  /** Primary invoice contact for {{contact.invoice.*}} subject preview. */
  hotelInvoiceContactFirstName: string;
  hotelInvoiceContactLastName: string;
  /** Invoice (hotel send) templates — user picks one before send. */
  invoiceTemplates: HotelDetailInvoiceTemplateOption[];
  /** From revenue_collection_hotel_commissions.invoicing_entity */
  hotelInvoicingEntity: 'NL' | 'SP' | null;
  /** From revenue_collection_hotel_commissions.admin_comment */
  hotelCommissionAdminComment: string;
}> {
  const { campaignId, hotelId, periodMonth, periodYear } = params;
  const { data: chRow, error: chErr } = await supabase
    .from('revenue_collection_campaign_hotels')
    .select('*')
    .eq('campaign_id', campaignId)
    .eq('hotel_id', hotelId)
    .maybeSingle();
  if (chErr) {
    console.error('HotelDetailModal campaign_hotel fetch:', chErr);
  }
  const outreach = (chRow ?? null) as HotelDetailModalOutreach | null;

  const { data: hotelMeta } = await supabase
    .from('hotels')
    .select('title, invoicing_contact')
    .eq('id', hotelId)
    .maybeSingle();
  const modalHotelTitle = hotelMeta?.title ?? null;
  const backyouInvoicingContact = hotelMeta?.invoicing_contact?.trim()
    ? hotelMeta.invoicing_contact.trim()
    : null;

  const { data: invoiceTplRows } = await supabase
    .from('revenue_collection_email_templates')
    .select('id, name, subject, updated_at')
    .eq('type', 'invoice')
    .order('name', { ascending: true })
    .order('updated_at', { ascending: false });
  const invoiceTemplates: HotelDetailInvoiceTemplateOption[] = (invoiceTplRows ?? []).map(
    (r: { id: string; name: string | null; subject: string | null }) => ({
      id: r.id,
      name: String(r.name ?? '').trim() || 'Untitled',
      subject: String(r.subject ?? '').trim(),
    }),
  );

  const { data: invoiceContactRows } = await supabase
    .from('contacts')
    .select('email, first_name, last_name')
    .eq('hotel_id', hotelId)
    .eq('contact_type', 'invoice')
    .order('last_name', { ascending: true })
    .order('first_name', { ascending: true });
  const invoiceContactList = invoiceContactRows ?? [];
  const hotelInvoiceContactFirstName = String(invoiceContactList[0]?.first_name ?? '').trim();
  const hotelInvoiceContactLastName = String(invoiceContactList[0]?.last_name ?? '').trim();
  const hotelInvoicingContact =
    defaultInvoiceToField(
      invoiceContactList.map((r) => String(r.email ?? '')),
      backyouInvoicingContact,
    ) || null;

  const [{ data: sendsData }, bounceEvents] = await Promise.all([
    supabase
      .from('revenue_collection_email_sends')
      .select(
        'email_kind, delivery_state, last_event_at, last_event_detail, postmark_message_id, team_member_id',
      )
      .eq('campaign_id', campaignId)
      .eq('hotel_id', hotelId),
    fetchRevenueCollectionBounceEvents({ campaignId, hotelId }),
  ]);

  const rawSends = enrichEmailSendsWithBounceTooltips(
    (sendsData ?? []) as Omit<HotelDetailModalEmailSend, 'bounce_tooltip'>[],
    bounceEvents,
    deliveryStateLabel,
  ) as HotelDetailModalEmailSend[];
  const recapTmIds = [
    ...new Set(
      rawSends
        .filter((s) => s.email_kind === 'sales_manager_recap' && s.team_member_id != null)
        .map((s) => Number(s.team_member_id)),
    ),
  ];
  const managerLabelById: Record<number, string> = {};
  if (recapTmIds.length > 0) {
    const { data: tmRows } = await supabase
      .from('team_members')
      .select('id, first_name, last_name, name')
      .in('id', recapTmIds);
    for (const r of tmRows ?? []) {
      const row = r as { id: number; first_name?: string | null; last_name?: string | null; name?: string | null };
      const id = Number(row.id);
      const fn = String(row.first_name ?? '').trim();
      const ln = String(row.last_name ?? '').trim();
      const n = String(row.name ?? '').trim();
      managerLabelById[id] =
        (fn || ln) ? `${fn} ${ln}`.trim() : n || `Manager #${id}`;
    }
  }
  const emailSends = rawSends.map((s) => {
    if (s.email_kind !== 'sales_manager_recap' || s.team_member_id == null) return s;
    const tid = Number(s.team_member_id);
    return {
      ...s,
      manager_label: managerLabelById[tid] ?? `Manager #${tid}`,
    };
  });

  const { data: rcfgRows } = await supabase
    .from('revenue_collection_reminder_config')
    .select('reminder_number, days_after_initial')
    .eq('campaign_id', campaignId)
    .order('reminder_number');
  const campaignReminderConfig = (rcfgRows ?? []) as CampaignReminderConfigRow[];

  const { data: hotelCommRow } = await supabase
    .from('revenue_collection_hotel_commissions')
    .select('commission_percent, invoicing_entity, admin_comment')
    .eq('hotel_id', hotelId)
    .maybeSingle();
  const hotelDefaultCommission =
    hotelCommRow?.commission_percent != null ? Number(hotelCommRow.commission_percent) : null;
  const hotelInvoicingEntity = normaliseHotelInvoicingEntity(
    (hotelCommRow as { invoicing_entity?: string | null } | null)?.invoicing_entity,
  );
  const hotelCommissionAdminComment = String(
    (hotelCommRow as { admin_comment?: string | null } | null)?.admin_comment ?? '',
  ).trim();

  const { data } = await supabase
    .from('mv_group_enriched_summary')
    .select(
      'request_id, checkin_date, checkout_date, number_of_nights, company, client_request_reference, request_manager, estimated_revenue',
    )
    .eq('hotel_id', hotelId)
    .eq('arrival_month', periodMonth)
    .eq('arrival_year', periodYear)
    .in('status', ['Booked', 'reserved'])
    .gt('estimated_revenue', 0)
    .order('checkin_date', { ascending: true })
    .order('request_id', { ascending: true });

  const { data: respData } = await supabase
    .from('revenue_collection_responses')
    .select(
      'request_id, checkin_date, realised_revenue, comment, commission_percent, backyou_last_sync_at, backyou_last_sync_ok, backyou_last_sync_error, backyou_synced_realised_revenue',
    )
    .eq('campaign_id', campaignId)
    .eq('hotel_id', hotelId);

  const { data: gcData } = await supabase
    .from('revenue_collection_global_comments')
    .select('global_comment')
    .eq('campaign_id', campaignId)
    .eq('hotel_id', hotelId)
    .maybeSingle();

  const respMap = new Map(
    (respData || []).map(
      (r: {
        request_id: number;
        checkin_date: string;
        realised_revenue?: unknown;
        comment?: string | null;
        commission_percent?: number | null;
        backyou_last_sync_at?: string | null;
        backyou_last_sync_ok?: boolean | null;
        backyou_last_sync_error?: string | null;
        backyou_synced_realised_revenue?: unknown;
      }) => [revenueResponseRowKey(r.request_id, r.checkin_date), r],
    ),
  );

  const groups = (data || []).map(
    (g: {
      request_id: number;
      checkin_date: string;
      checkout_date: string | null;
      number_of_nights: number | null;
      company: string;
      client_request_reference?: string | null;
      request_manager?: string | null;
      estimated_revenue: number;
    }) => {
      const key = revenueResponseRowKey(g.request_id, g.checkin_date);
      const resp = respMap.get(key);
      const storedComm =
        resp?.commission_percent != null ? Number(resp.commission_percent) : null;
      const commissionForEdit = storedComm !== null ? storedComm : hotelDefaultCommission;
      return {
        request_id: g.request_id,
        checkin_date: g.checkin_date,
        checkout_date: g.checkout_date ?? null,
        number_of_nights: g.number_of_nights == null ? null : Number(g.number_of_nights),
        company: g.company || '-',
        client_request_reference: g.client_request_reference?.trim() || null,
        request_manager: g.request_manager?.trim() || null,
        estimated_revenue: Number(g.estimated_revenue) || 0,
        commission_percent: commissionForEdit,
        realised_revenue:
          resp?.realised_revenue == null ? null : Number(resp.realised_revenue),
        comment: resp?.comment || null,
        backyou_last_sync_at: resp?.backyou_last_sync_at ?? null,
        backyou_last_sync_ok:
          resp?.backyou_last_sync_ok !== undefined && resp?.backyou_last_sync_ok !== null
            ? Boolean(resp.backyou_last_sync_ok)
            : null,
        backyou_last_sync_error: resp?.backyou_last_sync_error ?? null,
        backyou_synced_realised_revenue:
          resp?.backyou_synced_realised_revenue == null ||
          resp?.backyou_synced_realised_revenue === ''
            ? null
            : Number(resp.backyou_synced_realised_revenue),
      };
    },
  );

  const { data: manualDb } = await supabase
    .from('revenue_collection_manual_lines')
    .select(
      'id, client_name, ref_client, checkin_date, checkout_date, nights, estimated_revenue, realised_revenue, comment, commission_percent',
    )
    .eq('campaign_id', campaignId)
    .eq('hotel_id', hotelId)
    .order('checkin_date', { ascending: true });

  const manualLines = (manualDb || []).map(
    (m: {
      id: string;
      client_name: string;
      ref_client: string | null;
      checkin_date: string;
      checkout_date: string;
      nights: number | null;
      estimated_revenue: unknown;
      realised_revenue: unknown;
      comment: string | null;
      commission_percent: number | null;
    }) => {
      const storedComm =
        m.commission_percent != null ? Number(m.commission_percent) : null;
      const commissionForEdit = storedComm !== null ? storedComm : hotelDefaultCommission;
      return {
        id: m.id,
        client_name: m.client_name ?? '',
        ref_client: m.ref_client,
        checkin_date: normalizeCheckinDate(m.checkin_date),
        checkout_date: normalizeCheckinDate(m.checkout_date),
        estimated_revenue: Number(m.estimated_revenue) || 0,
        realised_revenue:
          m.realised_revenue != null && m.realised_revenue !== ''
            ? Number(m.realised_revenue)
            : null,
        comment: m.comment,
        commission_percent: commissionForEdit,
      };
    },
  );

  const globalComment = gcData?.global_comment || '';

  let hotelierFormUrl: string | null = null;
  const { data: activeToken } = await supabase
    .from('revenue_collection_tokens')
    .select('token')
    .eq('campaign_id', campaignId)
    .eq('hotel_id', hotelId)
    .gte('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (activeToken?.token) {
    const origin = globalThis.location?.origin?.replace(/\/+$/, '') ?? '';
    if (origin) {
      hotelierFormUrl = `${origin}/hotel-report/form/${activeToken.token}`;
    }
  }

  return {
    outreach,
    modalHotelTitle,
    emailSends,
    campaignReminderConfig,
    hotelDefaultCommission,
    groups,
    manualLines,
    globalComment,
    hotelierFormUrl,
    hotelInvoicingContact,
    hotelInvoiceContactFirstName,
    hotelInvoiceContactLastName,
    invoiceTemplates,
    hotelInvoicingEntity,
    hotelCommissionAdminComment,
  };
}

/** Client-side subject preview (matches edge substitution for common placeholders). */
function applyInvoiceTemplatePreview(
  template: string | null | undefined,
  hotelTitle: string,
  campaignPeriodTag: string,
  invoiceContactFirstName = '',
  invoiceContactLastName = '',
): string {
  if (!template) return '—';
  const fn = invoiceContactFirstName.trim();
  const ln = invoiceContactLastName.trim();
  return template
    .replace(/\{\{hotel_name\}\}/g, hotelTitle)
    .replace(/\{\{campaign_period\}\}/g, campaignPeriodTag)
    .replace(/\{\{link_url\}\}/g, '')
    .replace(/\{\{groups_summary\}\}/g, '—')
    .replace(/\{\{submission_summary\}\}/g, '—')
    .replace(/\{\{contact\.invoice\.first_name\}\}/g, fn)
    .replace(/\{\{contact\.invoice\.last_name\}\}/g, ln);
}

function hotelModalInvoiceTrackingSummary(send: HotelDetailModalEmailSend | undefined): string {
  if (!send) return 'Pending';
  const state = shortDeliveryStateLabel(send.delivery_state);
  const at = send.last_event_at ? formatDateTime(send.last_event_at) : null;
  return at ? `${state} · ${at}` : state;
}

function HotelModalBounceStatusButton({
  statusLine,
  contextTitle,
  bounceContent,
  onOpenBounceDetail,
  className = 'text-xs tabular-nums text-red-700/90 underline decoration-red-300/70 decoration-dotted underline-offset-2 hover:text-red-800',
}: Readonly<{
  statusLine: string;
  contextTitle: string;
  bounceContent: EmailBounceDetailContent;
  onOpenBounceDetail: (view: EmailBounceDetailView) => void;
  className?: string;
}>) {
  return (
    <button
      type="button"
      className={`text-left ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        onOpenBounceDetail({ contextTitle, ...bounceContent });
      }}
    >
      {statusLine}
    </button>
  );
}

const INVOICE_ATTACH_MAX_BYTES = 7 * 1024 * 1024;

function fileToPdfBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const dataUrl = r.result;
      if (typeof dataUrl !== 'string') {
        reject(new Error('Could not read file'));
        return;
      }
      const comma = dataUrl.indexOf(',');
      resolve(comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl);
    };
    r.onerror = () => reject(new Error('Could not read file'));
    r.readAsDataURL(file);
  });
}

function parseInvoiceToEmailTokens(raw: string): string[] {
  return raw
    .split(/[,;\n]+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
}

function HotelModalInvoiceSendFormFields({
  campaignId,
  hotelId,
  invoicingContact,
  hotelTitle,
  campaignPeriodTag,
  invoiceContactFirstName,
  invoiceContactLastName,
  invoiceTemplates,
  invoiceSend,
  onSent,
}: Readonly<{
  campaignId: string;
  hotelId: number;
  invoicingContact: string | null;
  hotelTitle: string;
  campaignPeriodTag: string;
  invoiceContactFirstName: string;
  invoiceContactLastName: string;
  invoiceTemplates: HotelDetailInvoiceTemplateOption[];
  invoiceSend: HotelDetailModalEmailSend | undefined;
  onSent: () => void;
}>) {
  const [toEmails, setToEmails] = useState(() => invoicingContact?.trim() || '');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const pdfFileInputRef = useRef<HTMLInputElement>(null);
  const [sending, setSending] = useState(false);
  const [markingExternal, setMarkingExternal] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [invoiceContactsByNormEmail, setInvoiceContactsByNormEmail] = useState<
    Record<string, { first_name: string; last_name: string }>
  >({});

  const markedExternal = invoiceSend?.delivery_state === 'external';
  const sentViaTool = Boolean(invoiceSend && invoiceSend.delivery_state !== 'external');
  const externalCheckboxDisabled = markingExternal || sending || markedExternal || sentViaTool;

  useEffect(() => {
    setToEmails(invoicingContact?.trim() || '');
  }, [invoicingContact]);

  useEffect(() => {
    if (invoiceTemplates.length === 0) {
      setSelectedTemplateId('');
      return;
    }
    setSelectedTemplateId((prev) =>
      prev && invoiceTemplates.some((t) => t.id === prev) ? prev : invoiceTemplates[0].id,
    );
  }, [invoiceTemplates]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase
        .from('contacts')
        .select('email, first_name, last_name')
        .eq('hotel_id', hotelId)
        .eq('contact_type', 'invoice');
      if (cancelled) return;
      if (error) {
        console.error('HotelModal invoice contacts:', error);
        setInvoiceContactsByNormEmail({});
        return;
      }
      const record: Record<string, { first_name: string; last_name: string }> = {};
      for (const row of data ?? []) {
        const e = String(row.email ?? '').trim();
        if (!e) continue;
        record[e.toLowerCase()] = {
          first_name: String(row.first_name ?? '').trim(),
          last_name: String(row.last_name ?? '').trim(),
        };
      }
      setInvoiceContactsByNormEmail(record);
    })();
    return () => {
      cancelled = true;
    };
  }, [hotelId]);

  const selectedSubject =
    invoiceTemplates.find((t) => t.id === selectedTemplateId)?.subject ?? null;
  const previewSubject = applyInvoiceTemplatePreview(
    selectedSubject,
    hotelTitle,
    campaignPeriodTag,
    invoiceContactFirstName,
    invoiceContactLastName,
  );

  const toFieldMatchedInvoiceNames = useMemo(() => {
    const tokens = parseInvoiceToEmailTokens(toEmails);
    const seen = new Set<string>();
    const out: { address: string; displayName: string }[] = [];
    for (const addr of tokens) {
      const key = addr.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      const c = invoiceContactsByNormEmail[key];
      if (!c) continue;
      const displayName = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
      if (!displayName) continue;
      out.push({ address: addr, displayName });
    }
    return out;
  }, [toEmails, invoiceContactsByNormEmail]);

  const handleSend = async () => {
    setLocalError(null);
    const trimmedTo = toEmails.trim();
    if (!trimmedTo) {
      setLocalError('Add at least one recipient email.');
      return;
    }
    if (invoiceTemplates.length === 0) {
      setLocalError('Add an Invoice (hotel send) template under Email templates.');
      return;
    }
    if (!selectedTemplateId) {
      setLocalError('Select an invoice template.');
      return;
    }
    if (!pdfFile) {
      setLocalError('Choose a PDF attachment.');
      return;
    }
    if (pdfFile.type !== 'application/pdf' && !pdfFile.name.toLowerCase().endsWith('.pdf')) {
      setLocalError('Attachment must be a PDF.');
      return;
    }
    if (pdfFile.size > INVOICE_ATTACH_MAX_BYTES) {
      setLocalError(`PDF must be under ${Math.round(INVOICE_ATTACH_MAX_BYTES / (1024 * 1024))} MB.`);
      return;
    }
    setSending(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');

      const attachment_base64 = await fileToPdfBase64(pdfFile);
      const { data, error: fnError } = await supabase.functions.invoke('send_revenue_collection_invoice', {
        body: {
          campaign_id: campaignId,
          hotel_id: hotelId,
          to_emails: trimmedTo,
          attachment_base64,
          attachment_name: pdfFile.name,
          template_id: selectedTemplateId,
        },
      });

      if (fnError) {
        const msg =
          data && typeof data === 'object' && data !== null && 'error' in data
            ? String((data as { error: string }).error)
            : fnError.message || 'Send failed';
        throw new Error(msg);
      }
      setPdfFile(null);
      const inp = pdfFileInputRef.current;
      if (inp) inp.value = '';
      onSent();
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const handleMarkSentExternally = async () => {
    if (externalCheckboxDisabled) return;
    setLocalError(null);
    setMarkingExternal(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');

      const { data, error: fnError } = await supabase.functions.invoke('send_revenue_collection_invoice', {
        body: {
          campaign_id: campaignId,
          hotel_id: hotelId,
          mark_sent_externally: true,
        },
      });

      if (fnError) {
        const msg =
          data && typeof data === 'object' && data !== null && 'error' in data
            ? String((data as { error: string }).error)
            : fnError.message || 'Could not mark invoice as sent';
        throw new Error(msg);
      }
      onSent();
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Could not mark invoice as sent');
    } finally {
      setMarkingExternal(false);
    }
  };

  const templateOptionIds = invoiceTemplates.map((t) => t.id);
  const sendDisabled =
    sending ||
    markingExternal ||
    invoiceTemplates.length === 0 ||
    !selectedTemplateId ||
    !pdfFile;

  const externalCheckboxTitle = sentViaTool
    ? 'Invoice was already sent via this tool'
    : markedExternal
      ? invoiceSend?.last_event_detail || 'Marked as sent outside this tool'
      : 'Mark status as Invoice sent without sending email from this tool';

  return (
    <div className="space-y-3 text-xs">
      <div>
        <label
          htmlFor={`invoice-template-dd-${hotelId}`}
          className="mb-1 block text-xs font-normal text-gray-600"
        >
          Select a template
        </label>
        <SingleSelectDropdown
          label="Invoice template"
          triggerId={`invoice-template-dd-${hotelId}`}
          triggerClassName="flex w-full min-w-0 items-center justify-between rounded border border-gray-300 bg-white px-2 py-1.5 text-left text-xs font-normal text-gray-900 shadow-sm focus:border-action focus:outline-none focus:ring-1 focus:ring-action/30 disabled:cursor-not-allowed disabled:opacity-50"
          options={templateOptionIds}
          selected={selectedTemplateId}
          onChange={(id) => setSelectedTemplateId(typeof id === 'string' ? id : '')}
          renderOption={(tid) => invoiceTemplates.find((x) => x.id === tid)?.name ?? tid}
          placeholder={
            templateOptionIds.length > 0
              ? 'Select template'
              : 'No Invoice (hotel send) templates'
          }
          allowClear={false}
          disabled={invoiceTemplates.length === 0}
          listBoxClassName="py-1 text-xs text-gray-700"
          className="w-full min-w-0"
        />
      </div>
      <p className="text-xs text-gray-600">
        <span className="font-normal text-gray-700">Preview subject:</span>{' '}
        <span className="text-gray-800">{previewSubject}</span>
      </p>
      <div>
        <label className="mb-1 block text-xs font-normal text-gray-600" htmlFor={`invoice-to-${hotelId}`}>
          To (comma-separated)
        </label>
        <input
          id={`invoice-to-${hotelId}`}
          type="text"
          value={toEmails}
          onChange={(e) => setToEmails(e.target.value)}
          className="w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-900 shadow-sm focus:border-action focus:outline-none focus:ring-1 focus:ring-action/30"
          placeholder="billing@hotel.com"
          autoComplete="off"
        />
        {toFieldMatchedInvoiceNames.length > 0 ? (
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1.5">
            <span className="shrink-0 text-xs font-normal text-gray-700">Invoice contact</span>
            <div className="flex flex-wrap gap-1.5" aria-live="polite">
              {toFieldMatchedInvoiceNames.map(({ address, displayName }) => (
                <span
                  key={address.toLowerCase()}
                  className="inline-flex max-w-full items-center gap-1 rounded-full border border-[#17776F]/25 bg-[#17776F]/10 px-2.5 py-1 text-xs text-gray-800"
                >
                  {toFieldMatchedInvoiceNames.length > 1 ? (
                    <>
                      <span className="max-w-[min(12rem,45vw)] truncate text-gray-600" title={address}>
                        {address}
                      </span>
                      <span className="shrink-0 text-gray-400" aria-hidden>
                        ·
                      </span>
                    </>
                  ) : null}
                  <span className="min-w-0 truncate font-medium text-gray-900">{displayName}</span>
                </span>
              ))}
            </div>
          </div>
        ) : null}
      </div>
      <div>
        <label
          htmlFor={`invoice-pdf-${hotelId}`}
          className="mb-1 block text-xs font-normal text-gray-600"
        >
          PDF attachment
        </label>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <input
            ref={pdfFileInputRef}
            id={`invoice-pdf-${hotelId}`}
            type="file"
            accept="application/pdf,.pdf"
            onChange={(e) => setPdfFile(e.target.files?.[0] ?? null)}
            className="sr-only"
            tabIndex={-1}
          />
          {!pdfFile ? (
            <button
              type="button"
              onClick={() => pdfFileInputRef.current?.click()}
              className="btn-secondary-sm shrink-0"
            >
              Choose file
            </button>
          ) : (
            <div className="flex min-w-0 max-w-full items-center gap-1.5 rounded border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-800 shadow-sm">
              <span className="min-w-0 flex-1 truncate font-medium" title={pdfFile.name}>
                {pdfFile.name}
              </span>
              <button
                type="button"
                onClick={() => {
                  setPdfFile(null);
                  const el = pdfFileInputRef.current;
                  if (el) el.value = '';
                }}
                className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded border border-transparent text-gray-400 hover:border-gray-200 hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-action/30"
                aria-label="Remove PDF attachment"
              >
                <X className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
              </button>
            </div>
          )}
        </div>
      </div>
      {localError ? <p className="text-xs text-red-600">{localError}</p> : null}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 pt-1">
        <label
          className={`inline-flex min-w-0 items-center gap-2 text-xs text-gray-700 ${
            externalCheckboxDisabled ? 'cursor-default opacity-80' : 'cursor-pointer'
          }`}
          title={externalCheckboxTitle}
        >
          <input
            type="checkbox"
            className="h-3.5 w-3.5 rounded border-gray-300 text-teal-700 focus:ring-action/30 disabled:cursor-not-allowed"
            checked={markedExternal}
            disabled={externalCheckboxDisabled}
            onChange={(e) => {
              if (e.target.checked) void handleMarkSentExternally();
            }}
          />
          <span className="min-w-0">
            {markingExternal ? 'Marking…' : 'Sent outside this tool'}
          </span>
        </label>
        <button
          type="button"
          onClick={() => void handleSend()}
          disabled={sendDisabled}
          className="btn-teal-subtle"
        >
          <Mail className="h-3.5 w-3.5" aria-hidden />
          {sending ? 'Sending…' : 'Send invoice'}
        </button>
      </div>
    </div>
  );
}

function HotelModalOutreachKindStatus({
  kind,
  send,
  outreach,
  pendingPlan,
  contextTitle,
  onOpenBounceDetail,
}: {
  kind: (typeof EMAIL_KIND_ORDER)[number];
  send: HotelDetailModalEmailSend | undefined;
  outreach: OutreachHotelSnapshot;
  pendingPlan: { at: Date; overdue: boolean } | undefined;
  contextTitle: string;
  onOpenBounceDetail: (view: EmailBounceDetailView) => void;
}) {
  const legacyAt = legacySentAtForKind(kind, outreach);
  if (send) {
    const statusLine = `${shortDeliveryStateLabel(send.delivery_state)} · ${formatDateTime(send.last_event_at)}`;
    const bounceContent = bounceDetailForEmailSend(
      send,
      EMAIL_KIND_LABELS[kind],
      deliveryStateLabel,
    );
    if (bounceContent) {
      return (
        <HotelModalBounceStatusButton
          statusLine={statusLine}
          contextTitle={contextTitle}
          bounceContent={bounceContent}
          onOpenBounceDetail={onOpenBounceDetail}
        />
      );
    }
    return <div className="text-xs tabular-nums text-gray-500">{statusLine}</div>;
  }
  if (legacyAt) {
    return (
      <span className="text-xs text-gray-600">
        Sent (before tracking) · {formatDateTime(legacyAt)}
      </span>
    );
  }
  if (pendingPlan) {
    return (
      <div className="text-xs text-gray-500 tabular-nums">
        <div>Planned send · {formatDateTime(pendingPlan.at.toISOString())}</div>
        {pendingPlan.overdue ? (
          <div className="mt-0.5">Due — typically sent on the next reminder run</div>
        ) : null}
      </div>
    );
  }
  return <span className="text-xs text-gray-400">—</span>;
}

function HotelModalSalesManagerRecapList({
  emailSends,
}: Readonly<{ emailSends: HotelDetailModalEmailSend[] }>) {
  const recapSends = [...emailSends]
    .filter((s) => s.email_kind === 'sales_manager_recap')
    .sort((a, b) => {
      const ta = a.last_event_at ? new Date(a.last_event_at).getTime() : 0;
      const tb = b.last_event_at ? new Date(b.last_event_at).getTime() : 0;
      return tb - ta;
    });
  if (recapSends.length === 0) return null;
  return (
    <div className="mt-2 border-t border-gray-200 pt-2">
      <div className="text-[11px] font-semibold text-gray-700 mb-1">Sales manager recaps (internal)</div>
      <ul className="space-y-1">
        {recapSends.map((s, i) => (
          <li
            key={`${s.team_member_id ?? 'x'}-${i}-${s.last_event_at ?? ''}`}
            className="flex justify-between gap-2 text-xs text-gray-600"
          >
            <span className="min-w-0 truncate">
              {s.manager_label ??
                (s.team_member_id != null ? `Manager #${s.team_member_id}` : 'Manager')}
            </span>
            <span className="shrink-0 tabular-nums text-gray-500 text-right">
              {shortDeliveryStateLabel(s.delivery_state)}
              {s.last_event_at ? ` · ${formatDateTime(s.last_event_at)}` : ''}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function HotelModalCampaignCancelActions({
  outreach,
  onCancelCampaignHotel,
  onUndoCancelCampaignHotel,
  cancellingCampaignHotel,
}: Readonly<{
  outreach: HotelDetailModalOutreach;
  onCancelCampaignHotel: () => void | Promise<void>;
  onUndoCancelCampaignHotel: (outreachSnapshot: CampaignHotelUndoCancelSnapshot) => void | Promise<void>;
  cancellingCampaignHotel: boolean;
}>) {
  const canCancel = canAdminCancelCampaignHotel({
    status: outreach.status,
    validated_by: outreach.validated_by,
  });
  const canUndo = canAdminUndoCancelCampaignHotel(outreach.status);

  if (!canCancel && !canUndo) return null;

  if (canUndo) {
    return (
      <button
        type="button"
        disabled={cancellingCampaignHotel}
        onClick={() => {
          if (
            !globalThis.confirm(
              'Restore this hotel to the campaign? It will count toward KPIs again.',
            )
          ) {
            return;
          }
          void onUndoCancelCampaignHotel({
            validated_at: outreach.validated_at,
            validated_by: outreach.validated_by,
            email_sent_at: outreach.email_sent_at,
            reminder_1_at: outreach.reminder_1_at,
            reminder_2_at: outreach.reminder_2_at,
            reminder_3_at: outreach.reminder_3_at,
          });
        }}
        className="btn-secondary-xs-compact disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <RotateCcw className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {cancellingCampaignHotel ? 'Restoring…' : 'Restore hotel'}
      </button>
    );
  }

  return (
    <button
      type="button"
      disabled={cancellingCampaignHotel}
      onClick={() => {
        if (
          !globalThis.confirm(
            'Cancel this hotel for the campaign? It will stay in the list but be excluded from KPIs and reminders.',
          )
        ) {
          return;
        }
        void onCancelCampaignHotel();
      }}
      className="btn-secondary-xs-compact disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <Ban className="h-3.5 w-3.5 shrink-0" aria-hidden />
      {cancellingCampaignHotel ? 'Cancelling…' : 'Cancel hotel'}
    </button>
  );
}

function HotelModalCancelledBanner({
  outreach,
}: Readonly<{
  outreach: HotelDetailModalOutreach;
}>) {
  if (outreach.status !== 'cancelled') return null;
  return (
    <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 font-medium">
      This hotel is cancelled — excluded from collection KPIs and reminders.
    </div>
  );
}

function HotelModalEmailOutreachSection({
  outreach,
  emailSends,
  pendingReminderPlans,
  onCancelReminders,
  cancellingReminders,
  onRefreshAfterCancel,
  contextTitle,
  onOpenBounceDetail,
}: {
  outreach: HotelDetailModalOutreach;
  emailSends: HotelDetailModalEmailSend[];
  pendingReminderPlans: Partial<Record<ReminderKind, { at: Date; overdue: boolean }>>;
  onCancelReminders: () => void | Promise<void>;
  cancellingReminders: boolean;
  onRefreshAfterCancel: () => Promise<void>;
  contextTitle: string;
  onOpenBounceDetail: (view: EmailBounceDetailView) => void;
}) {
  const { canMutateRevenue } = useRevenueAccess();
  const outreachKinds = EMAIL_KIND_ORDER.filter((kind) => kind !== 'invoice');
  return (
    <details className="group mb-3 rounded-lg border border-gray-200 bg-gray-50/80 text-xs">
      <summary className="flex w-full cursor-pointer list-none items-center gap-2 px-3 py-2.5 marker:content-none [&::-webkit-details-marker]:hidden">
        <ChevronRight
          className="h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 group-open:rotate-90 motion-reduce:transition-none"
          aria-hidden
        />
        <span className="shrink-0 font-semibold text-gray-900">Email outreach</span>
        <span className="min-w-0 max-w-[min(100%,18rem)] flex-1 text-right sm:max-w-none">
          <HotelModalLatestOutreachBadge emailSends={emailSends} outreach={outreach} />
        </span>
      </summary>
      <div className="flex w-full flex-col gap-2 border-t border-gray-200 px-3 pb-3 pt-3">
        {outreach.reminders_cancelled ? (
          <div className="text-amber-800 text-xs font-medium bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-1">
            Reminders cancelled for this hotel — no further automated reminders will be sent.
          </div>
        ) : null}
        <div className="flex flex-col gap-0 divide-y divide-gray-100">
          {outreachKinds.map((kind) => {
            const send = emailSends.find((s) => s.email_kind === kind);
            const label = EMAIL_KIND_LABELS[kind];
            const pendingPlan =
              kind === 'reminder_1' || kind === 'reminder_2' || kind === 'reminder_3'
                ? pendingReminderPlans[kind]
                : undefined;
            return (
              <div
                key={kind}
                className="flex items-start justify-between gap-4 py-2.5 first:pt-0"
              >
                <span className="text-xs font-normal text-gray-600 shrink-0 pt-0.5">{label}</span>
                <div className="text-right min-w-0 flex-1">
                  <HotelModalOutreachKindStatus
                    kind={kind}
                    send={send}
                    outreach={outreach}
                    pendingPlan={pendingPlan}
                    contextTitle={contextTitle}
                    onOpenBounceDetail={onOpenBounceDetail}
                  />
                </div>
              </div>
            );
          })}
        </div>
        <HotelModalSalesManagerRecapList emailSends={emailSends} />
        {!outreach.reminders_cancelled &&
        canMutateRevenue &&
        ['email_sent', 'reminder_1', 'reminder_2', 'reminder_3'].includes(
          outreach.status,
        ) ? (
          <div className="mt-3 flex justify-end border-t border-gray-200 pt-3">
            <button
              type="button"
              onClick={() => {
                void (async () => {
                  await onCancelReminders();
                  await onRefreshAfterCancel();
                })();
              }}
              disabled={cancellingReminders}
              className="inline-flex items-center gap-1 text-xs text-amber-600 hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
            >
              <Ban className="h-3 w-3 shrink-0" aria-hidden />
              {cancellingReminders ? 'Cancelling…' : 'Cancel reminders'}
            </button>
          </div>
        ) : null}
      </div>
    </details>
  );
}

/** Matches {@link HotelModalLatestOutreachBadge}: rounded rectangle, px-2 py-0.5, text-xs, slate pill. */
const HOTEL_MODAL_OUTREACH_MATCHING_TAG_BASE =
  'min-w-0 max-w-full gap-1 rounded px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-700';

/** Matches `revenue_collection_hotel_commissions` admin_comment length CHECK. */
const HOTEL_MODAL_COMMISSION_COMMENT_MAX_LEN = 500;

type HotelModalInvoicingEntityDraft = '' | 'NL' | 'SP';

const HOTEL_MODAL_INVOICING_ENTITY_OPTIONS: {
  value: HotelModalInvoicingEntityDraft;
  label: string;
}[] = [
  { value: '', label: 'Not set' },
  { value: 'NL', label: 'NL' },
  { value: 'SP', label: 'SP' },
];

function HotelModalCommissionSummaryTags({
  hotelDefaultCommission,
  hotelCommissionAdminComment,
  hotelInvoicingEntity,
}: Readonly<{
  hotelDefaultCommission: number | null;
  hotelCommissionAdminComment: string;
  hotelInvoicingEntity: 'NL' | 'SP' | null;
}>) {
  const comment = hotelCommissionAdminComment.trim();
  const defaultPct =
    hotelDefaultCommission == null ? '—' : `${hotelDefaultCommission}%`;
  const entityLabel = formatHotelInvoicingEntityCell(hotelInvoicingEntity);
  return (
    <>
      <span
        className={`inline-flex shrink-0 items-center whitespace-nowrap ${HOTEL_MODAL_OUTREACH_MATCHING_TAG_BASE}`}
      >
        <span className="text-slate-600">default</span>
        <span className="text-slate-500" aria-hidden>
          :
        </span>
        <span className="tabular-nums text-slate-800">{defaultPct}</span>
      </span>
      <span
        className={`inline-flex items-start ${HOTEL_MODAL_OUTREACH_MATCHING_TAG_BASE} sm:max-w-[min(100%,24rem)]`}
      >
        <span className="shrink-0 pt-0.5 text-slate-600">comment</span>
        <span className="shrink-0 pt-0.5 text-slate-500" aria-hidden>
          :
        </span>
        <span
          className={`min-w-0 flex-1 whitespace-pre-wrap break-words pt-0.5 ${
            comment ? 'text-slate-800' : 'text-slate-500'
          }`}
        >
          {comment || '—'}
        </span>
      </span>
      <span
        className={`inline-flex shrink-0 items-center whitespace-nowrap ${HOTEL_MODAL_OUTREACH_MATCHING_TAG_BASE}`}
      >
        <span className="text-slate-600">entity</span>
        <span className="text-slate-500" aria-hidden>
          :
        </span>
        <span className="tabular-nums text-slate-800">{entityLabel}</span>
      </span>
    </>
  );
}

function HotelModalCommissionDefaultsSection({
  hotelId,
  hotelDefaultCommission,
  hotelCommissionAdminComment,
  hotelInvoicingEntity,
  onSaved,
}: Readonly<{
  hotelId: number;
  hotelDefaultCommission: number | null;
  hotelCommissionAdminComment: string;
  hotelInvoicingEntity: 'NL' | 'SP' | null;
  onSaved: () => void | Promise<void>;
}>) {
  const [draftPct, setDraftPct] = useState('');
  const [draftComment, setDraftComment] = useState('');
  const [draftEntity, setDraftEntity] = useState<HotelModalInvoicingEntityDraft>('');
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    setDraftPct(hotelDefaultCommission != null ? String(hotelDefaultCommission) : '');
    setDraftComment(hotelCommissionAdminComment);
    setDraftEntity(
      hotelInvoicingEntity === 'NL' || hotelInvoicingEntity === 'SP' ? hotelInvoicingEntity : '',
    );
    setLocalError(null);
  }, [hotelId, hotelDefaultCommission, hotelCommissionAdminComment, hotelInvoicingEntity]);

  const handleDraftPctChange = (value: string) => {
    const v = value.replace(',', '.');
    if (v !== '' && !/^\d*\.?\d*$/.test(v)) return;
    setDraftPct(v);
  };

  const handleSave = async () => {
    setLocalError(null);
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const raw = draftPct.trim();
      if (raw === '') {
        const { error: delErr } = await supabase
          .from('revenue_collection_hotel_commissions')
          .delete()
          .eq('hotel_id', hotelId);
        if (delErr) throw delErr;
      } else {
        const num = Number.parseFloat(raw.replace(',', '.'));
        if (Number.isNaN(num) || num < 0 || num > 100) {
          throw new Error('Commission must be between 0 and 100.');
        }
        const ent = draftEntity;
        const note = draftComment.trim();
        const { error: upErr } = await supabase
          .from('revenue_collection_hotel_commissions')
          .upsert(
            {
              hotel_id: hotelId,
              commission_percent: num,
              updated_at: now,
              invoicing_entity: ent === '' ? null : ent,
              admin_comment: note === '' ? null : note,
            },
            { onConflict: 'hotel_id' },
          );
        if (upErr) throw upErr;
      }
      await onSaved();
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Could not save commission settings');
    } finally {
      setSaving(false);
    }
  };

  const commissionRowDisabled = draftPct.trim() === '';

  return (
    <details className="group mb-3 rounded-lg border border-gray-200 bg-gray-50/80 text-xs">
      <summary className="flex w-full cursor-pointer list-none items-center gap-2 px-3 py-2.5 marker:content-none [&::-webkit-details-marker]:hidden">
        <ChevronRight
          className="h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 group-open:rotate-90 motion-reduce:transition-none"
          aria-hidden
        />
        <span className="shrink-0 font-semibold text-gray-900">Commission</span>
        <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2">
          <HotelModalCommissionSummaryTags
            hotelDefaultCommission={hotelDefaultCommission}
            hotelCommissionAdminComment={hotelCommissionAdminComment}
            hotelInvoicingEntity={hotelInvoicingEntity}
          />
        </div>
      </summary>
      <div className="flex w-full flex-col gap-3 border-t border-gray-200 px-3 pb-3 pt-3">
        <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
          <div className="w-[7.5rem] shrink-0">
            <label
              htmlFor={`hotel-modal-commission-pct-${hotelId}`}
              className="mb-1 block text-xs font-normal text-gray-600"
            >
              Default %
            </label>
            <input
              id={`hotel-modal-commission-pct-${hotelId}`}
              type="text"
              inputMode="decimal"
              value={draftPct}
              onChange={(e) => handleDraftPctChange(e.target.value)}
              className="w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-900 shadow-sm focus:border-action focus:outline-none focus:ring-1 focus:ring-action/30"
              placeholder="e.g. 8.5"
              autoComplete="off"
              title="Leave empty to remove this hotel's commission row (same as Hotel commissions)."
            />
          </div>
          <div className="min-w-0 flex-1 basis-[clamp(12rem,28vw,28rem)]">
            <label
              htmlFor={`hotel-modal-commission-comment-${hotelId}`}
              className="mb-1 block text-xs font-normal text-gray-600"
            >
              Comment
            </label>
            <input
              id={`hotel-modal-commission-comment-${hotelId}`}
              type="text"
              value={draftComment}
              onChange={(e) => setDraftComment(e.target.value)}
              maxLength={HOTEL_MODAL_COMMISSION_COMMENT_MAX_LEN}
              disabled={commissionRowDisabled}
              placeholder="—"
              title={
                commissionRowDisabled
                  ? 'Set a commission % first to save a comment on this row.'
                  : `Up to ${HOTEL_MODAL_COMMISSION_COMMENT_MAX_LEN} characters`
              }
              className="w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-900 leading-tight shadow-sm focus:border-action focus:outline-none focus:ring-1 focus:ring-action/30 disabled:cursor-not-allowed disabled:opacity-50"
              autoComplete="off"
            />
          </div>
          <div className="min-w-[9rem] shrink-0">
            <label
              htmlFor={`hotel-modal-commission-entity-${hotelId}`}
              className="mb-1 block text-xs font-normal text-gray-600"
            >
              Invoicing entity
            </label>
            <select
              id={`hotel-modal-commission-entity-${hotelId}`}
              value={draftEntity}
              onChange={(e) =>
                setDraftEntity(e.target.value as HotelModalInvoicingEntityDraft)
              }
              disabled={commissionRowDisabled}
              title={
                commissionRowDisabled
                  ? 'Set a commission % first; invoicing entity is stored with the commission row.'
                  : undefined
              }
              className="w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-900 shadow-sm focus:border-action focus:outline-none focus:ring-1 focus:ring-action/30 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {HOTEL_MODAL_INVOICING_ENTITY_OPTIONS.map((o) => (
                <option key={o.value || 'empty'} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        {localError ? (
          <p className="text-[11px] text-red-600">{localError}</p>
        ) : null}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="btn-secondary-xs-compact disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save commission settings'}
          </button>
        </div>
      </div>
    </details>
  );
}

function HotelModalSubmissionFooterRecap({
  outreach,
  backyouSyncRecap,
}: Readonly<{
  outreach: HotelDetailModalOutreach;
  backyouSyncRecap: { text: string; success: boolean } | null;
}>) {
  const adminValidationTimeDisplay = resolveAdminValidationTimeDisplay(outreach);
  const submissionRecapValue = (() => {
    if (!outreach.validated_at) return '—';
    const dt = formatDateTime(outreach.validated_at);
    const ob = outreach.submitted_on_behalf_by?.trim();
    if (ob) {
      return `${dt} · On behalf of hotel · ${ob}`;
    }
    return dt;
  })();
  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <ScrollText className="h-3.5 w-3.5 text-[#17776F]" aria-hidden />
        <span className="text-xs font-normal text-gray-700">Logs</span>
      </div>
      <HotelModalRecapRow
        label="Hotel form submission"
        value={submissionRecapValue}
      />
      {outreach.validated_by && !outreach.validated_at ? (
        <p className="mt-0.5 text-xs text-gray-500">
          No submission via the hotel link; figures were entered or confirmed from the admin dashboard.
        </p>
      ) : null}
      {outreach.validated_by && adminValidationTimeDisplay != null ? (
        <p className="mt-1 text-xs text-gray-700">
          Admin validation
          <span className="tabular-nums">{' · '}{adminValidationTimeDisplay}</span>
          {' · '}
          {outreach.validated_by}
        </p>
      ) : null}
      {backyouSyncRecap ? (
        <p
          className={`mt-1 text-xs tabular-nums ${
            backyouSyncRecap.success ? 'text-emerald-800' : 'text-red-800'
          }`}
        >
          {backyouSyncRecap.text}
        </p>
      ) : null}
    </div>
  );
}

function HotelModalSubmissionFooterActions({
  showBackyouSync,
  showValidate,
  validatingSubmission,
  resyncingBackyou,
  submittingOnBehalf,
  onValidateSubmission,
  onBackyouResync,
}: Readonly<{
  showBackyouSync: boolean;
  showValidate: boolean;
  validatingSubmission: boolean;
  resyncingBackyou: boolean;
  submittingOnBehalf: boolean;
  onValidateSubmission: () => void;
  onBackyouResync: () => void;
}>) {
  if (!showBackyouSync && !showValidate) {
    return null;
  }
  return (
    <div className="flex flex-wrap justify-end gap-2">
      {showBackyouSync ? (
        <button
          type="button"
          onClick={onBackyouResync}
          disabled={resyncingBackyou || validatingSubmission || submittingOnBehalf}
          title="Retry pushing to Backyou after a failed row (red BY). Saving the form also runs sync—use this only when the form has no unsaved edits."
          className="btn-secondary-sm disabled:cursor-not-allowed"
        >
          {resyncingBackyou ? 'Syncing to Backyou…' : 'Sync to Backyou'}
        </button>
      ) : null}
      {showValidate ? (
        <button
          type="button"
          onClick={onValidateSubmission}
          disabled={validatingSubmission || resyncingBackyou || submittingOnBehalf}
          className="btn-teal-subtle"
        >
          <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
          {validatingSubmission ? 'Validating…' : 'Validate submission'}
        </button>
      ) : null}
    </div>
  );
}

function HotelModalSubmissionFooter({
  outreach,
  backyouSyncRecap,
}: Readonly<{
  outreach: HotelDetailModalOutreach;
  /** When validated and at least one group has realised: timestamp + Success or Failed (matches BY column). */
  backyouSyncRecap: { text: string; success: boolean } | null;
}>) {
  return (
    <div className="mt-4 border-t border-gray-200 pt-4">
      <HotelModalSubmissionFooterRecap outreach={outreach} backyouSyncRecap={backyouSyncRecap} />
    </div>
  );
}

function HotelDetailModal({
  campaignId,
  hotelId,
  periodMonth,
  periodYear,
  onClose,
  onRefresh,
  onCancelReminders,
  cancellingReminders,
  onCancelCampaignHotel,
  onUndoCancelCampaignHotel,
  cancellingCampaignHotel,
}: Readonly<{
  campaignId: string;
  hotelId: number;
  periodMonth: number;
  periodYear: number;
  onClose: () => void;
  onRefresh: () => void | Promise<void>;
  onCancelReminders: () => void | Promise<void>;
  cancellingReminders: boolean;
  onCancelCampaignHotel: () => void | Promise<void>;
  onUndoCancelCampaignHotel: (outreachSnapshot: CampaignHotelUndoCancelSnapshot) => void | Promise<void>;
  cancellingCampaignHotel: boolean;
}>) {
  const { canMutateRevenue } = useRevenueAccess();
  const [groups, setGroups] = useState<Array<{
    request_id: number;
    checkin_date: string;
    checkout_date: string | null;
    number_of_nights: number | null;
    company: string;
    client_request_reference?: string | null;
    request_manager: string | null;
    estimated_revenue: number;
    /** Stored override; null inherits hotel default from revenue_collection_hotel_commissions */
    commission_percent: number | null;
    realised_revenue: number | null;
    comment: string | null;
    backyou_last_sync_at: string | null;
    backyou_last_sync_ok: boolean | null;
    backyou_last_sync_error: string | null;
    backyou_synced_realised_revenue: number | null;
  }>>([]);
  const [manualLines, setManualLines] = useState<
    Array<{
      id: string;
      client_name: string;
      ref_client: string | null;
      checkin_date: string;
      checkout_date: string;
      estimated_revenue: number;
      commission_percent: number | null;
      realised_revenue: number | null;
      comment: string | null;
    }>
  >([]);
  const [deletedManualLineIds, setDeletedManualLineIds] = useState<string[]>([]);
  const [hotelDefaultCommission, setHotelDefaultCommission] = useState<number | null>(null);
  const [globalComment, setGlobalComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validatingSubmission, setValidatingSubmission] = useState(false);
  const [resyncingBackyou, setResyncingBackyou] = useState(false);
  const [submittingOnBehalf, setSubmittingOnBehalf] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** `request_id:checkin_date` after copy success (brief UX feedback). */
  const [copiedRequestRowKey, setCopiedRequestRowKey] = useState<string | null>(null);
  const copyRequestIdFeedbackTimerRef = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);
  const [outreach, setOutreach] = useState<HotelDetailModalOutreach | null>(null);
  const [emailSends, setEmailSends] = useState<HotelDetailModalEmailSend[]>([]);
  /** Public hotelier link (same token as emails); null if none active. */
  const [hotelierFormUrl, setHotelierFormUrl] = useState<string | null>(null);
  const [hotelFormLinkCopied, setHotelFormLinkCopied] = useState(false);
  const [modalHotelTitle, setModalHotelTitle] = useState<string | null>(null);
  const [modalBounceDetail, setModalBounceDetail] = useState<EmailBounceDetailView | null>(null);
  const [campaignReminderConfig, setCampaignReminderConfig] = useState<CampaignReminderConfigRow[]>([]);
  const [lastBackyouSyncWarning, setLastBackyouSyncWarning] = useState<string | null>(null);
  /** Last loaded/saved form fingerprint; validated submissions require dirty state to enable Save. */
  const [formDirtyBaselineKey, setFormDirtyBaselineKey] = useState('');
  const [hotelInvoicingContact, setHotelInvoicingContact] = useState<string | null>(null);
  const [hotelInvoiceContactFirstName, setHotelInvoiceContactFirstName] = useState('');
  const [hotelInvoiceContactLastName, setHotelInvoiceContactLastName] = useState('');
  const [hotelInvoicingEntity, setHotelInvoicingEntity] = useState<'NL' | 'SP' | null>(null);
  const [hotelCommissionAdminComment, setHotelCommissionAdminComment] = useState('');
  const [invoiceTemplates, setInvoiceTemplates] = useState<HotelDetailInvoiceTemplateOption[]>([]);
  /** Raw commission % while typing (avoids "8." → 8 → "85" when controlled value drops the decimal). */
  const [commissionPercentDrafts, setCommissionPercentDrafts] = useState<Record<string, string>>({});
  /** Raw realised € while typing (avoids "1005." → 1005 → losing the dot before cents). */
  const [realisedRevenueDrafts, setRealisedRevenueDrafts] = useState<Record<string, string>>({});
  const [groupSortBy, setGroupSortBy] = useState<HotelModalGroupSort>('checkin');
  const [internalNoteId, setInternalNoteId] = useState<string | null>(null);
  const [internalNoteBody, setInternalNoteBody] = useState('');

  useEffect(() => {
    return () => {
      if (copyRequestIdFeedbackTimerRef.current) {
        globalThis.clearTimeout(copyRequestIdFeedbackTimerRef.current);
      }
    };
  }, []);

  const handleCopyGroupRequestId = useCallback(async (requestId: number, checkinDate: string) => {
    const rowKey = `${requestId}:${checkinDate}`;
    try {
      await copyPlainTextToClipboard(String(requestId));
      setError(null);
      if (copyRequestIdFeedbackTimerRef.current) {
        globalThis.clearTimeout(copyRequestIdFeedbackTimerRef.current);
      }
      setCopiedRequestRowKey(rowKey);
      copyRequestIdFeedbackTimerRef.current = globalThis.setTimeout(() => {
        setCopiedRequestRowKey(null);
        copyRequestIdFeedbackTimerRef.current = null;
      }, 1600);
    } catch {
      setError('Could not copy request ID.');
    }
  }, []);

  const fetchInternalNote = useCallback(async () => {
    const { data, error: fetchErr } = await supabase
      .from('revenue_collection_hotel_internal_comments')
      .select('id, body')
      .eq('campaign_id', campaignId)
      .eq('hotel_id', hotelId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (fetchErr) {
      console.error('fetchInternalNote:', fetchErr);
      setInternalNoteId(null);
      setInternalNoteBody('');
      return;
    }
    setInternalNoteId(data?.id ? String(data.id) : null);
    setInternalNoteBody(data?.body ? String(data.body) : '');
  }, [campaignId, hotelId]);

  const fetchGroups = async () => {
    setLoading(true);
    setHotelierFormUrl(null);
    setHotelFormLinkCopied(false);
    setModalHotelTitle(null);
    setCampaignReminderConfig([]);
    setHotelInvoicingEntity(null);
    setHotelInvoiceContactFirstName('');
    setHotelInvoiceContactLastName('');
    setHotelCommissionAdminComment('');
    setInvoiceTemplates([]);
    setCommissionPercentDrafts({});
    setRealisedRevenueDrafts({});
    setGroupSortBy('checkin');
    const data = await loadHotelDetailModalData({
      campaignId,
      hotelId,
      periodMonth,
      periodYear,
    });
    setOutreach(data.outreach);
    setModalHotelTitle(data.modalHotelTitle);
    setEmailSends(data.emailSends);
    setCampaignReminderConfig(data.campaignReminderConfig);
    setHotelDefaultCommission(data.hotelDefaultCommission);
    setGroups(data.groups);
    setManualLines(data.manualLines);
    setDeletedManualLineIds([]);
    setGlobalComment(data.globalComment);
    setHotelierFormUrl(data.hotelierFormUrl);
    setHotelInvoicingContact(data.hotelInvoicingContact);
    setHotelInvoiceContactFirstName(data.hotelInvoiceContactFirstName);
    setHotelInvoiceContactLastName(data.hotelInvoiceContactLastName);
    setHotelInvoicingEntity(data.hotelInvoicingEntity);
    setHotelCommissionAdminComment(data.hotelCommissionAdminComment);
    setInvoiceTemplates(data.invoiceTemplates);
    setFormDirtyBaselineKey(
      serializeHotelModalDirtySnapshot(data.groups, data.manualLines, data.globalComment, []),
    );
    setLoading(false);
  };

  const silentRefreshAfterCommissionSave = useCallback(async () => {
    try {
      const data = await loadHotelDetailModalData({
        campaignId,
        hotelId,
        periodMonth,
        periodYear,
      });
      setHotelDefaultCommission(data.hotelDefaultCommission);
      setHotelCommissionAdminComment(data.hotelCommissionAdminComment);
      setHotelInvoicingEntity(data.hotelInvoicingEntity);
      setGroups(data.groups);
      setManualLines(data.manualLines);
      setRealisedRevenueDrafts({});
      setFormDirtyBaselineKey(
        serializeHotelModalDirtySnapshot(
          data.groups,
          data.manualLines,
          globalComment,
          deletedManualLineIds,
        ),
      );
      await Promise.resolve(onRefresh());
    } catch (e) {
      console.error('silentRefreshAfterCommissionSave:', e);
      setError(e instanceof Error ? e.message : 'Could not refresh after commission save');
    }
  }, [
    campaignId,
    hotelId,
    periodMonth,
    periodYear,
    globalComment,
    deletedManualLineIds,
    onRefresh,
  ]);

  useEffect(() => {
    setLastBackyouSyncWarning(null);
    void fetchGroups();
    void fetchInternalNote();
  }, [campaignId, hotelId, periodMonth, periodYear, fetchInternalNote]);

  const pendingReminderPlans = useMemo(() => {
    if (!outreach) return {} as Partial<Record<ReminderKind, { at: Date; overdue: boolean }>>;
    return buildPendingReminderPlans(outreach, campaignReminderConfig);
  }, [outreach, campaignReminderConfig]);

  const manualStayWindow = manualLineStayWindowBounds(
    `${periodYear}-${String(periodMonth).padStart(2, '0')}`,
  );

  const isHotelModalFormDirty = useMemo(() => {
    if (!formDirtyBaselineKey) return false;
    return (
      serializeHotelModalDirtySnapshot(
        groups,
        manualLines,
        globalComment,
        deletedManualLineIds,
      ) !== formDirtyBaselineKey
    );
  }, [groups, manualLines, globalComment, deletedManualLineIds, formDirtyBaselineKey]);

  const groupCommissionDraftKey = (g: { request_id: number; checkin_date: string }) =>
    `g:${g.request_id}:${g.checkin_date}`;

  const manualCommissionDraftKey = (id: string) => `m:${id}`;

  const handleRealisedChange = (idx: number, value: string) => {
    const row = groups[idx];
    if (!row) return;
    const key = groupCommissionDraftKey(row);
    const normalized = normaliseRealisedAmountInput(value);
    setRealisedRevenueDrafts((prev) => ({ ...prev, [key]: normalized }));
  };

  const handleRealisedBlur = (idx: number) => {
    const row = groups[idx];
    if (!row) return;
    const key = groupCommissionDraftKey(row);
    const draft = realisedRevenueDrafts[key];
    if (draft === undefined) return;
    const num = parseRealisedAmountInputToNumber(draft);
    setGroups((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], realised_revenue: num };
      return next;
    });
    setRealisedRevenueDrafts((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleCommentChange = (idx: number, value: string) => {
    setGroups((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], comment: value };
      return next;
    });
  };

  const handleCommissionChange = (idx: number, value: string) => {
    const row = groups[idx];
    if (!row) return;
    const key = groupCommissionDraftKey(row);

    if (value.trim() === '') {
      setCommissionPercentDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setGroups((prev) => {
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          commission_percent: hotelDefaultCommission ?? null,
        };
        return next;
      });
      return;
    }

    setCommissionPercentDrafts((prev) => ({ ...prev, [key]: value }));

    const norm = value.trim().replace(',', '.');
    if (norm === '.' || norm === '-' || norm === '-.') return;

    const num = Number.parseFloat(norm);
    if (Number.isNaN(num)) return;
    const clamped = Math.min(100, Math.max(0, num));
    setGroups((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], commission_percent: clamped };
      return next;
    });
  };

  const handleCommissionBlur = (idx: number) => {
    const row = groups[idx];
    if (!row) return;
    const key = groupCommissionDraftKey(row);
    setCommissionPercentDrafts((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleManualRealisedChange = (idx: number, value: string) => {
    const row = manualLines[idx];
    if (!row) return;
    const key = manualCommissionDraftKey(row.id);
    const normalized = normaliseRealisedAmountInput(value);
    setRealisedRevenueDrafts((prev) => ({ ...prev, [key]: normalized }));
  };

  const handleManualRealisedBlur = (idx: number) => {
    const row = manualLines[idx];
    if (!row) return;
    const key = manualCommissionDraftKey(row.id);
    const draft = realisedRevenueDrafts[key];
    if (draft === undefined) return;
    const num = parseRealisedAmountInputToNumber(draft);
    setManualLines((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], realised_revenue: num };
      return next;
    });
    setRealisedRevenueDrafts((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleManualCommissionChange = (idx: number, value: string) => {
    const row = manualLines[idx];
    if (!row) return;
    const key = manualCommissionDraftKey(row.id);

    if (value.trim() === '') {
      setCommissionPercentDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setManualLines((prev) => {
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          commission_percent: hotelDefaultCommission ?? null,
        };
        return next;
      });
      return;
    }

    setCommissionPercentDrafts((prev) => ({ ...prev, [key]: value }));

    const norm = value.trim().replace(',', '.');
    if (norm === '.' || norm === '-' || norm === '-.') return;

    const num = Number.parseFloat(norm);
    if (Number.isNaN(num)) return;
    const clamped = Math.min(100, Math.max(0, num));
    setManualLines((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], commission_percent: clamped };
      return next;
    });
  };

  const handleManualCommissionBlur = (idx: number) => {
    const row = manualLines[idx];
    if (!row) return;
    const key = manualCommissionDraftKey(row.id);
    setCommissionPercentDrafts((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleManualCommentChange = (idx: number, value: string) => {
    setManualLines((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], comment: value || null };
      return next;
    });
  };

  const removeManualLine = (idx: number) => {
    const row = manualLines[idx];
    if (row?.id) {
      setDeletedManualLineIds((d) => (d.includes(row.id) ? d : [...d, row.id]));
      setCommissionPercentDrafts((prev) => {
        const next = { ...prev };
        delete next[manualCommissionDraftKey(row.id)];
        return next;
      });
    }
    setManualLines((prev) => prev.filter((_, i) => i !== idx));
  };

  const addEmptyManualLine = () => {
    const periodLabel = `${periodYear}-${String(periodMonth).padStart(2, '0')}`;
    const defaults = defaultManualLineStayDates(periodLabel);
    setManualLines((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        client_name: '',
        ref_client: null,
        checkin_date: defaults?.checkin ?? '',
        checkout_date: defaults?.checkout ?? '',
        estimated_revenue: 0,
        realised_revenue: null,
        comment: null,
        commission_percent: hotelDefaultCommission,
      },
    ]);
  };

  const effectiveCommissionPct = (
    g: { commission_percent: number | null },
  ): number | null => (g.commission_percent != null ? g.commission_percent : hotelDefaultCommission);

  type InvoiceAmountRow =
    | (typeof groups)[number]
    | (typeof manualLines)[number];

  const effectiveRealisedForInvoiceRow = (row: InvoiceAmountRow): number | null => {
    if ('request_id' in row) {
      const key = groupCommissionDraftKey(row);
      const draft = realisedRevenueDrafts[key];
      if (draft !== undefined) return parseRealisedAmountInputToNumber(draft);
      return row.realised_revenue;
    }
    const key = manualCommissionDraftKey(row.id);
    const draft = realisedRevenueDrafts[key];
    if (draft !== undefined) return parseRealisedAmountInputToNumber(draft);
    return row.realised_revenue;
  };

  /** Realised revenue × (effective commission % / 100) */
  const invoiceAmountForGroup = (row: InvoiceAmountRow): number | null => {
    const pct = effectiveCommissionPct(row);
    if (pct == null) return null;
    const rev = effectiveRealisedForInvoiceRow(row);
    if (rev == null) return null;
    return rev * (pct / 100);
  };

  const formatInvoiceAmountCell = (row: InvoiceAmountRow) => {
    const amt = invoiceAmountForGroup(row);
    if (amt == null) return '—';
    return formatCurrencyDecimals(amt);
  };

  const { totalRealisedRevenue, totalInvoiceAmount } = useMemo(
    () =>
      computeTotalRealisedAndInvoice(
        groups.map((g) => {
          const key = groupCommissionDraftKey(g);
          const draft = realisedRevenueDrafts[key];
          const realised =
            draft !== undefined ? parseRealisedAmountInputToNumber(draft) : g.realised_revenue;
          return { ...g, realised_revenue: realised };
        }),
        manualLines.map((m) => {
          const key = manualCommissionDraftKey(m.id);
          const draft = realisedRevenueDrafts[key];
          const realised =
            draft !== undefined ? parseRealisedAmountInputToNumber(draft) : m.realised_revenue;
          return { ...m, realised_revenue: realised };
        }),
        hotelDefaultCommission,
      ),
    [groups, manualLines, hotelDefaultCommission, realisedRevenueDrafts],
  );

  const sortedGroupRows = useMemo(
    () => sortedHotelModalGroupRows(groups, groupSortBy),
    [groups, groupSortBy],
  );

  const backyouSyncRecapBase = useMemo(() => computeHotelModalBackyouSyncRecap(groups), [groups]);

  const backyouSyncRecap = useMemo(() => {
    if (
      !outreach?.status ||
      !isRevenueCampaignHotelFormComplete(outreach.status) ||
      !backyouSyncRecapBase
    ) {
      return null;
    }
    const statusWord = backyouSyncRecapBase.success ? 'Success' : 'Failed';
    return {
      text: `Backyou sync · ${backyouSyncRecapBase.atLabel} · ${statusWord}`,
      success: backyouSyncRecapBase.success,
    };
  }, [outreach?.status, backyouSyncRecapBase]);

  const showBackyouSyncButton = useMemo(() => {
    if (!outreach?.status || !isRevenueCampaignHotelFormComplete(outreach.status)) return false;
    if (isHotelModalFormDirty) return false;
    return hotelModalHasAnyGroupWithFailedBySync(groups);
  }, [outreach?.status, groups, isHotelModalFormDirty]);

  const submissionToolbarVisible =
    canMutateRevenue &&
    outreach != null &&
    outreach.status !== 'cancelled' &&
    (showBackyouSyncButton ||
      (outreach.status === 'validated' && !outreach.validated_by));

  const showCampaignCancelToolbar = useMemo(() => {
    if (!canMutateRevenue || !outreach) return false;
    return (
      canAdminCancelCampaignHotel({
        status: outreach.status,
        validated_by: outreach.validated_by,
      }) || canAdminUndoCancelCampaignHotel(outreach.status)
    );
  }, [outreach, canMutateRevenue]);

  const isHotelCancelled = outreach?.status === 'cancelled';
  const isHotelModalReadOnly = isHotelCancelled || !canMutateRevenue;

  const canSubmitOnBehalf = useMemo(() => {
    if (!outreach) return false;
    if (isHotelModalReadOnly) return false;
    if (outreach.validated_at) return false;
    if (isRevenueCampaignHotelFormComplete(outreach.status)) return false;
    return true;
  }, [outreach, isHotelModalReadOnly]);

  const handleSave = async () => {
    if (!canMutateRevenue) return;
    setSaving(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');

      const { data, error: fnError } = await supabase.functions.invoke('save_revenue_responses_admin', {
        body: {
          campaign_id: campaignId,
          hotel_id: hotelId,
          validate: false,
          responses: groups.map((g) => ({
            request_id: g.request_id,
            checkin_date: g.checkin_date,
            estimated_revenue: g.estimated_revenue,
            realised_revenue: g.realised_revenue,
            comment: g.comment,
            commission_percent: g.commission_percent,
          })),
          manual_lines: manualLines
            .filter((m) => m.client_name.trim() !== '')
            .map((m) => ({
              id: m.id,
              client_name: m.client_name.trim(),
              ref_client: m.ref_client?.trim() ? m.ref_client.trim() : null,
              checkin_date: m.checkin_date,
              checkout_date: m.checkout_date,
              estimated_revenue: m.estimated_revenue,
              realised_revenue: m.realised_revenue,
              comment: m.comment,
              commission_percent: m.commission_percent,
            })),
          ...(deletedManualLineIds.length > 0 ? { deleted_manual_line_ids: deletedManualLineIds } : {}),
        },
      });

      if (fnError) {
        throw new Error(revenueAdminInvokeErrorMessage(data, fnError, 'Save failed'));
      }
      setDeletedManualLineIds([]);
      await fetchGroups();
      await Promise.resolve(onRefresh());
      const bs = parseAdminSaveBackyouSync(data);
      if (bs && !bs.ok) {
        setLastBackyouSyncWarning(backyouSyncFailureBannerText(bs));
      } else {
        setLastBackyouSyncWarning(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitOnBehalf = async () => {
    if (!canMutateRevenue) return;
    setSubmittingOnBehalf(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');

      const { data, error: fnError } = await supabase.functions.invoke('save_revenue_responses_admin', {
        body: {
          campaign_id: campaignId,
          hotel_id: hotelId,
          submit_on_behalf_of_hotel: true,
          responses: groups.map((g) => ({
            request_id: g.request_id,
            checkin_date: g.checkin_date,
            estimated_revenue: g.estimated_revenue,
            realised_revenue: g.realised_revenue,
            comment: g.comment,
            commission_percent: g.commission_percent,
          })),
          manual_lines: manualLines
            .filter((m) => m.client_name.trim() !== '')
            .map((m) => ({
              id: m.id,
              client_name: m.client_name.trim(),
              ref_client: m.ref_client?.trim() ? m.ref_client.trim() : null,
              checkin_date: m.checkin_date,
              checkout_date: m.checkout_date,
              estimated_revenue: m.estimated_revenue,
              realised_revenue: m.realised_revenue,
              comment: m.comment,
              commission_percent: m.commission_percent,
            })),
          ...(deletedManualLineIds.length > 0 ? { deleted_manual_line_ids: deletedManualLineIds } : {}),
        },
      });

      if (fnError) {
        throw new Error(revenueAdminInvokeErrorMessage(data, fnError, 'Submit on behalf failed'));
      }
      setDeletedManualLineIds([]);
      await fetchGroups();
      await Promise.resolve(onRefresh());
      setLastBackyouSyncWarning(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submit on behalf failed');
    } finally {
      setSubmittingOnBehalf(false);
    }
  };

  const handleValidateSubmission = async () => {
    if (!canMutateRevenue) return;
    setValidatingSubmission(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');

      const { data, error: fnError } = await supabase.functions.invoke('save_revenue_responses_admin', {
        body: {
          campaign_id: campaignId,
          hotel_id: hotelId,
          validate: true,
          responses: [],
        },
      });

      if (fnError) {
        throw new Error(revenueAdminInvokeErrorMessage(data, fnError, 'Validation failed'));
      }
      await fetchGroups();
      await Promise.resolve(onRefresh());
      const bs = parseAdminSaveBackyouSync(data);
      if (bs && !bs.ok) {
        setLastBackyouSyncWarning(backyouSyncFailureBannerText(bs));
      } else {
        setLastBackyouSyncWarning(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Validation failed');
    } finally {
      setValidatingSubmission(false);
    }
  };

  const handleBackyouResync = async () => {
    if (!canMutateRevenue) return;
    setResyncingBackyou(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');

      const { data, error: fnError } = await supabase.functions.invoke('save_revenue_responses_admin', {
        body: {
          campaign_id: campaignId,
          hotel_id: hotelId,
          backyou_resync_only: true,
        },
      });

      if (fnError) {
        throw new Error(revenueAdminInvokeErrorMessage(data, fnError, 'Backyou sync failed'));
      }
      await fetchGroups();
      await Promise.resolve(onRefresh());
      const bs = parseAdminSaveBackyouSync(data);
      if (bs && !bs.ok) {
        setLastBackyouSyncWarning(backyouSyncFailureBannerText(bs));
      } else {
        setLastBackyouSyncWarning(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Backyou sync failed');
    } finally {
      setResyncingBackyou(false);
    }
  };

  const campaignPeriodTag = `${periodYear}-${String(periodMonth).padStart(2, '0')}`;
  const modalTitleHotel = modalHotelTitle?.trim() || `Hotel ${hotelId}`;

  const invoiceSend = emailSends.find((s) => s.email_kind === 'invoice');
  const invoiceBounceContent = invoiceSend
    ? bounceDetailForEmailSend(invoiceSend, EMAIL_KIND_LABELS.invoice, deliveryStateLabel)
    : null;
  const invoiceStatusSummary = hotelModalInvoiceTrackingSummary(invoiceSend);

  return (
    <>
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="flex min-h-0 min-w-0 w-full max-w-6xl flex-col overflow-hidden rounded-lg bg-white shadow-xl max-h-[90vh]">
        <div className="relative border-b px-4 py-3 pr-14">
          <button
            type="button"
            onClick={onClose}
            className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded p-2 hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="grid w-full grid-cols-[1fr_auto_1fr] items-center gap-x-3 gap-y-1">
            <div className="min-w-0 text-left">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <h3 className="text-base font-semibold text-gray-900">
                  {modalTitleHotel}
                </h3>
                <span className="inline-flex shrink-0 items-center rounded-full border border-gray-200 bg-gray-100 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-gray-700">
                  {campaignPeriodTag}
                </span>
              </div>
            </div>
            <div className="flex justify-center">
              {hotelierFormUrl && !isHotelCancelled ? (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(hotelierFormUrl);
                      setHotelFormLinkCopied(true);
                      globalThis.setTimeout(() => {
                        setHotelFormLinkCopied(false);
                      }, 2000);
                    } catch {
                      setError('Could not copy link—try copying from the address bar manually.');
                    }
                  }}
                  className="inline-flex max-w-full items-center justify-center gap-1.5 rounded-md border border-action/30 bg-action-surface/60 px-2.5 py-1 text-xs font-medium text-action transition-colors hover:bg-action-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-action/35 focus-visible:ring-offset-1"
                >
                  <Copy className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
                  {hotelFormLinkCopied ? 'Copied!' : 'Hotel form link'}
                </button>
              ) : null}
            </div>
            <div className="min-w-0" aria-hidden="true" />
          </div>
        </div>
        {error && (
          <div className="mx-3 sm:mx-4 mt-2 bg-red-50 border border-red-200 text-red-700 px-3 py-1.5 rounded text-xs">
            {error}
          </div>
        )}
        {lastBackyouSyncWarning ? (
          <div className="mx-3 sm:mx-4 mt-2 whitespace-pre-line bg-amber-50 border border-amber-200 text-amber-900 px-3 py-1.5 rounded text-xs">
            {lastBackyouSyncWarning}
          </div>
        ) : null}
        <div className="min-w-0 overflow-auto flex-1 p-3 sm:p-4">
          {loading ? (
            <div className="text-xs text-gray-500">Loading...</div>
          ) : (
            <>
              {outreach ? (
                <>
                  <HotelModalCancelledBanner outreach={outreach} />
                  <HotelModalEmailOutreachSection
                    outreach={outreach}
                    emailSends={emailSends}
                    pendingReminderPlans={pendingReminderPlans}
                    onCancelReminders={onCancelReminders}
                    cancellingReminders={cancellingReminders}
                    onRefreshAfterCancel={fetchGroups}
                    contextTitle={modalTitleHotel}
                    onOpenBounceDetail={setModalBounceDetail}
                  />
                </>
              ) : null}
              {!isHotelCancelled && canMutateRevenue ? (
                <HotelModalCommissionDefaultsSection
                  hotelId={hotelId}
                  hotelDefaultCommission={hotelDefaultCommission}
                  hotelCommissionAdminComment={hotelCommissionAdminComment}
                  hotelInvoicingEntity={hotelInvoicingEntity}
                  onSaved={silentRefreshAfterCommissionSave}
                />
              ) : !isHotelCancelled ? (
                <div className="mb-3 rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2.5 text-xs">
                  <div className="flex w-full items-center gap-2">
                    <span className="shrink-0 font-semibold text-gray-900">Commission</span>
                    <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2">
                      <HotelModalCommissionSummaryTags
                        hotelDefaultCommission={hotelDefaultCommission}
                        hotelCommissionAdminComment={hotelCommissionAdminComment}
                        hotelInvoicingEntity={hotelInvoicingEntity}
                      />
                    </div>
                  </div>
                </div>
              ) : null}
              {!isHotelCancelled && canMutateRevenue && hotelOutreachEligibleForInvoiceSection(outreach) ? (
                <div className="mt-3 mb-6">
                  <details className="group rounded-lg border border-gray-200 bg-gray-50/90 text-xs">
                    <summary className="flex w-full cursor-pointer list-none items-center gap-2 px-3 py-2.5 marker:content-none [&::-webkit-details-marker]:hidden">
                      <ChevronRight
                        className="h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 group-open:rotate-90 motion-reduce:transition-none"
                        aria-hidden
                      />
                      <span className="shrink-0 font-semibold text-gray-900">Send Invoice</span>
                      <span className="min-w-0 flex-1 text-right text-xs font-normal tabular-nums text-gray-600">
                        {invoiceBounceContent ? (
                          <HotelModalBounceStatusButton
                            statusLine={invoiceStatusSummary}
                            contextTitle={modalTitleHotel}
                            bounceContent={invoiceBounceContent}
                            onOpenBounceDetail={setModalBounceDetail}
                            className="text-xs tabular-nums text-red-700/90 underline decoration-red-300/70 decoration-dotted underline-offset-2 hover:text-red-800"
                          />
                        ) : (
                          invoiceStatusSummary
                        )}
                      </span>
                    </summary>
                    <div className="space-y-3 border-t border-gray-200 px-3 pb-5 pt-3">
                      <HotelModalInvoiceSendFormFields
                        campaignId={campaignId}
                        hotelId={hotelId}
                        invoicingContact={hotelInvoicingContact}
                        hotelTitle={modalTitleHotel}
                        campaignPeriodTag={campaignPeriodTag}
                        invoiceContactFirstName={hotelInvoiceContactFirstName}
                        invoiceContactLastName={hotelInvoiceContactLastName}
                        invoiceTemplates={invoiceTemplates}
                        invoiceSend={emailSends.find((s) => s.email_kind === 'invoice')}
                        onSent={() => {
                          void fetchGroups();
                          void onRefresh();
                        }}
                      />
                    </div>
                  </details>
                </div>
              ) : !isHotelCancelled && hotelOutreachEligibleForInvoiceSection(outreach) ? (
                <div className="mt-3 mb-6 rounded-lg border border-gray-200 bg-gray-50/90 px-3 py-2.5 text-xs">
                  <div className="flex w-full items-center gap-2">
                    <span className="shrink-0 font-semibold text-gray-900">Invoice</span>
                    <span className="min-w-0 flex-1 text-right text-xs font-normal tabular-nums text-gray-600">
                      {invoiceBounceContent ? (
                        <HotelModalBounceStatusButton
                          statusLine={invoiceStatusSummary}
                          contextTitle={modalTitleHotel}
                          bounceContent={invoiceBounceContent}
                          onOpenBounceDetail={setModalBounceDetail}
                          className="text-xs tabular-nums text-red-700/90 underline decoration-red-300/70 decoration-dotted underline-offset-2 hover:text-red-800"
                        />
                      ) : (
                        invoiceStatusSummary
                      )}
                    </span>
                  </div>
                </div>
              ) : null}
            <div className="w-full min-w-0 max-w-full text-xs">
              <div className="mb-1 text-xs font-semibold text-gray-900">Hotel groups</div>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <label htmlFor="hotel-modal-group-sort" className="text-xs font-normal text-gray-700">
                    Sort groups by
                  </label>
                  <select
                    id="hotel-modal-group-sort"
                    className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 shadow-sm focus:border-action focus:outline-none focus:ring-1 focus:ring-action/30"
                    value={groupSortBy}
                    onChange={(e) => setGroupSortBy(e.target.value as HotelModalGroupSort)}
                    disabled={isHotelCancelled}
                  >
                    <option value="checkin">Arrival date</option>
                    <option value="client">Client</option>
                    <option value="manager">Manager</option>
                  </select>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {showCampaignCancelToolbar && outreach ? (
                    <HotelModalCampaignCancelActions
                      outreach={outreach}
                      onCancelCampaignHotel={async () => {
                        await onCancelCampaignHotel();
                        await fetchGroups();
                      }}
                      onUndoCancelCampaignHotel={async (outreachSnapshot) => {
                        await onUndoCancelCampaignHotel(outreachSnapshot);
                        await fetchGroups();
                      }}
                      cancellingCampaignHotel={cancellingCampaignHotel}
                    />
                  ) : null}
                  {!isHotelModalReadOnly ? (
                    <button
                      type="button"
                      onClick={addEmptyManualLine}
                      className="btn-secondary-xs-compact"
                    >
                      Add extra groups
                    </button>
                  ) : null}
                </div>
              </div>
            <fieldset disabled={isHotelModalReadOnly} className="min-w-0 border-0 p-0 m-0">
              <table className="w-full table-fixed divide-y divide-gray-200 text-xs leading-tight">
                <colgroup>
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '5%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '6%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '3%' }} />
                  <col style={{ width: '10%' }} />
                </colgroup>
              <thead className="bg-gray-50">
                <tr>
                  <th title="Client" className="px-1 py-1.5 text-left font-medium text-gray-500 uppercase leading-tight sm:px-1.5">
                    Client
                  </th>
                  <th title="Client reference" className="px-1 py-1.5 text-left font-medium text-gray-500 uppercase leading-tight sm:px-1.5">
                    Ref.
                  </th>
                  <th
                    title="Manager (initials; full name on hover)"
                    className="px-0.5 py-1.5 text-center font-medium text-gray-500 uppercase leading-tight sm:px-1"
                  >
                    <span className="hidden sm:inline">Mgr.</span>
                    <span className="sm:hidden" aria-hidden>
                      M
                    </span>
                  </th>
                  <th title="Check-in" className="px-1 py-1.5 text-left font-medium text-gray-500 uppercase leading-tight sm:px-1.5">
                    <span className="hidden sm:inline">Check-in</span>
                    <span className="sm:hidden">In</span>
                  </th>
                  <th title="Check-out" className="px-1 py-1.5 text-left font-medium text-gray-500 uppercase leading-tight sm:px-1.5">
                    <span className="hidden sm:inline">Check-out</span>
                    <span className="sm:hidden">Out</span>
                  </th>
                  <th title="Nights" className="px-1 py-1.5 text-right font-medium text-gray-500 uppercase tabular-nums leading-tight sm:px-1.5">
                    N
                  </th>
                  <th title="Estimated revenue" className="px-1 py-1.5 text-right font-medium text-gray-500 uppercase tabular-nums leading-tight sm:px-1.5">
                    Est.
                  </th>
                  <th title="Realised revenue" className="px-1 py-1.5 text-right font-medium text-gray-500 uppercase tabular-nums leading-tight sm:px-1.5">
                    <span className="hidden sm:inline">Realised</span>
                    <span className="sm:hidden">Real.</span>
                  </th>
                  <th title="Commission %" className="px-1 py-1.5 text-right font-medium text-gray-500 uppercase tabular-nums leading-tight sm:px-1.5">
                    %
                  </th>
                  <th title="Invoice amount" className="px-1 py-1.5 text-right font-medium text-gray-500 uppercase tabular-nums leading-tight sm:px-1.5">
                    Inv.
                  </th>
                  <th
                    title="Backyou realised revenue sync"
                    className="px-0 py-1.5 text-center font-medium text-gray-500 uppercase leading-tight"
                  >
                    <span className="hidden sm:inline">BY</span>
                    <span className="sm:hidden" aria-hidden>
                      ·
                    </span>
                  </th>
                  <th title="Comment" className="px-1 py-1.5 text-left font-medium text-gray-500 uppercase leading-tight sm:px-1.5">
                    Note
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 text-gray-900">
                {sortedGroupRows.map(({ g, index: idx }) => (
                  <tr key={`${g.request_id}-${g.checkin_date}`}>
                    <td className="min-w-0 px-1 py-1.5 align-middle sm:px-1.5">
                      <div className="flex min-w-0 items-start gap-0.5">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            void handleCopyGroupRequestId(g.request_id, g.checkin_date);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          title={`Copy request ID (${g.request_id})`}
                          aria-label={`Copy request ID ${g.request_id}`}
                          className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-sm p-0 text-gray-500 transition-colors hover:bg-gray-50 hover:text-[#17776F] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#148D82]/35"
                        >
                          {copiedRequestRowKey === `${g.request_id}:${g.checkin_date}` ? (
                            <CheckCircle2 className="h-3 w-3 text-[#17776F]" strokeWidth={2} aria-hidden />
                          ) : (
                            <Copy className="h-2.5 w-2.5" strokeWidth={2.25} aria-hidden />
                          )}
                        </button>
                        <span className="line-clamp-2 min-w-0 flex-1 break-words">{g.company}</span>
                      </div>
                    </td>
                    <td className="min-w-0 px-1 py-1.5 align-middle text-gray-600 sm:px-1.5">
                      <span className="line-clamp-2 break-words">{g.client_request_reference?.trim() || '—'}</span>
                    </td>
                    <td className="min-w-0 px-0.5 py-1.5 text-center align-middle sm:px-1">
                      {g.request_manager?.trim() ? (
                        <span
                          title={g.request_manager}
                          className="inline-flex min-w-[2rem] items-center justify-center rounded-full border border-gray-200/90 bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-800 tabular-nums shadow-sm sm:text-xs"
                        >
                          {formatManagerInitials(g.request_manager)}
                        </span>
                      ) : (
                        <span className="tabular-nums text-gray-400">—</span>
                      )}
                    </td>
                    <td className="min-w-0 px-1 py-1.5 align-middle tabular-nums sm:px-1.5">{formatDateOnly(g.checkin_date)}</td>
                    <td className="min-w-0 px-1 py-1.5 align-middle tabular-nums sm:px-1.5">
                      {g.checkout_date ? formatDateOnly(g.checkout_date) : '—'}
                    </td>
                    <td className="min-w-0 px-1 py-1.5 text-right align-middle tabular-nums sm:px-1.5">
                      {formatGroupNightsLabel(g.number_of_nights, g.checkin_date, g.checkout_date)}
                    </td>
                    <td className="min-w-0 px-1 py-1.5 text-right align-middle tabular-nums sm:px-1.5">{formatCurrencyDecimals(g.estimated_revenue)}</td>
                    <td className="min-w-0 px-1 py-1.5 text-right align-middle tabular-nums sm:px-1.5">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={
                          realisedRevenueDrafts[groupCommissionDraftKey(g)] !== undefined
                            ? realisedRevenueDrafts[groupCommissionDraftKey(g)]
                            : formatRealisedAmountInputValue(g.realised_revenue)
                        }
                        onChange={(e) => handleRealisedChange(idx, e.target.value)}
                        onBlur={() => handleRealisedBlur(idx)}
                        className="box-border w-full min-w-0 max-w-full border border-gray-300 rounded px-1 py-0.5 text-right text-xs leading-tight"
                      />
                    </td>
                    <td className="min-w-0 px-1 py-1.5 text-right align-middle tabular-nums sm:px-1.5">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={
                          commissionPercentDrafts[groupCommissionDraftKey(g)] !== undefined
                            ? commissionPercentDrafts[groupCommissionDraftKey(g)]
                            : g.commission_percent != null
                              ? String(g.commission_percent)
                              : ''
                        }
                        onChange={(e) => handleCommissionChange(idx, e.target.value)}
                        onBlur={() => handleCommissionBlur(idx)}
                        className="box-border w-full min-w-0 border border-gray-300 rounded px-1 py-0.5 text-right text-xs tabular-nums leading-tight"
                        aria-label="Commission percent"
                      />
                    </td>
                    <td className="min-w-0 px-1 py-1.5 text-right align-middle tabular-nums text-gray-800 sm:px-1.5">
                      {formatInvoiceAmountCell(g)}
                    </td>
                    <td className="min-w-0 px-0 py-1.5 text-center align-middle">
                      <HotelDetailModalBackyouIndicator g={g} />
                    </td>
                    <td className="min-w-0 px-1 py-1.5 align-middle sm:px-1.5">
                      <input
                        type="text"
                        value={g.comment || ''}
                        onChange={(e) => handleCommentChange(idx, e.target.value)}
                        className="box-border w-full min-w-0 border border-gray-300 rounded px-1 py-0.5 text-xs leading-tight"
                      />
                    </td>
                  </tr>
                ))}
                {manualLines.map((m, midx) => {
                  const derivedNights = nightsBetweenStayDates(m.checkin_date, m.checkout_date);
                  return (
                    <tr key={m.id} className="bg-sky-50/40">
                      <td className="min-w-0 px-1 py-1.5 align-middle sm:px-1.5">
                        <div className="flex min-w-0 items-center gap-1">
                          <span
                            className="shrink-0 rounded bg-sky-100 px-0.5 py-px text-[9px] font-semibold uppercase tracking-wide text-sky-800 sm:text-[10px]"
                            title="Extra group"
                          >
                            Add.
                          </span>
                          <input
                            type="text"
                            value={m.client_name}
                            onChange={(e) =>
                              setManualLines((prev) => {
                                const next = [...prev];
                                next[midx] = { ...next[midx], client_name: e.target.value };
                                return next;
                              })
                            }
                            className="min-w-0 flex-1 border border-gray-300 rounded px-1 py-0.5 text-xs"
                            placeholder="Client"
                          />
                        </div>
                      </td>
                      <td className="min-w-0 px-1 py-1.5 align-middle text-gray-600 sm:px-1.5">
                        <input
                          type="text"
                          value={m.ref_client ?? ''}
                          onChange={(e) =>
                            setManualLines((prev) => {
                              const next = [...prev];
                              next[midx] = {
                                ...next[midx],
                                ref_client: e.target.value.trim() ? e.target.value : null,
                              };
                              return next;
                            })
                          }
                          className="w-full min-w-0 border border-gray-300 rounded px-1 py-0.5 text-xs"
                        />
                      </td>
                      <td
                        className="min-w-0 px-0.5 py-1.5 text-center align-middle text-gray-400 sm:px-1"
                        title="Extra groups have no request manager"
                      >
                        —
                      </td>
                      <td className="min-w-0 px-1 py-1.5 align-middle tabular-nums sm:px-1.5">
                        <input
                          type="date"
                          value={m.checkin_date}
                          min={manualStayWindow?.first}
                          max={manualStayWindow?.last}
                          onChange={(e) =>
                            setManualLines((prev) => {
                              const next = [...prev];
                              next[midx] = { ...next[midx], checkin_date: e.target.value };
                              return next;
                            })
                          }
                          className="w-full min-w-0 border border-gray-300 rounded px-0.5 py-0.5 text-xs"
                        />
                      </td>
                      <td className="min-w-0 px-1 py-1.5 align-middle tabular-nums sm:px-1.5">
                        <input
                          type="date"
                          value={m.checkout_date}
                          min={
                            m.checkin_date
                              ? addCalendarDays(m.checkin_date, 1)
                              : manualStayWindow?.first
                          }
                          max={manualStayWindow?.last}
                          onChange={(e) =>
                            setManualLines((prev) => {
                              const next = [...prev];
                              next[midx] = { ...next[midx], checkout_date: e.target.value };
                              return next;
                            })
                          }
                          className="w-full min-w-0 border border-gray-300 rounded px-0.5 py-0.5 text-xs"
                        />
                      </td>
                      <td className="min-w-0 px-1 py-1.5 text-right align-middle tabular-nums sm:px-1.5">
                        {derivedNights != null ? derivedNights : '—'}
                      </td>
                      <td className="min-w-0 px-1 py-1.5 text-right align-middle tabular-nums text-gray-400 sm:px-1.5">
                        —
                      </td>
                      <td className="min-w-0 px-1 py-1.5 text-right align-middle tabular-nums sm:px-1.5">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={
                            realisedRevenueDrafts[manualCommissionDraftKey(m.id)] !== undefined
                              ? realisedRevenueDrafts[manualCommissionDraftKey(m.id)]
                              : formatRealisedAmountInputValue(m.realised_revenue)
                          }
                          onChange={(e) => handleManualRealisedChange(midx, e.target.value)}
                          onBlur={() => handleManualRealisedBlur(midx)}
                          className="box-border w-full min-w-0 border border-gray-300 rounded px-1 py-0.5 text-right text-xs leading-tight"
                        />
                      </td>
                      <td className="min-w-0 px-1 py-1.5 text-right align-middle tabular-nums sm:px-1.5">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={
                            commissionPercentDrafts[manualCommissionDraftKey(m.id)] !== undefined
                              ? commissionPercentDrafts[manualCommissionDraftKey(m.id)]
                              : m.commission_percent == null
                                ? ''
                                : String(m.commission_percent)
                          }
                          onChange={(e) => handleManualCommissionChange(midx, e.target.value)}
                          onBlur={() => handleManualCommissionBlur(midx)}
                          className="box-border w-full min-w-0 border border-gray-300 rounded px-1 py-0.5 text-right text-xs tabular-nums leading-tight"
                          aria-label="Commission percent (extra group)"
                        />
                      </td>
                      <td className="min-w-0 px-1 py-1.5 text-right align-middle tabular-nums text-gray-800 sm:px-1.5">
                        {formatInvoiceAmountCell(m)}
                      </td>
                      <td
                        className="min-w-0 px-0 py-1.5 text-center align-middle text-gray-300"
                        title="Extra groups are not pushed to Backyou"
                      >
                        —
                      </td>
                      <td className="min-w-0 px-1 py-1.5 align-middle sm:px-1.5">
                        <div className="flex min-w-0 items-center gap-0.5">
                          <input
                            type="text"
                            value={m.comment || ''}
                            onChange={(e) => handleManualCommentChange(midx, e.target.value)}
                            className="min-w-0 flex-1 border border-gray-300 rounded px-1 py-0.5 text-xs leading-tight"
                          />
                          <button
                            type="button"
                            onClick={() => removeManualLine(midx)}
                            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded border border-gray-200 text-red-700 hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/30"
                            aria-label="Remove extra group"
                            title="Remove line"
                          >
                            <Trash2 className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {(groups.length > 0 || manualLines.length > 0) && (
                <tfoot>
                  <tr className="border-t border-gray-200 bg-gray-50/90 text-xs">
                    <td
                      colSpan={7}
                      className="px-1 py-1.5 text-left font-medium text-gray-700 align-middle sm:px-1.5"
                    >
                      Total
                    </td>
                    <td className="px-1 py-1.5 text-right font-semibold tabular-nums text-gray-900 align-middle sm:px-1.5">
                      {formatCurrencyDecimals(totalRealisedRevenue)}
                    </td>
                    <td className="px-1 py-1.5 text-right text-gray-400 align-middle sm:px-1.5">—</td>
                    <td className="px-1 py-1.5 text-right font-semibold tabular-nums text-gray-900 align-middle sm:px-1.5">
                      {formatCurrencyDecimals(totalInvoiceAmount)}
                    </td>
                    <td className="px-1 py-1.5 align-middle sm:px-1.5" />
                    <td className="px-1 py-1.5 align-middle sm:px-1.5" />
                  </tr>
                </tfoot>
              )}
            </table>
            </fieldset>
            {submissionToolbarVisible && outreach ? (
              <div className="mt-2 flex justify-end">
                <HotelModalSubmissionFooterActions
                  showBackyouSync={showBackyouSyncButton}
                  showValidate={
                    outreach.status === 'validated' && !outreach.validated_by
                  }
                  validatingSubmission={validatingSubmission}
                  resyncingBackyou={resyncingBackyou}
                  submittingOnBehalf={submittingOnBehalf}
                  onValidateSubmission={() => void handleValidateSubmission()}
                  onBackyouResync={() => void handleBackyouResync()}
                />
              </div>
            ) : null}
            </div>
            <div className="mt-3 pb-5 text-xs">
              <div className="mb-1 text-xs font-semibold text-gray-900">Hotel comment</div>
              <div
                className={`w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-xs leading-relaxed whitespace-pre-wrap ${
                  globalComment.trim() ? 'text-gray-800' : 'text-gray-400'
                }`}
              >
                {globalComment.trim() ? globalComment : '—'}
              </div>
            </div>
            <div className="mt-4 border-t border-gray-200 pt-4">
              <div className="mb-2 text-xs font-normal text-gray-700">
                Internal note{' '}
                <span className="text-[10px] text-gray-500">(not visible to the hotel)</span>
              </div>
              <CampaignHotelInternalNoteField
                campaignId={campaignId}
                hotelId={hotelId}
                noteId={internalNoteId}
                initialBody={internalNoteBody}
                onSaved={(note) => {
                  setInternalNoteId(note.id);
                  setInternalNoteBody(note.body);
                  void onRefresh();
                }}
                rows={2}
                className="text-xs"
              />
            </div>
            {outreach ? (
              <HotelModalSubmissionFooter
                outreach={outreach}
                backyouSyncRecap={backyouSyncRecap}
              />
            ) : null}
            </>
          )}
        </div>
        {!loading && !isHotelModalReadOnly && (
          <div className="px-4 py-3 border-t flex flex-wrap justify-end gap-2">
            {canSubmitOnBehalf ? (
              <button
                type="button"
                onClick={() => void handleSubmitOnBehalf()}
                disabled={
                  saving ||
                  submittingOnBehalf ||
                  validatingSubmission ||
                  resyncingBackyou
                }
                title="Record hotel form submission without emailing the hotel. An admin can then Validate submission as usual."
                className="btn-secondary-sm px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submittingOnBehalf ? 'Submitting…' : 'Submit on behalf of hotel'}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={
                saving ||
                submittingOnBehalf ||
                resyncingBackyou ||
                (!!outreach?.validated_by && !isHotelModalFormDirty)
              }
              title={hotelModalSaveButtonTitle(outreach?.validated_by, isHotelModalFormDirty)}
              className="btn-primary px-5 py-2 text-sm disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </div>
    </div>
    <EmailBounceDetailModal
      view={modalBounceDetail}
      onClose={() => setModalBounceDetail(null)}
    />
    </>
  );
}
